import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNotesAuth } from '@/lib/notesAuth';
import { notesApi } from '@/lib/notesApi';
import { ModuleSwitch } from './ModuleSwitch';
import { PushPrompt, PushBell } from './PushPrompt';
import PlannerReminderAlerts from './PlannerReminderAlerts';
import PlannerNavShortcuts from './PlannerNavShortcuts';
import ChatDock from './ChatDock';
import { TaskShareDialog, TasksAdminDialog } from './TasksModals';
import { TaskTemplatesDialog, TaskGroupsDialog, SaveAsTemplateDialog } from './TasksTemplatesGroups';
import TaskComments from './TaskComments';
import TaskAttachments from './TaskAttachments';
import { useTaskReminders, remKey, formatWhen, toLocalInputValue } from './useTaskReminders';
import { useChatEnabled } from './useChatEnabled';
import TaskBodyEditor from './TaskBodyEditor';
import TaskActivity from './TaskActivity';
import TaskSubtasks from './TaskSubtasks';
import TaskLinkedNotes from './TaskLinkedNotes';
import TaskLinkedEvents from './TaskLinkedEvents';
import TaskInlineEditor from './TaskInlineEditor';
import { DictationHint } from './DictationHint';
import { useDictation } from './useDictation';
import { SPEECH_SUPPORTED, tokenizeDictation, applyOpsToText } from './dictationCommands';
import { parseFilterText, parseTaskText, FILTER_EXAMPLES, TASK_EXAMPLES } from './taskNlParser';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import {
  ListChecks, Plus, Search, Star, Flag, Clock, CalendarDays, Trash2, X, Filter,
  Sun, Moon, LogOut, PanelLeftClose, PanelLeftOpen, PanelRight, Hash, Circle, Menu, Rows3,
  ChevronDown, ChevronRight, ChevronLeft, ArrowLeft, Check, Share2, Users2, Bell, BellRing, CalendarClock, Repeat,
  MessageSquare, Paperclip, List, LayoutGrid, AtSign, GripVertical, Mic, MicOff, Loader2, Type, BookOpen, NotebookText,
  Maximize2, Minimize2, Palette, Bookmark, Pencil, SlidersHorizontal, Keyboard, AlertTriangle, Play, Pause, CheckCircle2, CheckCheck, RotateCcw, DatabaseBackup, ListTree, Eye, Inbox,
  Command as CmdIcon, CornerDownLeft, Columns3, Minus, Send,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Small date helpers (all boundaries computed in the user's LOCAL timezone)
// ---------------------------------------------------------------------------
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
// Due status for a task relative to now: 'overdue' | 'today' | 'future' | 'none'.
// Drives the coloured card borders on the All Tasks grid + Project calendar.
const dueStatusOf = (t) => {
  if (!t || !t.due_at) return 'none';
  const dd = new Date(t.due_at).getTime();
  if (isNaN(dd)) return 'none';
  const now = new Date();
  // A completed task is never "overdue" — finished work can't be late. This
  // keeps completed past-due tasks out of every overdue display module-wide.
  if (dd < startOfDay(now).getTime()) return t.is_completed ? 'none' : 'overdue';
  if (dd <= endOfDay(now).getTime()) return 'today';
  return 'future';
};
const dueBorderCls = (st) => (st === 'overdue' ? 'border-[var(--n-danger)]' : st === 'today' ? 'border-[var(--n-success)]' : 'border-[var(--n-border)]');
const pad = (n) => String(n).padStart(2, '0');
const toLocalDate = (iso) => { if (!iso) return ''; const d = new Date(iso); if (isNaN(d.getTime())) return ''; return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const toLocalTime = (iso) => { if (!iso) return ''; const d = new Date(iso); if (isNaN(d.getTime())) return ''; return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const combineDueAt = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const dt = new Date(`${dateStr}T${timeStr || '09:00'}`);
  return isNaN(dt.getTime()) ? null : dt.toISOString();
};
const fmtTime = (iso) => { if (!iso) return ''; const d = new Date(iso); if (isNaN(d.getTime())) return ''; return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }); };
const fmtDateLong = (d) => d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
// Due dates are date-only. Store as local end-of-day so a task is "overdue"
// only after its day has fully passed, and render a friendly relative label.
const dueIso = (dateStr) => { if (!dateStr) return null; const d = new Date(`${dateStr}T00:00:00`); if (isNaN(d.getTime())) return null; return endOfDay(d).toISOString(); };
// Sharing-scope predicate shared by the Today filter bar and the Filters view.
//   with_me    → shared with me (someone else owns it)
//   by_me      → I own it and it's shared out to others
//   any_shared → either of the above
const matchSharedMode = (t, mode) => {
  if (mode === 'with_me') return !!t.shared_by;
  if (mode === 'by_me') return !!t.is_shared && !t.shared_by;
  if (mode === 'any_shared') return !!t.is_shared;
  return true;
};
const SHARED_MODE_OPTIONS = [
  { value: 'any', label: 'Any sharing' },
  { value: 'with_me', label: 'Shared with me' },
  { value: 'by_me', label: 'Shared by me' },
  { value: 'any_shared', label: 'Any shared' },
];
const TODAY_DUE_OPTIONS = [
  { value: 'any', label: 'Any due date' },
  { value: 'no_due', label: 'No due date' },
  { value: 'on', label: 'On a date' },
  { value: 'range', label: 'Date range' },
];
// All Tasks due dropdown — adds calendar-month scopes; "No due date" moves to a
// dedicated toggle (its tasks live in the right-hand panel), so it's omitted here.
const ALL_DUE_OPTIONS = [
  { value: 'any', label: 'Any due date' },
  { value: 'on', label: 'On a date' },
  { value: 'range', label: 'Date range' },
  { value: 'this_month', label: 'This month' },
  { value: 'next_month', label: 'Next month' },
];
const fmtDueDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso); if (isNaN(d.getTime())) return '';
  const diff = Math.round((startOfDay(d) - startOfDay(new Date())) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  const opts = { day: 'numeric', month: 'short' };
  if (d.getFullYear() !== new Date().getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString(undefined, opts);
};

// Client UTC offset (JS convention: minutes, negative east of UTC) so the
// backend can bucket "Today"/"Next 7 days" in the user's LOCAL timezone and
// match end-of-day due dates exactly.
const TZ_OFFSET = new Date().getTimezoneOffset();

const PRIO = {
  high: { label: 'High', cls: 'text-[var(--n-danger)]' },
  medium: { label: 'Medium', cls: 'text-[var(--n-accent-2)]' },
  normal: { label: 'Normal', cls: 'text-[var(--n-muted)]' },
};
const PRIO_ORDER = ['high', 'medium', 'normal'];
const RECUR = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'daily', label: 'Every day' },
  { value: 'twicedaily', label: 'Twice a day' },
  { value: 'weekly', label: 'Every week' },
  { value: 'monthly', label: 'Every month' },
  { value: 'annually', label: 'Every year' },
];
const RECUR_LABEL = Object.fromEntries(RECUR.map((r) => [r.value, r.label]));
const RECUR_TASK = [
  { value: 'none', label: "Doesn't repeat" },
  { value: 'daily', label: 'Repeat daily' },
  { value: 'weekly', label: 'Repeat weekly' },
  { value: 'monthly', label: 'Repeat monthly' },
  { value: 'annually', label: 'Repeat yearly' },
  { value: 'weekdays', label: 'Repeat on days…' },
];
const RECUR_TASK_SHORT = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', annually: 'Yearly' };
// Weekday repeat (Mon=0 … Sun=6 — matches Python's datetime.weekday()).
const WEEKDAY_CHIPS = [
  { v: 0, label: 'M', name: 'Monday' }, { v: 1, label: 'T', name: 'Tuesday' },
  { v: 2, label: 'W', name: 'Wednesday' }, { v: 3, label: 'T', name: 'Thursday' },
  { v: 4, label: 'F', name: 'Friday' }, { v: 5, label: 'S', name: 'Saturday' },
  { v: 6, label: 'S', name: 'Sunday' },
];
const WEEKDAY_ABBR = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// JS getDay() is Sun=0..Sat=6 → convert to Mon=0..Sun=6.
const jsToMon0 = (d) => (d.getDay() + 6) % 7;
const defaultWeekdayFor = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  return jsToMon0(Number.isNaN(d.getTime()) ? new Date() : d);
};
// Compact recurrence badge (handles the weekdays rule).
const recurShort = (task) => {
  const r = task && task.recurrence;
  if (!r || r === 'none') return '';
  if (r === 'weekdays') {
    const wd = [...(task.recurrence_weekdays || [])].sort((a, b) => a - b);
    if (!wd.length) return 'Weekly';
    if (wd.length === 7) return 'Daily';
    if (wd.length === 5 && wd.join() === '0,1,2,3,4') return 'Mon–Fri';
    return wd.map((d) => WEEKDAY_ABBR[d]).join('·');
  }
  return RECUR_TASK_SHORT[r] || '';
};
const RECUR_END = [
  { value: 'never', label: 'Never ends' },
  { value: 'on', label: 'On date' },
  { value: 'after', label: 'After…' },
];
const DUE_PRESETS = [
  { value: 'any', label: 'Any' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'today', label: 'Today' },
  { value: 'next7', label: 'Next 7 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'next_month', label: 'Next month' },
  { value: 'none', label: 'No date' },
  { value: 'custom', label: 'Custom range' },
];

const FilterChip = ({ label, icon, dot, onRemove, testid }) => (
  <span data-testid={testid} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--n-accent)] bg-[var(--n-accent-bg)] px-2.5 py-1 text-[12px] font-medium text-[var(--n-accent)]">
    {dot ? <span className="h-2 w-2 rounded-full" style={{ background: dot }} /> : icon}
    <span className="max-w-[180px] truncate">{label}</span>
    <button onClick={onRemove} data-testid={`${testid}-remove`} title="Remove"
      className="n-focus -mr-0.5 grid h-4 w-4 place-items-center rounded-full hover:bg-[var(--n-surface)]"><X size={11} /></button>
  </span>
);
const BOARD_COLUMNS = [
  { key: 'todo', label: 'To do' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'done', label: 'Done' },
];
const statusOf = (t) => t.status || (t.is_completed ? 'done' : 'todo');
const STATUS_OPTS = [
  { value: 'todo', label: 'To do' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
];
const STATUS_META = {
  todo: { label: 'To do', dot: 'var(--n-muted-2)', chip: 'text-[var(--n-muted)] border-[var(--n-border)]' },
  in_progress: { label: 'In progress', dot: 'var(--n-accent-2)', chip: 'text-[var(--n-accent-2)] border-[var(--n-accent-2)]/40 bg-[var(--n-accent-2)]/10' },
  done: { label: 'Done', dot: 'var(--n-accent)', chip: 'text-[var(--n-accent)] border-[var(--n-accent-soft)] bg-[var(--n-accent-bg)]' },
};
// Curated project palette (rich shades — never raw primaries).
const PROJECT_COLORS = ['#B8892E', '#D2A24A', '#6C8CBF', '#5AA17F', '#C56B6B', '#B49BE0', '#4FA3A5', '#C98A5E'];

// Due-date buckets for grouping the Project List & Filters List by when tasks
// are due (chronological order; first matching bucket wins).
const DATE_BUCKETS = [
  { key: 'overdue', label: 'Overdue', tone: 'text-[var(--n-danger)]' },
  { key: 'today', label: 'Today', tone: 'text-[var(--n-accent)]' },
  { key: 'tomorrow', label: 'Tomorrow', tone: 'text-[var(--n-ink)]' },
  { key: 'this_week', label: 'This Week', tone: 'text-[var(--n-ink)]' },
  { key: 'next_week', label: 'Next Week', tone: 'text-[var(--n-ink)]' },
  { key: 'this_month', label: 'This Month', tone: 'text-[var(--n-ink)]' },
  { key: 'next_month', label: 'Next Month', tone: 'text-[var(--n-ink)]' },
  { key: 'later', label: 'Later', tone: 'text-[var(--n-ink)]' },
  { key: 'no_due', label: 'No Due Date', tone: 'text-[var(--n-muted)]' },
  // Completed tasks group at the very end (never inside a date bucket, so they
  // can't show up as "overdue"). Surfaced by the All Tasks "Completed" toggle.
  { key: 'completed', label: 'Completed', tone: 'text-[var(--n-muted)]' },
];

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.todo;
  return (
    <span data-testid="task-card-status-pill" className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10.5px] font-medium ${m.chip}`}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: m.dot }} />{m.label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Shared little components
// ---------------------------------------------------------------------------
const PriorityMark = ({ priority }) => {
  if (priority === 'high') return <Flag size={14} className="shrink-0 text-[var(--n-danger)]" data-testid="task-card-priority-indicator" fill="currentColor" />;
  if (priority === 'medium') return <Circle size={9} className="shrink-0 text-[var(--n-accent-2)]" data-testid="task-card-priority-indicator" fill="currentColor" />;
  return null;
};

const DueChip = ({ iso, done }) => {
  if (!iso) return null;
  const overdue = !done && startOfDay(new Date(iso)).getTime() < startOfDay(new Date()).getTime();
  return (
    <span data-testid="task-card-due-chip"
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${overdue ? 'border-[var(--n-danger)]/40 bg-[var(--n-danger)]/10 text-[var(--n-danger)]' : 'border-[var(--n-border)] bg-[var(--n-canvas-2)] text-[var(--n-ink-soft)]'}`}>
      <CalendarDays size={11} />{fmtDueDate(iso)}
    </span>
  );
};

// Gold accent used for the "shared with me" chip (shows the sharer's name).
// Kept as literals so it renders identically in light + dark Notes themes.
const N_GOLD = '#C99A2E';
const N_GOLD_BG = 'rgba(201,154,46,0.16)';
const N_GOLD_BORDER = 'rgba(201,154,46,0.48)';

// Current viewer's user id — assigned by <TasksAppShell> during render so the
// module-scoped avatar components below can hide the viewer's own avatar from
// collaborator stacks. Stable for the session.
let VIEWER_ID = null;

const AvatarStack = ({ people = [], size = 20, max = 3, testid = 'task-avatar-stack' }) => {
  // Hide the current viewer's own avatar — you never need to see yourself in
  // the collaborator set (it read as an amber "unseen" pip, which was
  // misleading). Everyone else still sees you.
  const list = (people || []).filter((u) => u && u.id !== VIEWER_ID);
  if (!list.length) return null;
  const shown = list.slice(0, max);
  const extra = list.length - shown.length;
  return (
    <span className="flex -space-x-1.5" data-testid={testid}>
      {shown.map((u) => {
        // Read-receipt: a collaborator who has opened the task shows GREEN. The
        // owner has, by definition, seen their own task, so their avatar always
        // reads as "seen" (green) to everyone else — never amber/unread.
        const seen = u.is_owner || u.has_read;
        let seenWhen = null;
        if (seen && u.read_at) {
          try { seenWhen = new Date(u.read_at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }); } catch { seenWhen = null; }
        }
        const title = `${u.name}${u.is_owner ? ' (owner)' : ''}${seen ? (seenWhen ? ` · seen ${seenWhen}` : ' · seen') : ' · not seen yet'}`;
        return (
          <span key={u.id}
            title={title}
            data-testid={`${testid}-avatar${seen ? '-seen' : ''}`}
            style={{ height: size, width: size }}
            className={`grid place-items-center rounded-full border-2 border-[var(--n-surface)] text-[9px] font-bold text-white ${seen ? 'bg-[var(--n-success)]' : 'bg-[var(--n-accent)]'}`}>
            {u.initial || (u.name || '?').charAt(0).toUpperCase()}
          </span>
        );
      })}
      {extra > 0 && (
        <span style={{ height: size, width: size }} className="grid place-items-center rounded-full border-2 border-[var(--n-surface)] bg-[var(--n-canvas-2)] text-[9px] font-bold text-[var(--n-muted)]">+{extra}</span>
      )}
    </span>
  );
};

// Vertical avatar column — shown on the RIGHTMOST panel of a grid task card.
// Unlike AvatarStack (horizontal, capped with a +N overflow), this renders
// EVERY shared user in a vertical line so the whole collaborator set is
// visible at a glance. Each avatar keeps the read-receipt colour (green =
// seen) and a hover tooltip revealing exactly when the task was last seen.
// A single collaborator is centred vertically within the panel.
const AvatarColumn = ({ people = [], size = 18, testid = 'task-avatar-column' }) => {
  // Hide the current viewer's own avatar (see AvatarStack for the rationale).
  const list = (people || []).filter((u) => u && u.id !== VIEWER_ID);
  if (!list.length) return null;
  return (
    <span
      data-testid={testid}
      className="flex shrink-0 flex-col items-center justify-center gap-1 self-stretch border-l border-[var(--n-border)] pl-2">
      {list.map((u) => {
        // Read-receipt: a collaborator who has opened the task shows GREEN. The
        // owner always reads as "seen" (green) to everyone else.
        const seen = u.is_owner || u.has_read;
        let seenWhen = null;
        if (seen && u.read_at) {
          try { seenWhen = new Date(u.read_at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }); } catch { seenWhen = null; }
        }
        const title = `${u.name}${u.is_owner ? ' (owner)' : ''}${seen ? (seenWhen ? ` · seen ${seenWhen}` : ' · seen') : ' · not seen yet'}`;
        return (
          <span key={u.id}
            title={title}
            data-testid={`${testid}-avatar${seen ? '-seen' : ''}`}
            style={{ height: size, width: size }}
            className={`grid shrink-0 place-items-center rounded-full border-2 border-[var(--n-surface)] text-[9px] font-bold text-white ${seen ? 'bg-[var(--n-success)]' : 'bg-[var(--n-accent)]'}`}>
            {u.initial || (u.name || '?').charAt(0).toUpperCase()}
          </span>
        );
      })}
    </span>
  );
};

// Custom dropdown (avoids raw <select>) — used for recurrence & status.
const MiniSelect = ({ value, onChange, options, disabled, testid, width = 'w-44', leading = null }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const cur = options.find((o) => o.value === value) || options[0];
  return (
    <div className="relative" ref={ref}>
      <button type="button" disabled={disabled} data-testid={testid} onClick={() => setOpen((o) => !o)}
        className="n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-2.5 py-1.5 text-[13px] text-[var(--n-ink-soft)] disabled:opacity-50 hover:bg-[var(--n-surface)] transition-colors">
        {leading} {cur?.label} <ChevronDown size={13} />
      </button>
      {open && (
        <div className={`absolute left-0 top-full z-40 mt-1 ${width} rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]`}>
          {options.map((o) => (
            <button key={o.value} data-testid={`${testid}-opt-${o.value}`} onClick={() => { onChange(o.value); setOpen(false); }}
              className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] hover:bg-[var(--n-canvas-2)] transition-colors ${value === o.value ? 'text-[var(--n-accent)]' : 'text-[var(--n-ink-soft)]'}`}>
              {o.label}{value === o.value && <Check size={12} className="ml-auto" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Task row (agenda) — compact, with left priority bar + shared meta line
// ---------------------------------------------------------------------------
const TaskRow = ({ task, active, onToggle, onOpen, selected, onSelect, selectionActive, onDelete, expanded, onToggleExpand, subtasksOpen, onToggleSubtasks }) => {
  const recurring = task.recurrence && task.recurrence !== 'none';
  const canDelete = onDelete && !task.shared_by; // only the creator/owner can delete
  return (
    <div data-testid="task-card"
      className={`group relative flex items-center gap-3 overflow-hidden rounded-xl border bg-[var(--n-surface)] px-3.5 py-3 transition-colors cursor-pointer
        ${selected ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)]' : active ? 'border-[var(--n-accent)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'border-[var(--n-border)] hover:bg-[var(--n-canvas-2)]'}`}
      onClick={() => (onToggleExpand ? onToggleExpand(task.id) : onOpen(task))}>
      {task.priority === 'high' && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[var(--n-danger)]" aria-hidden="true" />}
      {task.priority === 'medium' && <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[var(--n-accent-2)]" aria-hidden="true" />}
      {onToggleExpand ? (
        <button type="button" data-testid="task-card-inline-edit-toggle" title={expanded ? 'Close inline editor' : 'Edit inline (project, tags, due, reminder, sharing, sub-tasks)'}
          onClick={(e) => { e.stopPropagation(); onToggleExpand(task.id); }}
          className={`n-focus -ml-1 grid h-5 w-4 shrink-0 place-items-center rounded transition-colors ${expanded ? 'text-[var(--n-accent)]' : 'text-[var(--n-muted-2)] hover:text-[var(--n-ink)]'}`}
          aria-expanded={!!expanded}>
          <ChevronRight size={15} className={`transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </button>
      ) : null}
      {onSelect && (
        <button type="button" data-testid="task-card-select-checkbox" title="Select task"
          onClick={(e) => { e.stopPropagation(); onSelect(task); }}
          className={`n-focus grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border transition-all
            ${selected ? 'border-[var(--n-accent)] bg-[var(--n-accent)] text-white' : `border-[var(--n-muted-2)] hover:border-[var(--n-accent)] ${selectionActive ? '' : 'opacity-0 group-hover:opacity-100'}`}`}
          aria-label={selected ? 'Deselect task' : 'Select task'} aria-pressed={selected}>
          {selected && <Check size={12} strokeWidth={3} />}
        </button>
      )}
      <button type="button" data-testid="task-card-complete-checkbox"
        onClick={(e) => { e.stopPropagation(); onToggle(task); }}
        className={`n-focus grid h-[18px] w-[18px] shrink-0 place-items-center rounded-md border transition-colors
          ${task.is_completed ? 'border-[var(--n-accent)] bg-[var(--n-accent)] text-white' : 'border-[var(--n-muted-2)] hover:border-[var(--n-accent)]'}`}
        aria-label={task.is_completed ? 'Mark incomplete' : 'Mark complete'}>
        {task.is_completed && <Check size={12} strokeWidth={3} />}
      </button>
      <div className="min-w-0 flex-1">
        <div className={`truncate text-sm font-medium ${task.is_completed ? 'text-[var(--n-muted-2)] line-through' : 'text-[var(--n-ink)]'}`}>{task.title}</div>
        {task.snippet && (
          <div className="mt-0.5 truncate text-[12px] text-[var(--n-muted)]" data-testid="task-card-snippet">{task.snippet}</div>
        )}
        {(task.tags?.length > 0) && (
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {task.tags.slice(0, 3).map((t) => (
              <span key={t} data-testid="task-card-tag-chip" className="inline-flex items-center gap-0.5 rounded-full border border-[var(--n-border)] px-1.5 py-0.5 text-[10.5px] text-[var(--n-muted)]"><Hash size={9} />{t}</span>
            ))}
          </div>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-[var(--n-muted-2)]">
          <StatusPill status={statusOf(task)} />
          {task.subtask_total > 0 && (
            onToggleSubtasks ? (
              <button type="button" data-testid="task-card-subtask-progress"
                onClick={(e) => { e.stopPropagation(); onToggleSubtasks(task.id); }}
                title={subtasksOpen ? 'Hide sub-tasks' : 'Tick sub-tasks without opening the editor'}
                aria-expanded={!!subtasksOpen}
                className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 transition-colors hover:bg-[var(--n-canvas-2)] ${subtasksOpen ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : task.subtask_done === task.subtask_total ? 'text-[var(--n-accent)]' : 'text-[var(--n-muted-2)] hover:text-[var(--n-accent)]'}`}>
                <ListChecks size={11} />{task.subtask_done}/{task.subtask_total}
                <ChevronRight size={11} className={`transition-transform ${subtasksOpen ? 'rotate-90' : ''}`} />
              </button>
            ) : (
              <span className={`inline-flex items-center gap-1 ${task.subtask_done === task.subtask_total ? 'text-[var(--n-accent)]' : ''}`}
                data-testid="task-card-subtask-progress" title={`${task.subtask_done} of ${task.subtask_total} sub-tasks done`}>
                <ListChecks size={11} />{task.subtask_done}/{task.subtask_total}
              </span>
            )
          )}
          {recurring && <span className="inline-flex items-center gap-0.5 text-[var(--n-accent)]" data-testid="task-card-recurrence-badge" title={`Repeats ${recurShort(task)}`}><Repeat size={11} />{recurShort(task)}</span>}
          {task.attachment_count > 0 && <span className="inline-flex items-center gap-0.5" data-testid="task-card-attachments-count" title={`${task.attachment_count} attachment(s)`}><Paperclip size={11} />{task.attachment_count}</span>}
          {task.linked_notes_count > 0 && <span className="inline-flex items-center gap-0.5 text-[var(--n-muted)]" data-testid="task-card-linked-notes" title={`${task.linked_notes_count} linked note${task.linked_notes_count > 1 ? 's' : ''}`}><NotebookText size={11} />{task.linked_notes_count > 1 ? task.linked_notes_count : ''}</span>}
          {task.comment_count > 0 && <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 ${task.unread_mention ? 'bg-[var(--n-accent-bg)] font-bold text-[var(--n-accent)]' : 'bg-[var(--n-canvas-2)]'}`} data-testid="task-card-comments-count" data-unread={task.unread_mention ? 'true' : 'false'} title={task.unread_mention ? `${task.comment_count} comment(s) · you were mentioned` : `${task.comment_count} comment(s)`}>{task.unread_mention ? <AtSign size={11} /> : <MessageSquare size={11} />}{task.comment_count}</span>}
          {task.shared_by && <span className="inline-flex items-center gap-1 text-[var(--n-accent)]" data-testid="task-card-shared-by"><Users2 size={11} />{task.shared_by}</span>}
          <span className="ml-auto inline-flex items-center gap-2">
            {task.is_shared && <AvatarStack people={task.shared_with} size={18} max={3} />}
            {onOpen && (
              <button type="button" data-testid="task-card-show-details" title="Open the detail panel (notes, comments, activity)"
                onClick={(e) => { e.stopPropagation(); onOpen(task); }}
                className="n-focus inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-[var(--n-muted)] transition-colors hover:text-[var(--n-accent)] hover:underline">
                <PanelRight size={11} /> Show details
              </button>
            )}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <DueChip iso={task.due_at} done={task.is_completed} />
        <PriorityMark priority={task.priority} />
        {canDelete && (
          <button type="button" data-testid="task-card-delete-button" title="Delete task"
            onClick={(e) => { e.stopPropagation(); onDelete(task); }}
            className="n-focus grid h-7 w-7 place-items-center rounded-lg text-[var(--n-muted-2)] opacity-0 transition-all hover:bg-[var(--n-danger)]/10 hover:text-[var(--n-danger)] group-hover:opacity-100">
            <Trash2 size={15} />
          </button>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SmartPopover — floating panel anchored to a point (long-press / right-click)
// or an element rect (sub-task badge). Viewport-aware positioning: prefer
// opening BELOW the anchor; if there isn't room, open ABOVE; if neither fits,
// centre it as a floating modal. Never renders off-screen; caps height at 70vh
// with an internal scroll so every option stays reachable. Repositions on
// content resize (ResizeObserver) + window resize. Non-recursive.
// ---------------------------------------------------------------------------
const SmartPopover = ({
  anchor, width = 288, onClose, testId, backdropTestId, closeGuardMs = 0,
  zPanel = 'z-[301]', zBackdrop = 'z-[300]', children,
}) => {
  const ref = useRef(null);
  const mountedAt = useRef(Date.now());
  const [pos, setPos] = useState(null);

  const ax = (anchor.left + anchor.right) / 2;
  const aTop = anchor.top;
  const aBottom = anchor.bottom;
  const isPoint = anchor.left === anchor.right;
  const aLeft = anchor.left;

  const reflow = useCallback(() => {
    const el = ref.current;
    if (!el || typeof window === 'undefined') return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const m = 8;
    const gap = 6;
    const rect = el.getBoundingClientRect();
    const h = rect.height;
    const w = rect.width || width;
    const below = vh - aBottom - m;
    const above = aTop - m;
    let top;
    if (below >= h + gap) top = aBottom + gap;          // open downward
    else if (above >= h + gap) top = aTop - h - gap;    // open upward
    else top = (vh - h) / 2;                            // centre as a modal
    top = Math.max(m, Math.min(top, vh - h - m));
    let left = isPoint ? ax - w / 2 : aLeft;
    if (left + w > vw - m) left = vw - w - m;           // open leftward near right edge
    left = Math.max(m, left);
    setPos({ top: Math.round(top), left: Math.round(left) });
  }, [ax, aTop, aBottom, aLeft, isPoint, width]);

  useLayoutEffect(() => {
    reflow();
    const el = ref.current;
    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && el) {
      ro = new ResizeObserver(() => reflow());
      ro.observe(el);
    }
    window.addEventListener('resize', reflow);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', reflow);
    };
  }, [reflow]);

  return (
    <>
      <div className={`fixed inset-0 ${zBackdrop}`} data-testid={backdropTestId}
        onClick={() => { if (closeGuardMs && Date.now() - mountedAt.current < closeGuardMs) return; onClose(); }}
        onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
      <div ref={ref} data-testid={testId} role="menu"
        className={`fixed ${zPanel} flex flex-col overflow-y-auto n-scroll rounded-2xl border border-[var(--n-border)] bg-[var(--n-surface)] shadow-[var(--n-shadow)]`}
        style={{ width: `min(${width}px, calc(100vw - 16px))`, maxHeight: '70vh', top: pos ? pos.top : -9999, left: pos ? pos.left : -9999, visibility: pos ? 'visible' : 'hidden' }}
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}>
        {children}
      </div>
    </>
  );
};

// "Pinged X hours ago" cooldown label for the Ping screen.
const pingedAgoLabel = (iso) => {
  if (!iso) return 'Pinged recently';
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff) || diff < 0) return 'Pinged recently';
  const h = Math.floor(diff / 3600000);
  if (h >= 1) return `Pinged ${h} hour${h > 1 ? 's' : ''} ago`;
  const mins = Math.max(1, Math.floor(diff / 60000));
  return `Pinged ${mins} min${mins > 1 ? 's' : ''} ago`;
};

// ---------------------------------------------------------------------------
// PingList — the collaborator picker shared by the card quick-actions Ping
// screen and the task-detail Ping button. Self-fetches status on mount, sends
// one ping at a time, shows "Ping sent to X" then calls onConfirmed() after
// 1.5s. Reflects server rejections (429 cooldown / 400 already-opened).
// ---------------------------------------------------------------------------
const PingList = ({ task, onFetchPingStatus, onPing, onConfirmed }) => {
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmName, setConfirmName] = useState(null);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const data = await onFetchPingStatus(task);
        if (alive) setUsers((data && data.users) || []);
      } catch {
        if (alive) { setUsers([]); setError('Could not load collaborators.'); }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; if (timer.current) clearTimeout(timer.current); };
  }, [task, onFetchPingStatus]);

  const doPing = async (u) => {
    if (!u || !u.pingable || busyId) return;
    setBusyId(u.id); setError(null);
    try {
      await onPing(task, u.id);
      setConfirmName(u.name);
      timer.current = window.setTimeout(() => { if (onConfirmed) onConfirmed(); }, 1500);
    } catch (e) {
      const status = e && e.response && e.response.status;
      setUsers((prev) => (prev || []).map((x) => (x.id === u.id
        ? { ...x, pingable: false, pinged_at: status === 429 ? (x.pinged_at || new Date().toISOString()) : x.pinged_at, has_read: status === 400 ? true : x.has_read }
        : x)));
      setError(status === 429 ? `${u.name} was already pinged in the last 24 hours.`
        : status === 400 ? `${u.name} can no longer be pinged.`
          : 'Could not send the ping.');
    } finally {
      setBusyId(null);
    }
  };

  if (confirmName) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-6 text-center" data-testid="ping-confirm">
        <span className="grid h-11 w-11 place-items-center rounded-full bg-[var(--n-accent-bg)] text-[var(--n-success)]"><CheckCircle2 size={22} /></span>
        <span className="text-[13px] font-semibold text-[var(--n-ink)]">Ping sent to {confirmName}</span>
      </div>
    );
  }
  if (loading) {
    return <div className="flex items-center justify-center py-8 text-[var(--n-muted-2)]" data-testid="ping-loading"><Loader2 size={18} className="animate-spin" /></div>;
  }
  return (
    <>
      {(users || []).length === 0 ? (
        <div className="py-6 text-center text-[12.5px] text-[var(--n-muted)]" data-testid="ping-empty">No collaborators to ping.</div>
      ) : (
        <div className="flex flex-col gap-1" data-testid="ping-list">
          {(users || []).map((u) => {
            const cooldown = !u.pingable && !!u.pinged_at;
            const seen = !u.pingable && !u.pinged_at && !!u.has_read;
            const busy = busyId === u.id;
            return (
              <button key={u.id} type="button" data-testid="ping-user" data-pingable={u.pingable ? 'true' : 'false'}
                disabled={!u.pingable || busy} onClick={() => doPing(u)}
                title={u.pingable ? `Ping ${u.name}` : cooldown ? pingedAgoLabel(u.pinged_at) : seen ? 'Already opened this task' : u.name}
                className={`n-focus flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left transition-colors ${u.pingable ? 'hover:bg-[var(--n-canvas-2)]' : 'cursor-not-allowed opacity-60'}`}>
                <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-[11px] font-bold text-white ${u.pingable ? 'bg-[var(--n-accent)]' : 'bg-[var(--n-muted-2)]'}`}>{u.initial || (u.name || '?').charAt(0).toUpperCase()}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-medium text-[var(--n-ink)]">{u.name}</span>
                  {cooldown && <span className="block truncate text-[11px] text-[var(--n-muted-2)]">{pingedAgoLabel(u.pinged_at)}</span>}
                  {seen && <span className="block truncate text-[11px] text-[var(--n-muted-2)]">Already opened</span>}
                </span>
                <span className="shrink-0">
                  {busy ? <Loader2 size={16} className="animate-spin text-[var(--n-muted-2)]" />
                    : u.pingable ? <Bell size={16} className="text-[var(--n-accent)]" />
                      : cooldown ? <Clock size={15} className="text-[var(--n-muted-2)]" />
                        : seen ? <Eye size={15} className="text-[var(--n-muted-2)]" /> : null}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {error && <div className="mt-2 rounded-lg bg-[var(--n-canvas-2)] px-2.5 py-1.5 text-[11.5px] font-medium text-[var(--n-danger)]" data-testid="ping-error">{error}</div>}
    </>
  );
};

// Task-detail Ping control — opens PingList in a SmartPopover anchored to the
// button so owners can nudge collaborators without the card menu.
const PingButton = ({ task, onFetchPingStatus, onPing }) => {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const openPanel = () => {
    const r = btnRef.current.getBoundingClientRect();
    setAnchor({ top: r.top, bottom: r.bottom, left: r.left, right: r.right });
    setOpen(true);
  };
  return (
    <>
      <button ref={btnRef} type="button" onClick={openPanel} title="Ping a collaborator" data-testid="task-detail-ping"
        className="n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] px-2 py-1 text-[12px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] transition-colors"><Bell size={13} /> Ping</button>
      {open && anchor && (
        <SmartPopover anchor={anchor} width={288} onClose={() => setOpen(false)}
          testId="task-detail-ping-popover" backdropTestId="task-detail-ping-backdrop" zPanel="z-[311]" zBackdrop="z-[310]">
          <div className="p-3">
            <div className="mb-2.5 flex items-center justify-between gap-2">
              <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-[var(--n-ink)]">Ping a collaborator</span>
              <button type="button" onClick={() => setOpen(false)} data-testid="task-detail-ping-close"
                className="n-focus grid h-6 w-6 shrink-0 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]"><X size={14} /></button>
            </div>
            <PingList task={task} onFetchPingStatus={onFetchPingStatus} onPing={onPing} onConfirmed={() => setOpen(false)} />
          </div>
        </SmartPopover>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Long-press quick actions (mobile) — a compact panel that pops up when a card
// is pressed and held. Lets the user mark done, change the due date, and drop a
// quick comment without opening the full task. Non-recursive, self-contained.
// ---------------------------------------------------------------------------
const TaskQuickActions = ({ task, position, onClose, onToggleComplete, onSetDue, onSetPriority, onPostComment, onFetchPingStatus, onPing }) => {
  const [dueVal, setDueVal] = useState(task.due_at ? toLocalDate(task.due_at) : '');
  const [prio, setPrio] = useState(task.priority || 'normal');
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState(false);
  const done = !!task.is_completed;

  // Ping sub-screen — only the owner of a SHARED task can nudge collaborators.
  const canPing = !!task.is_shared && !task.shared_by;
  const [screen, setScreen] = useState('main'); // 'main' | 'ping'

  // Quick "snooze" — set the due date relative to today (used by the chips).
  const applyQuickDue = (offsetDays) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + offsetDays);
    const s = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    setDueVal(s);
    onSetDue(task, dueIso(s));
  };

  const submitComment = async () => {
    const body = comment.trim();
    if (!body || posting) return;
    setPosting(true);
    try {
      await onPostComment(task, body);
      setComment('');
      setPosted(true);
      window.setTimeout(() => onClose(), 550);
    } catch { /* keep text so the user can retry */ } finally { setPosting(false); }
  };

  const anchor = { top: position.y, bottom: position.y, left: position.x, right: position.x };

  return (
    <SmartPopover anchor={anchor} width={288} onClose={onClose}
      testId="task-quick-actions" backdropTestId="task-quick-actions-backdrop"
      closeGuardMs={400} zPanel="z-[301]" zBackdrop="z-[300]">
      {screen === 'ping' ? (
        <div className="p-3" data-testid="task-quick-actions-ping-screen">
          <div className="mb-2.5 flex items-center gap-2">
            <button type="button" onClick={() => setScreen('main')} data-testid="task-quick-actions-ping-back"
              className="n-focus grid h-6 w-6 shrink-0 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]"><ArrowLeft size={15} /></button>
            <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-[var(--n-ink)]">Ping a collaborator</span>
            <button type="button" onClick={onClose} data-testid="task-quick-actions-ping-close"
              className="n-focus grid h-6 w-6 shrink-0 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]"><X size={14} /></button>
          </div>
          <PingList task={task} onFetchPingStatus={onFetchPingStatus} onPing={onPing} onConfirmed={() => setScreen('main')} />
        </div>
      ) : (
        <div className="p-3">
          <div className="mb-2.5 flex items-center justify-between gap-2">
            <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-[var(--n-ink)]" data-testid="task-quick-actions-title">{task.title || 'Untitled task'}</span>
            <button type="button" onClick={onClose} data-testid="task-quick-actions-close"
              className="n-focus grid h-6 w-6 shrink-0 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]"><X size={14} /></button>
          </div>

          <button type="button" data-testid="task-quick-actions-toggle-complete"
            onClick={() => { onToggleComplete(task); onClose(); }}
            className={`n-focus mb-2.5 flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-[13px] font-semibold transition-colors ${done
              ? 'border border-[var(--n-border)] bg-[var(--n-canvas-2)] text-[var(--n-ink-soft)] hover:bg-[var(--n-surface)]'
              : 'bg-[var(--n-accent)] text-white hover:opacity-90'}`}>
            {done ? <><RotateCcw size={15} /> Mark as not done</> : <><CheckCircle2 size={15} /> Mark as done</>}
          </button>

          {canPing && (
            <button type="button" data-testid="task-quick-actions-ping" onClick={() => setScreen('ping')}
              className="n-focus mb-3 flex w-full items-center justify-center gap-2 rounded-xl border border-[var(--n-border)] px-3 py-2.5 text-[13px] font-semibold text-[var(--n-ink-soft)] transition-colors hover:border-[var(--n-accent)] hover:text-[var(--n-accent)]">
              <Bell size={15} /> Ping a collaborator
            </button>
          )}

          <div className="mb-3">
          <div className="mb-1 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]">
            <Flag size={12} /> Priority
          </div>
          <div className="flex w-full rounded-xl border border-[var(--n-border)] p-0.5" data-testid="task-quick-actions-priority">
            {PRIO_ORDER.map((p) => (
              <button key={p} type="button" data-testid={`task-quick-actions-priority-${p}`}
                onClick={() => { setPrio(p); onSetPriority(task, p); }}
                className={`flex flex-1 items-center justify-center gap-1 rounded-lg px-2 py-1.5 text-[12px] font-semibold transition-colors ${prio === p
                  ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]'
                  : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                <Flag size={11} className={prio === p ? PRIO[p]?.cls : ''} />{PRIO[p]?.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-3">
          <div className="mb-1 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]">
            <CalendarDays size={12} /> Due date
          </div>
          <div className="flex items-center gap-1.5">
            <input type="date" value={dueVal} data-testid="task-quick-actions-due-input"
              onChange={(e) => { setDueVal(e.target.value); onSetDue(task, e.target.value ? dueIso(e.target.value) : null); }}
              className="n-focus min-w-0 flex-1 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-2 text-[13px] text-[var(--n-ink)]" />
            {dueVal && (
              <button type="button" data-testid="task-quick-actions-due-clear" title="Clear due date"
                onClick={() => { setDueVal(''); onSetDue(task, null); }}
                className="n-focus grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-[var(--n-border)] text-[var(--n-muted-2)] hover:text-[var(--n-danger)]"><X size={14} /></button>
            )}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid="task-quick-actions-snooze">
            {[['Tomorrow', 1, 'tomorrow'], ['Next week', 7, 'next-week']].map(([label, off, key]) => (
              <button key={key} type="button" data-testid={`task-quick-actions-snooze-${key}`}
                onClick={() => applyQuickDue(off)}
                className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1 text-[11.5px] font-medium text-[var(--n-ink-soft)] hover:border-[var(--n-accent)] hover:text-[var(--n-accent)] transition-colors">{label}</button>
            ))}
          </div>
        </div>

        <div>
          <div className="mb-1 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]">
            <MessageSquare size={12} /> Quick comment
          </div>
          <div className="flex items-end gap-1.5">
            <textarea value={comment} rows={2} data-testid="task-quick-actions-comment-input"
              onChange={(e) => { setComment(e.target.value); setPosted(false); }}
              placeholder="Add a comment…"
              className="n-focus max-h-24 min-h-[44px] flex-1 resize-none rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-2 text-[13px] text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]" />
            <button type="button" onClick={submitComment} disabled={posting || !comment.trim()} data-testid="task-quick-actions-comment-submit"
              title="Post comment"
              className="n-focus inline-flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-xl bg-[var(--n-accent)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity">
              {posting ? <Loader2 size={15} className="animate-spin" /> : posted ? <Check size={15} /> : <Send size={15} />}
            </button>
          </div>
        </div>
      </div>
      )}
    </SmartPopover>
  );
};

// ---------------------------------------------------------------------------
// Main shell
// ---------------------------------------------------------------------------
export default function TasksAppShell() {
  const { principal, logout, theme, toggleTheme, isSuper, updateDefaultProject } = useNotesAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isDark = theme === 'dark';
  // Expose the current viewer's id to the module-scoped avatar components so
  // they can hide the viewer's own avatar from collaborator stacks. Set in the
  // render body (not an effect) so children read the correct value in the same
  // paint. Stable per session.
  VIEWER_ID = principal?.id || null;

  const [adminOpen, setAdminOpen] = useState(false); // Tasks backup & restore dialog (super admin)
  const [templatesOpen, setTemplatesOpen] = useState(false); // Task templates manager
  const [groupsOpen, setGroupsOpen] = useState(false); // Task groups manager
  const [taskTemplates, setTaskTemplates] = useState([]); // templates for the "start from template" chooser
  const [taskGroups, setTaskGroups] = useState([]); // groups (owned + member) for NL share-with-a-group
  const [saveTemplateTask, setSaveTemplateTask] = useState(null); // task pending "Save as template"

  const [projects, setProjects] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagEditing, setTagEditing] = useState(null); // tag label currently being renamed (sidebar)
  const [tagEditVal, setTagEditVal] = useState('');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('today'); // 'today' | 'upcoming' | 'filters' | <projectId>
  const [upcomingRange, setUpcomingRange] = useState(() => (Number(localStorage.getItem('tasks:upcomingRange')) === 14 ? 14 : 7)); // Upcoming board: 7 or 14 days
  const setUpcomingRangePersist = useCallback((n) => { const v = n === 14 ? 14 : 7; setUpcomingRange(v); try { localStorage.setItem('tasks:upcomingRange', String(v)); } catch { /* noop */ } }, []);
  const [showCompletedUpcoming, setShowCompletedUpcoming] = useState(() => { try { return localStorage.getItem('tasks:upcomingShowDone') !== '0'; } catch { return true; } }); // Upcoming: show completed tasks
  const toggleShowCompletedUpcoming = useCallback(() => setShowCompletedUpcoming((v) => { const nx = !v; try { localStorage.setItem('tasks:upcomingShowDone', nx ? '1' : '0'); } catch { /* noop */ } return nx; }), []);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [railHidden, setRailHidden] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  // Mobile: collapse the search field + filter bar behind two compact icons so
  // the list gets the full screen. Desktop always shows them inline.
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [tagFilterOpen, setTagFilterOpen] = useState(false);
  const [tagFilterQuery, setTagFilterQuery] = useState(''); // search within the "Filter by tag" dropdown
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const [renamingProjectId, setRenamingProjectId] = useState(null); // project rename (detail panel picker)
  const [projectRenameVal, setProjectRenameVal] = useState('');
  const [toast, setToast] = useState(null);
  const [shareDialog, setShareDialog] = useState({ open: false, resource: null });
  const [directory, setDirectory] = useState([]); // Notes users (for NL share-with)
  const [presence, setPresence] = useState({});
  const [commentEvent, setCommentEvent] = useState(null);
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [mentionInbox, setMentionInbox] = useState({ count: 0, items: [] }); // unread @mentions of me
  const [mentionInboxOpen, setMentionInboxOpen] = useState(false); // mentions bell dropdown
  const [pendingCommentFocus, setPendingCommentFocus] = useState(null); // comment_id to scroll/highlight on open
  const [customSnoozeKey, setCustomSnoozeKey] = useState(null);
  const [customSnoozeVal, setCustomSnoozeVal] = useState('');
  const [viewMode, setViewMode] = useState(() => { // 'grid' | 'list' | 'board' | 'calendar' (project views)
    try { const v = localStorage.getItem('tasks:projectViewMode'); return ['list', 'board', 'calendar', 'grid'].includes(v) ? v : 'grid'; } catch { return 'grid'; }
  });
  // All Tasks view: 'grid' (flat date-sorted cards) | 'calendar' (month grid).
  const [allViewMode, setAllViewMode] = useState(() => {
    try { return localStorage.getItem('tasks:allViewMode') === 'calendar' ? 'calendar' : 'grid'; } catch { return 'grid'; }
  });
  // Project Calendar view: first-of-month Date driving the displayed month grid.
  const [calMonth, setCalMonth] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  // Calendar cell density: 'comfortable' (default) or 'compact' (denser rows +
  // taller cells so busy days surface more tasks before scrolling).
  const [calDensity, setCalDensity] = useState(() => { try { return localStorage.getItem('tasks:calDensity') === 'compact' ? 'compact' : 'comfortable'; } catch { return 'comfortable'; } });
  const setCalDensityPref = useCallback((d) => { setCalDensity(d); try { localStorage.setItem('tasks:calDensity', d); } catch { /* noop */ } }, []);
  const [todayViewMode, setTodayViewMode] = useState(() => { // 'grid' | 'list' | 'board' (Today view) — Grid is the default
    try { const v = localStorage.getItem('tasks:todayViewMode'); return (v === 'board' || v === 'grid' || v === 'list') ? v : 'grid'; } catch { return 'grid'; }
  });
  // Completed view: 'grid' (cards grouped by project) | 'list'. Grid is default.
  const [completedViewMode, setCompletedViewMode] = useState(() => {
    try { return localStorage.getItem('tasks:completedViewMode') === 'list' ? 'list' : 'grid'; } catch { return 'grid'; }
  });
  // Filters & Labels view: 'list' (agenda) | 'calendar' (month grid). Lets users
  // spot filtered / label-matched tasks by due date, like Projects & All Tasks.
  const [filtersViewMode, setFiltersViewMode] = useState(() => {
    try { const v = localStorage.getItem('tasks:filtersViewMode'); return (v === 'calendar' || v === 'list' || v === 'grid') ? v : 'grid'; } catch { return 'grid'; }
  });
  const [mentionAlerts, setMentionAlerts] = useState([]);
  const [pingAlerts, setPingAlerts] = useState([]);
  const [dragTaskId, setDragTaskId] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null); // Upcoming board: day ISO being hovered while dragging
  const [boardQuickCol, setBoardQuickCol] = useState(null); // board column key with open quick-add
  const [boardQuickVal, setBoardQuickVal] = useState('');
  const [wipEditCol, setWipEditCol] = useState(null);       // board column key whose WIP editor is open
  const [wipEditVal, setWipEditVal] = useState('');
  const [newProjectColor, setNewProjectColor] = useState(PROJECT_COLORS[1]);
  const [colorMenu, setColorMenu] = useState(null); // projectId whose color palette is open
  const [activityEvent, setActivityEvent] = useState(null);
  const [linkEvent, setLinkEvent] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false); // keyboard shortcuts overlay
  const [cmdOpen, setCmdOpen] = useState(false); // ⌘K command palette (quick filter/jump)
  const [smartAddOpen, setSmartAddOpen] = useState(false); // natural-language "add task" modal
  const [chatOpen, setChatOpen] = useState(() => {
    // A docked chat stays present across Notes ↔ Tasks; auto-open on mount.
    try { return localStorage.getItem('chat_docked') === '1'; } catch { return false; }
  });
  const [chatUnread, setChatUnread] = useState(0);
  const [chatReserve, setChatReserve] = useState(0); // right padding when chat is docked
  // Chat is hidden entirely while Maintenance Lockdown is ON (Super Admin).
  const { enabled: chatEnabled, enabledRef: chatEnabledRef } = useChatEnabled();
  useEffect(() => { try { if (chatEnabled && new URLSearchParams(window.location.search).get('chat')) setChatOpen(true); } catch { /* noop */ } }, [chatEnabled]);
  // If a lockdown flips ON while the panel is open, close it immediately and
  // release any reserved dock width so the content reflows to full width.
  useEffect(() => { if (!chatEnabled) { setChatOpen(false); setChatReserve(0); } }, [chatEnabled]);
  const searchRef = useRef(null);
  const [applyToSeries, setApplyToSeries] = useState(false); // edit-scope for recurring tasks
  const [completedToday, setCompletedToday] = useState([]); // Today view: everything completed today
  const [todayCompletedOpen, setTodayCompletedOpen] = useState(true);
  const [expandedRows, setExpandedRows] = useState(() => new Set()); // list rows with sub-tasks expanded inline
  const [subtaskRows, setSubtaskRows] = useState(() => new Set()); // rows/cards with the tick-only sub-task checklist open
  const [subtaskPopover, setSubtaskPopover] = useState(null); // card sub-task quick popover: { id, top, left, canEdit } | null
  const [quickPanel, setQuickPanel] = useState(null); // mobile long-press quick actions: { task, x, y } | null
  const longPressRef = useRef({ timer: null, x: 0, y: 0, fired: false }); // long-press gesture bookkeeping
  const [collapsedBuckets, setCollapsedBuckets] = useState(() => new Set()); // collapsed due-date groups (by bucket key)
  const [allGridExpanded, setAllGridExpanded] = useState(() => new Set(['today'])); // All Tasks grid: expanded date buckets (Today only by default)
  // Today view: project groups are COLLAPSED by default. We remember which
  // groups the user has EXPANDED for the current browser session so their
  // choice sticks while navigating (sessionStorage, per the product ask).
  const [todayExpandedProjects, setTodayExpandedProjects] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem('tasks:todayExpandedProjects') || '[]')); } catch { return new Set(); }
  });
  // Completed view: which project groups are expanded. Starts EMPTY so every
  // group is COLLAPSED by default; session-remembered so choices persist on nav.
  const [completedExpandedProjects, setCompletedExpandedProjects] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem('tasks:completedExpandedProjects') || '[]')); } catch { return new Set(); }
  });
  const [compFrom, setCompFrom] = useState('');       // Completed view: completed-on range (yyyy-mm-dd)
  const [compTo, setCompTo] = useState('');
  const [compProject, setCompProject] = useState('all'); // Completed view: project filter
  // Today view: advanced filters (client-side over the loaded Today set).
  const [tProject, setTProject] = useState('all');       // 'all' | project id
  const [tDueMode, setTDueMode] = useState('any');       // any | no_due | on | range
  const [tDueOn, setTDueOn] = useState('');              // yyyy-mm-dd (on a specific day)
  const [tDueFrom, setTDueFrom] = useState('');          // yyyy-mm-dd (range start)
  const [tDueTo, setTDueTo] = useState('');              // yyyy-mm-dd (range end)
  const [tShared, setTShared] = useState('any');         // any | with_me | by_me | any_shared
  const [tCompleted, setTCompleted] = useState(false);   // All Tasks: also show a "Completed" group at the end
  const [tNoDue, setTNoDue] = useState(true);            // All Tasks: show the right-hand "No due date" panel
  const [tSaving, setTSaving] = useState(false);         // Today: inline "save filter" open
  const [tSaveName, setTSaveName] = useState('');        // Today: pending saved-filter name
  const [tSaveMenuOpen, setTSaveMenuOpen] = useState(false); // inline save menu (save-new vs update-existing)
  const [inlineFilterId, setInlineFilterId] = useState(null); // saved filter applied via the All Tasks combo
  const [inlineFilterKey, setInlineFilterKey] = useState(null); // snapshot of the inline state at apply time
  const [seriesData, setSeriesData] = useState(null);        // {rule,paused,occurrences[]}
  const [seriesOpen, setSeriesOpen] = useState(false);       // series history expanded
  // --- Saved filters + filter builder (Filters & Labels) ---
  const [savedFilters, setSavedFilters] = useState([]);
  const [overdueTaskCount, setOverdueTaskCount] = useState(0);
  const [todayOpenCount, setTodayOpenCount] = useState(0);
  // Filters & Labels: "Build a filter" panel starts collapsed (user expands on demand).
  const [filterBuilderOpen, setFilterBuilderOpen] = useState(() => {
    try { return localStorage.getItem('tasks:filterBuilderOpen') === '1'; } catch { return false; }
  });
  const [selectedTaskIds, setSelectedTaskIds] = useState(() => new Set());
  const [activeFilterId, setActiveFilterId] = useState(null); // applied saved filter id (view === 'filters')
  const [fDueSet, setFDueSet] = useState([]); // multi-select due scopes: overdue|today|next7|none|custom (OR'd)
  const [fDueStart, setFDueStart] = useState(''); // yyyy-mm-dd (custom)
  const [fDueEnd, setFDueEnd] = useState('');     // yyyy-mm-dd (custom)
  const [fTags, setFTags] = useState([]);
  const [fTagMode, setFTagMode] = useState('any'); // any|all
  const [fProjects, setFProjects] = useState([]);
  const [fStatuses, setFStatuses] = useState([]);     // multi-select: todo|in_progress|done
  const [fPriorities, setFPriorities] = useState([]); // multi-select: high|medium|normal
  const [fShared, setFShared] = useState('any');      // sharing scope (any|with_me|by_me|any_shared)
  const [savingFilter, setSavingFilter] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [renamingFilterId, setRenamingFilterId] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [secOpen, setSecOpen] = useState(() => {
    try { return JSON.parse(localStorage.getItem('tasks:secOpen')) || { filters: false, projects: true, tags: true }; }
    catch { return { filters: false, projects: true, tags: true }; }
  });
  const tagFilterRef = useRef(null);
  const dragFilterRef = useRef(null);
  const projectPickerRef = useRef(null);
  const remindersRef = useRef(null);
  const mentionsRef = useRef(null);

  // WebSocket refs
  const wsRef = useRef(null);
  const handlerRef = useRef(null);
  const selectedIdRef = useRef(null);
  const subscribedRef = useRef(null);
  const reloadTimerRef = useRef(null);
  const openTaskByIdRef = useRef(null);
  const meIdRef = useRef(null);

  const notify = useCallback((msg) => { setToast(msg); window.clearTimeout(notify._t); notify._t = window.setTimeout(() => setToast(null), 2600); }, []);

  const reminders = useTaskReminders({ onOpen: (id) => openTaskByIdRef.current?.(id) });

  useEffect(() => { const t = setTimeout(() => setDebounced(search.trim()), 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  useEffect(() => { meIdRef.current = principal?.id || null; }, [principal]);

  // Module shortcuts — g then n → Notes, g then t → Tasks, g then c → Chat.
  // Typing-aware so it never fires while editing text (inputs, textareas,
  // contenteditable). stopPropagation keeps the second key from also firing a
  // single-key shortcut on the separate window-level listener below.
  const gPendingRef = useRef(null);
  useEffect(() => {
    const isTyping = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = (el.tagName || '').toLowerCase();
      return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
    };
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTyping()) return;
      const k = (e.key || '').toLowerCase();
      if (gPendingRef.current) {
        clearTimeout(gPendingRef.current);
        gPendingRef.current = null;
        if (k === 'n') { e.preventDefault(); e.stopPropagation(); navigate('/notes'); return; }
        if (k === 't') { e.preventDefault(); e.stopPropagation(); navigate('/notes/tasks'); return; }
        if (k === 'c') { if (!chatEnabledRef.current) return; e.preventDefault(); e.stopPropagation(); setChatOpen(true); return; }
      }
      if (k === 'g') {
        gPendingRef.current = setTimeout(() => { gPendingRef.current = null; }, 800);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); if (gPendingRef.current) clearTimeout(gPendingRef.current); };
  }, [navigate]);

  const projectById = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);
  const tagKey = selectedTags.join('|');

  const loadProjects = useCallback(async () => {
    try { const { data } = await notesApi.get('/notes/tasks/projects'); setProjects(data || []); } catch { /* silent */ }
  }, []);
  const loadTags = useCallback(async () => {
    try { const { data } = await notesApi.get('/notes/tasks/tags'); setTags(data || []); } catch { /* silent */ }
  }, []);
  const loadDirectory = useCallback(async () => {
    try { const { data } = await notesApi.get('/notes/directory'); setDirectory(data || []); } catch { /* silent */ }
  }, []);
  const loadTemplates = useCallback(async () => {
    try { const { data } = await notesApi.get('/notes/tasks/templates'); setTaskTemplates(data || []); } catch { /* silent */ }
  }, []);
  const loadGroups = useCallback(async () => {
    try { const { data } = await notesApi.get('/notes/tasks/groups'); setTaskGroups(data || []); } catch { /* silent */ }
  }, []);
  const loadFilters = useCallback(async () => {
    try { const { data } = await notesApi.get(`/notes/tasks/filters?tz_offset=${TZ_OFFSET}`); setSavedFilters(data || []); } catch { /* silent */ }
  }, []);
  const loadCounts = useCallback(async () => {
    try {
      const { data } = await notesApi.get(`/notes/tasks/counts?tz_offset=${TZ_OFFSET}`);
      setOverdueTaskCount(data?.overdue || 0);
      setTodayOpenCount(data?.today_open || 0);
    } catch { /* silent */ }
    // Saved-filter badge counts share the same refresh cadence as sidebar counts.
    loadFilters();
  }, [loadFilters]);

  const toggleSec = useCallback((k) => setSecOpen((s) => {
    const nx = { ...s, [k]: !s[k] };
    try { localStorage.setItem('tasks:secOpen', JSON.stringify(nx)); } catch { /* noop */ }
    return nx;
  }), []);

  // Serialised filter draft — drives task reload while in the Filters view.
  const filterKey = useMemo(
    () => [fDueSet.join(','), fDueStart, fDueEnd, fTags.join(','), fTagMode, fProjects.join(','), fStatuses.join(','), fPriorities.join(',')].join('|'),
    [fDueSet, fDueStart, fDueEnd, fTags, fTagMode, fProjects, fStatuses, fPriorities],
  );

  const rangeFor = useCallback((v) => {
    const now = new Date();
    // Today = due today + overdue (anything up to end of today) + no due date.
    if (v === 'today') return { due_end: endOfDay(now).toISOString(), due_or_none: true };
    // Upcoming = everything due up to the end of the range window. We omit
    // due_start so OVERDUE tasks are fetched too (shown in the overdue rail);
    // the range covers 7 or 14 days depending on the board toggle.
    if (v === 'upcoming') return { due_end: endOfDay(addDays(now, upcomingRange - 1)).toISOString() };
    return {};
  }, [upcomingRange]);

  const didInitialLoadRef = useRef(false); // skeleton shows ONLY on the first load
  const loadTasks = useCallback(async (silent = false) => {
    // In-module navigation (view switches, add/delete/patch, WS refreshes) must
    // never flash the full-screen skeleton — only the very first load does.
    const showSkeleton = !silent && !didInitialLoadRef.current;
    if (showSkeleton) setLoading(true);
    try {
      const now = new Date();
      // Dedicated "Completed" view: every completed task across all projects.
      if (view === 'completed') {
        const p = new URLSearchParams();
        p.append('include_shared', 'true');
        p.append('is_completed', 'true');
        if (compFrom) p.append('completed_start', startOfDay(new Date(`${compFrom}T00:00:00`)).toISOString());
        if (compTo) p.append('completed_end', endOfDay(new Date(`${compTo}T00:00:00`)).toISOString());
        if (compProject !== 'all') p.append('project_id', compProject);
        if (debounced) p.append('q', debounced);
        selectedTags.forEach((t) => p.append('tags', t));
        const { data } = await notesApi.get(`/notes/tasks/tasks?${p.toString()}`);
        setTasks(data || []);
        setCompletedToday([]);
        return;
      }
      // "Shared with my groups": every task reachable via a group I'm a member of.
      if (view === 'groups') {
        const p = new URLSearchParams();
        p.append('shared_scope', 'groups');
        p.append('tz_offset', String(TZ_OFFSET));
        if (debounced) p.append('q', debounced);
        selectedTags.forEach((t) => p.append('tags', t));
        const { data } = await notesApi.get(`/notes/tasks/tasks?${p.toString()}`);
        setTasks(data || []);
        setCompletedToday([]);
        return;
      }
      // "All Tasks": every active task the user owns or that's shared with
      // them, across all projects — flat, date-sorted grid (search + tag aware).
      if (view === 'all') {
        const p = new URLSearchParams();
        p.append('include_shared', 'true');
        // Completed toggle OFF → only active tasks. ON → load everything so
        // completed tasks can surface in their own trailing group.
        if (!tCompleted) p.append('is_completed', 'false');
        p.append('tz_offset', String(TZ_OFFSET));
        if (debounced) p.append('q', debounced);
        selectedTags.forEach((t) => p.append('tags', t));
        const { data } = await notesApi.get(`/notes/tasks/tasks?${p.toString()}`);
        setTasks(data || []);
        setCompletedToday([]);
        return;
      }
      const params = new URLSearchParams();
      params.append('include_shared', 'true');
      params.append('tz_offset', String(TZ_OFFSET));
      if (view === 'filters') {
        // Build query from the active filter draft. Due scopes are OR'd together
        // by the backend; tags & projects AND with them.
        fDueSet.forEach((s) => params.append('due_scopes', s));
        if (fDueSet.includes('custom')) {
          if (fDueStart) params.append('due_start', startOfDay(new Date(`${fDueStart}T00:00:00`)).toISOString());
          if (fDueEnd) params.append('due_end', endOfDay(new Date(`${fDueEnd}T00:00:00`)).toISOString());
        }
        fTags.forEach((t) => params.append('tags', t));
        if (fTags.length) params.append('tag_mode', fTagMode);
        fProjects.forEach((p) => params.append('project_ids', p));
        fStatuses.forEach((s) => params.append('statuses', s));
        fPriorities.forEach((p) => params.append('priorities', p));
      } else {
        const isProject = view !== 'today' && view !== 'upcoming';
        if (isProject) params.append('project_id', view);
        const r = rangeFor(view);
        if (r.due_start) params.append('due_start', r.due_start);
        if (r.due_end) params.append('due_end', r.due_end);
        if (r.due_or_none) params.append('due_or_none', 'true');
        selectedTags.forEach((t) => params.append('tags', t));
      }
      // Completed tasks move into a separate "Completed" section (below the
      // active list) on Today AND on Project list views. Board view keeps
      // completed items in its "done" column, so it loads everything.
      const isProjectList = view !== 'today' && view !== 'upcoming' && view !== 'filters' && view !== 'completed' && viewMode === 'list';
      // Today board keeps completed items in its "done" column (load everything);
      // Today list splits them into a separate "Completed today" section.
      const splitCompleted = (view === 'today' && todayViewMode !== 'board') || isProjectList;
      if (splitCompleted) params.append('is_completed', 'false');
      if (debounced) params.append('q', debounced);
      const { data } = await notesApi.get(`/notes/tasks/tasks?${params.toString()}`);
      setTasks(data || []);
      if (splitCompleted) {
        const cp = new URLSearchParams();
        cp.append('include_shared', 'true');
        cp.append('is_completed', 'true');
        if (view === 'today') {
          cp.append('completed_start', startOfDay(now).toISOString());
          cp.append('completed_end', endOfDay(now).toISOString());
        } else {
          cp.append('project_id', view);
        }
        if (debounced) cp.append('q', debounced);
        selectedTags.forEach((t) => cp.append('tags', t));
        try {
          const { data: cData } = await notesApi.get(`/notes/tasks/tasks?${cp.toString()}`);
          setCompletedToday(cData || []);
        } catch { setCompletedToday([]); }
      } else {
        setCompletedToday([]);
      }
    } catch { setTasks([]); setCompletedToday([]); } finally { didInitialLoadRef.current = true; if (showSkeleton) setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, debounced, tagKey, rangeFor, filterKey, compFrom, compTo, compProject, viewMode, todayViewMode, tCompleted]);

  // Rename a tag everywhere it's used (cascades to all of the user's tasks).
  const renameTag = useCallback(async (oldTag, nextRaw) => {
    const next = String(nextRaw || '').trim().replace(/^#/, '').trim();
    setTagEditing(null); setTagEditVal('');
    if (!next || next === oldTag) return;
    try {
      const { data } = await notesApi.patch('/notes/tasks/tags', { old: oldTag, new: next });
      const fin = data.tag || next;
      setSelectedTags((prev) => prev.map((t) => (t === oldTag ? fin : t)));
      setFTags((prev) => prev.map((t) => (t === oldTag ? fin : t)));
      await Promise.all([loadTags(), loadTasks(true)]);
      notify(`Tag renamed to “${fin}”${data.updated ? ` · updated ${data.updated} task${data.updated === 1 ? '' : 's'}` : ''}.`);
    } catch (e) { notify(e?.response?.data?.detail || 'Could not rename tag.'); }
  }, [loadTags, loadTasks, notify]);

  useEffect(() => { loadProjects(); loadTags(); loadFilters(); loadCounts(); loadDirectory(); loadTemplates(); loadGroups(); }, [loadProjects, loadTags, loadFilters, loadCounts, loadDirectory, loadTemplates, loadGroups]);
  useEffect(() => { loadTasks(); loadCounts(); }, [loadTasks, loadCounts]);
  useEffect(() => { setSelectedTaskIds(new Set()); }, [view]);

  // Close popovers on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (tagFilterRef.current && !tagFilterRef.current.contains(e.target)) setTagFilterOpen(false);
      // The detail panel is rendered twice (desktop + mobile drawer), so a single
      // ref can't reliably contain both pickers — match any picker container via
      // a data attribute instead so an in-menu click never closes it prematurely.
      if (!e.target.closest?.('[data-project-picker]')) setProjectPickerOpen(false);
      if (remindersRef.current && !remindersRef.current.contains(e.target)) setRemindersOpen(false);
      if (mentionsRef.current && !mentionsRef.current.contains(e.target)) setMentionInboxOpen(false);
      if (!e.target.closest('[data-testid="tasks-project-color-menu"]') && !e.target.closest('[data-testid="tasks-sidebar-project-color"]')) setColorMenu(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // ---- WebSocket plumbing --------------------------------------------------
  const wsSend = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) { try { ws.send(JSON.stringify(obj)); } catch { /* ignore */ } }
  }, []);
  const subscribeTask = useCallback((id) => {
    const prev = subscribedRef.current;
    if (prev && prev !== id) wsSend({ type: 'unsubscribe', task_id: prev });
    if (id) wsSend({ type: 'subscribe', task_id: id });
    subscribedRef.current = id;
  }, [wsSend]);

  const applyLocal = useCallback((updated) => {
    setTasks((list) => list.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
    setSelectedTask((s) => (s && s.id === updated.id ? { ...s, ...updated } : s));
  }, []);

  // Unread @mentions of me (mentions inbox bell + jump-to-comment).
  const loadMentions = useCallback(async () => {
    try {
      const { data } = await notesApi.get('/notes/tasks/mentions/unread');
      setMentionInbox({ count: data?.count || 0, items: data?.items || [] });
    } catch { /* keep last known */ }
  }, []);
  useEffect(() => { loadMentions(); }, [loadMentions]); // initial mentions load

  // Mirror of `tasks` for reading current state inside callbacks without
  // making them depend on (and re-run with) every list change.
  const tasksRef = useRef([]);
  useEffect(() => { tasksRef.current = tasks; }, [tasks]);

  // Mirror of the open inline-editor rows. Used by patchTask to avoid a list
  // re-sort (which would move the row to another date bucket and UNMOUNT the
  // open editor + native date picker — the "page refresh" users reported)
  // while a row is being edited inline. We reconcile ordering on collapse.
  const expandedRowsRef = useRef(new Set());
  useEffect(() => { expandedRowsRef.current = expandedRows; }, [expandedRows]);
  const pendingResortRef = useRef(false);

  const openTask = useCallback(async (t) => {
    setSelectedId(t.id);
    setSelectedTask(t && t.title ? t : null);
    subscribeTask(t.id);
    try {
      const { data } = await notesApi.get(`/notes/tasks/tasks/${t.id}`);
      setSelectedTask(data);
      // Record that I've opened this task on any SHARED task: it turns my avatar
      // green on the owner's card (read receipt) AND clears my unread @mention
      // badge (I've now seen the comments). Skip for purely personal tasks —
      // they can't have collaborators, so no mentions/receipts apply.
      const shared = data && ((data.role && data.role !== 'owner') || data.shared_by || data.is_shared);
      if (shared) notesApi.post(`/notes/tasks/tasks/${data.id}/read`).then(() => loadMentions()).catch(() => { /* non-critical */ });
      if (data) {
        applyLocal({ id: data.id, unread_mention: false, unread_mention_count: 0 });
        // Optimistically drop this task's mentions from the inbox badge.
        setMentionInbox((mi) => { const items = mi.items.filter((x) => x.task_id !== data.id); return { count: items.length, items }; });
      }
    } catch { /* keep light */ }
  }, [subscribeTask, applyLocal, loadMentions]);
  useEffect(() => { openTaskByIdRef.current = (id) => openTask({ id }); }, [openTask]);

  // ---- Web Push deep-linking ------------------------------------------------
  // A push click lands on /notes/tasks?task=<id>[&comment=<id>]. Open that task
  // (jumping to the comment when present), then strip the params so it doesn't
  // reopen on later renders.
  useEffect(() => {
    const taskId = searchParams.get('task');
    if (!taskId) return;
    const commentId = searchParams.get('comment');
    if (commentId) setPendingCommentFocus(commentId);
    openTask({ id: taskId });
    const next = new URLSearchParams(searchParams);
    next.delete('task'); next.delete('comment');
    setSearchParams(next, { replace: true });
  }, [searchParams, openTask, setSearchParams]);

  // Auto-open the Add-a-Task dialog when arriving with ?compose=1 (e.g. the "T"
  // shortcut pressed from the Planner module routes here).
  useEffect(() => {
    if (searchParams.get('compose') !== '1') return;
    setSmartAddOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('compose');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // The service worker asks the (already open) app to route in-place after a
  // notification click. Same-origin path only — router handles the rest.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMsg = (e) => {
      const d = e.data;
      if (d && d.type === 'inventique-push-navigate' && d.url) {
        try { navigate(d.url); } catch { /* ignore bad path */ }
      }
    };
    navigator.serviceWorker.addEventListener('message', onMsg);
    return () => navigator.serviceWorker.removeEventListener('message', onMsg);
  }, [navigate]);

  // Open a mentioned task and jump straight to the comment that tagged me.
  const openMention = useCallback((item) => {
    if (!item) return;
    setPendingCommentFocus(item.comment_id || null);
    setMentionInboxOpen(false);
    setMentionInbox((mi) => { const items = mi.items.filter((x) => x.task_id !== item.task_id); return { count: items.length, items }; });
    openTask({ id: item.task_id });
  }, [openTask]);

  const closeDetail = useCallback(() => {
    if (subscribedRef.current) { wsSend({ type: 'unsubscribe', task_id: subscribedRef.current }); subscribedRef.current = null; }
    setSelectedId(null); setSelectedTask(null); setCommentEvent(null); setPendingCommentFocus(null);
  }, [wsSend]);

  const scheduleReload = useCallback(() => {
    if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
    // Silent reload: WS-driven list refreshes (incl. the echo of your own inline
    // edits) must NOT flash the skeleton or unmount the open inline editor.
    reloadTimerRef.current = setTimeout(() => {
      // While an inline editor is open, a re-sort would move the row to another
      // date bucket and unmount the editor (and its native date picker) — the
      // "page refresh" users report. Defer until the editor closes.
      if (expandedRowsRef.current.size > 0) { pendingResortRef.current = true; return; }
      loadTasks(true); loadProjects(); loadTags(); loadCounts(); loadMentions();
    }, 400);
  }, [loadTasks, loadProjects, loadTags, loadCounts, loadMentions]);

  // Live ref so WS reconnects / tab refocus can resync without a stale closure.
  const scheduleReloadRef = useRef(null);
  useEffect(() => { scheduleReloadRef.current = scheduleReload; }, [scheduleReload]);

  // Apply a re-sort/refetch that was deferred while an inline editor was open.
  const flushPendingResort = useCallback(() => {
    if (!pendingResortRef.current) return;
    pendingResortRef.current = false;
    loadTasks(true); loadProjects(); loadTags(); loadCounts();
  }, [loadTasks, loadProjects, loadTags, loadCounts]);

  // A sub-task change can auto-complete / reopen its owning task server-side —
  // mirror that onto the open detail + the visible row. Only when the task's
  // completion actually FLIPPED do we do a *silent* refresh (no loading flash,
  // so the inline sub-task list never unmounts → no refresh loop) so the task
  // lands in the right section (active vs Completed) and badges update.
  const syncTaskFromSubtasks = useCallback((tpatch) => {
    if (!tpatch || !tpatch.id) return;
    const cur = tasksRef.current.find((x) => x.id === tpatch.id);
    const completionChanged = cur ? (!!cur.is_completed !== !!tpatch.is_completed) : false;
    setSelectedTask((s) => (s && s.id === tpatch.id ? { ...s, is_completed: tpatch.is_completed, status: tpatch.status } : s));
    setTasks((list) => list.map((x) => (x.id === tpatch.id ? { ...x, is_completed: tpatch.is_completed, status: tpatch.status } : x)));
    if (completionChanged) { loadTasks(true); loadProjects(); loadCounts(); }
  }, [loadTasks, loadProjects, loadCounts]);

  const toggleRowExpand = useCallback((id) => setExpandedRows((s) => {
    const n = new Set(s);
    if (n.has(id)) {
      n.delete(id);
      // Editor closing — apply any due/project change (or WS refresh) that we
      // deferred so the row lands correctly now (silent, no skeleton flash).
      if (n.size === 0) flushPendingResort();
    } else {
      n.add(id);
    }
    return n;
  }), [flushPendingResort]);

  // Open/close the lightweight tick-only sub-task checklist under a row/card.
  // Kept separate from ``expandedRows`` (the full inline editor) so a user can
  // just tick sub-tasks without the editor. Opening one closes the other to
  // avoid a doubled sub-task list.
  const toggleSubtaskRow = useCallback((id) => {
    setExpandedRows((s) => {
      if (!s.has(id)) return s;
      const n = new Set(s); n.delete(id);
      if (n.size === 0) flushPendingResort();
      return n;
    });
    setSubtaskRows((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, [flushPendingResort]);

  const handleWsMessage = useCallback((msg) => {
    const t = msg?.type;
    if (t === 'task_updated' && msg.task) {
      const n = msg.task;
      setTasks((prev) => prev.map((x) => (x.id === n.id ? {
        ...x,
        title: n.title, snippet: n.snippet, priority: n.priority, due_at: n.due_at, tags: n.tags,
        is_completed: n.is_completed, completed_at: n.completed_at, completed_by: n.completed_by,
        reminder_at: n.reminder_at, reminder_rule: n.reminder_rule,
        has_attachments: n.has_attachments, attachment_count: n.attachment_count, updated_at: n.updated_at,
      } : x)));
      if (n.id === selectedIdRef.current) {
        setSelectedTask((prev) => (prev ? {
          ...prev,
          is_completed: n.is_completed, completed_at: n.completed_at, completed_by: n.completed_by,
          priority: n.priority, due_at: n.due_at, tags: n.tags,
          reminder_at: n.reminder_at, reminder_rule: n.reminder_rule,
          attachments: n.attachments || prev.attachments,
          has_attachments: n.has_attachments, attachment_count: n.attachment_count,
        } : prev));
      }
    } else if (t === 'task_deleted') {
      const id = msg.task_id;
      setTasks((prev) => prev.filter((x) => x.id !== id));
      if (id === selectedIdRef.current) { closeDetail(); notify('This task was deleted by its owner.'); }
    } else if (t === 'tasks_list_changed') {
      scheduleReload();
    } else if (t === 'tasks_shares_changed') {
      scheduleReload();
      reminders.refreshUpcoming();
    } else if (t === 'tasks_templates_changed') {
      loadTemplates();
    } else if (t === 'task_comment' && msg.comment) {
      if (msg.task_id === selectedIdRef.current) setCommentEvent({ kind: 'add', task_id: msg.task_id, comment: msg.comment, ts: Date.now() });
      setTasks((prev) => prev.map((x) => (x.id === msg.task_id
        ? { ...x, comment_count: (typeof msg.comment_count === 'number' ? msg.comment_count : (x.comment_count || 0) + 1) }
        : x)));
      // Subtle in-app toast for collaborators so they notice a new comment without
      // watching the badge. Skip my own comments and the task I'm already viewing.
      const c = msg.comment;
      if (c && c.author_id && c.author_id !== meIdRef.current && msg.task_id !== selectedIdRef.current) {
        const title = msg.task_title || 'a task';
        const author = c.author_name || 'Someone';
        notify(`New comment on "${title}" · ${author}`);
      }
    } else if (t === 'task_comment_deleted') {
      if (msg.task_id === selectedIdRef.current) setCommentEvent({ kind: 'del', task_id: msg.task_id, comment_id: msg.comment_id, ts: Date.now() });
      setTasks((prev) => prev.map((x) => (x.id === msg.task_id
        ? { ...x, comment_count: (typeof msg.comment_count === 'number' ? msg.comment_count : Math.max(0, (x.comment_count || 0) - 1)) }
        : x)));
    } else if (t === 'task_links_changed') {
      // A note was linked/unlinked. Refresh THIS user's visible linked-note
      // count on the card (per-user ACL), and refresh the open detail section.
      const lid = msg.task_id;
      notesApi.get(`/notes/tasks/tasks/${lid}/links`).then(({ data }) => {
        const n = (data.links || []).length;
        setTasks((prev) => prev.map((x) => (x.id === lid ? { ...x, linked_notes_count: n } : x)));
      }).catch(() => { /* ignore */ });
      if (lid === selectedIdRef.current) setLinkEvent({ task_id: lid, ts: Date.now() });
    } else if (t === 'task_note_linked') {
      // In-app notification for collaborators who can access both note & task.
      notify(`${msg.by_name || 'Someone'} linked "${msg.note_title || 'a note'}" to "${msg.task_title || 'a task'}"`);
    } else if (t === 'task_presence' && msg.task_id) {
      setPresence((prev) => ({ ...prev, [msg.task_id]: msg.users || [] }));
    } else if (t === 'task_mention') {
      reminders.playChime?.();
      const item = { key: `${msg.comment_id || Date.now()}`, task_id: msg.task_id, comment_id: msg.comment_id, task_title: msg.task_title, author_name: msg.author_name, snippet: msg.snippet, ts: Date.now() };
      setMentionAlerts((prev) => [item, ...prev.filter((x) => x.key !== item.key)].slice(0, 4));
      // Light up the task's comment badge (bold) — I've been tagged and haven't
      // opened it yet. Cleared once I open the task (see openTask).
      setTasks((prev) => prev.map((x) => (x.id === msg.task_id ? { ...x, unread_mention: true, unread_mention_count: (x.unread_mention_count || 0) + 1 } : x)));
      loadMentions();
    } else if (t === 'task_activity' && msg.entry) {
      if (msg.task_id === selectedIdRef.current) setActivityEvent({ task_id: msg.task_id, entry: msg.entry, ts: Date.now() });
    } else if (t === 'task_ping' && msg.task_id) {
      // A live "your attention is needed" nudge — the owner just pinged me.
      reminders.playChime?.();
      const item = { key: `ping-${msg.task_id}-${Date.now()}`, task_id: msg.task_id, task_title: msg.task_title, from_name: msg.from_name };
      setPingAlerts((prev) => [item, ...prev.filter((x) => x.task_id !== msg.task_id)].slice(0, 4));
    }
  }, [notify, closeDetail, scheduleReload, reminders, loadTemplates, loadMentions]);
  useEffect(() => { handlerRef.current = handleWsMessage; }, [handleWsMessage]);

  useEffect(() => {
    const token = localStorage.getItem('notes_token') || localStorage.getItem('inv_token');
    if (!token) return;
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/^http/i, 'ws');
    const url = `${base}/api/notes/tasks/ws?token=${encodeURIComponent(token)}`;
    let closed = false; let pingIv = null; let reconnectTimer = null; let firstOpen = true;
    const connect = () => {
      let ws;
      try { ws = new WebSocket(url); } catch { return; }
      wsRef.current = ws;
      ws.onopen = () => {
        pingIv = setInterval(() => { try { if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' })); } catch { /* ignore */ } }, 25000);
        const sid = selectedIdRef.current;
        if (sid) { try { ws.send(JSON.stringify({ type: 'subscribe', task_id: sid })); } catch { /* ignore */ } subscribedRef.current = sid; }
        // Catch up on anything missed while the socket was down (WS events have
        // no replay). The very first connect is skipped — the mount already
        // loaded the list. Every RE-connect resyncs so the list is never stale.
        if (!firstOpen) { try { scheduleReloadRef.current?.(); } catch { /* ignore */ } }
        firstOpen = false;
      };
      ws.onmessage = (e) => { try { handlerRef.current?.(JSON.parse(e.data)); } catch { /* ignore */ } };
      ws.onclose = () => { if (pingIv) clearInterval(pingIv); if (!closed) reconnectTimer = setTimeout(connect, 2500); };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };
    connect();
    return () => { closed = true; if (pingIv) clearInterval(pingIv); if (reconnectTimer) clearTimeout(reconnectTimer); try { wsRef.current?.close(); } catch { /* ignore */ } };
  }, []);

  // Resync when the tab becomes visible / regains focus. On mobile & tablets the
  // OS suspends backgrounded pages, so the WebSocket is torn down and any task
  // updates that happened meanwhile (e.g. a collaborator completing a shared
  // task) are missed. Returning to the app now silently refreshes the list so
  // the user never has to pull-to-refresh or reload.
  useEffect(() => {
    const resync = () => { if (document.visibilityState === 'visible') { try { scheduleReloadRef.current?.(); } catch { /* ignore */ } } };
    document.addEventListener('visibilitychange', resync);
    window.addEventListener('focus', resync);
    return () => { document.removeEventListener('visibilitychange', resync); window.removeEventListener('focus', resync); };
  }, []);

  // ---- Mutations ----
  const createProject = useCallback(async () => {
    const name = newProjectName.trim();
    if (!name) { setNewProjectOpen(false); setNewProjectName(''); return; }
    try {
      const { data } = await notesApi.post('/notes/tasks/projects', { name, color: newProjectColor });
      setNewProjectName(''); setNewProjectOpen(false); setNewProjectColor(PROJECT_COLORS[1]);
      await loadProjects();
      setView(data.id);
    } catch { notify('Could not create project.'); }
  }, [newProjectName, newProjectColor, loadProjects, notify]);

  const recolorProject = useCallback(async (p, color) => {
    setColorMenu(null);
    setProjects((prev) => prev.map((x) => (x.id === p.id ? { ...x, color } : x)));
    try { await notesApi.patch(`/notes/tasks/projects/${p.id}`, { color }); }
    catch { notify('Could not update colour.'); loadProjects(); }
  }, [notify, loadProjects]);

  // Rename an owned project inline (from the detail-panel project picker).
  const renameProjectInline = useCallback(async (pid) => {
    const name = projectRenameVal.trim();
    setRenamingProjectId(null);
    if (!name) return;
    setProjects((prev) => prev.map((x) => (x.id === pid ? { ...x, name } : x)));
    try { await notesApi.patch(`/notes/tasks/projects/${pid}`, { name }); loadProjects(); notify('Project renamed.'); }
    catch (e) { notify(e?.response?.data?.detail || 'Could not rename project.'); loadProjects(); }
  }, [projectRenameVal, loadProjects, notify]);

  // Rename a project directly (used by the inline editor's project picker).
  const renameProject = useCallback(async (pid, name) => {
    const nm = (name || '').trim();
    if (!nm) return;
    setProjects((prev) => prev.map((x) => (x.id === pid ? { ...x, name: nm } : x)));
    try { await notesApi.patch(`/notes/tasks/projects/${pid}`, { name: nm }); loadProjects(); loadTasks(true); notify('Project renamed.'); }
    catch (e) { notify(e?.response?.data?.detail || 'Could not rename project.'); loadProjects(); }
  }, [loadProjects, loadTasks, notify]);

  // Board column quick-add: create a task directly into a column's status.
  const addBoardTask = useCallback(async (projectId, status, title) => {
    const clean = (title || '').trim();
    if (!clean) { setBoardQuickCol(null); setBoardQuickVal(''); return; }
    try {
      const { data } = await notesApi.post('/notes/tasks/tasks', { project_id: projectId, title: clean, status });
      setBoardQuickVal('');
      await loadTasks(); loadProjects(); loadCounts();
      return data;
    } catch { notify('Could not add task.'); }
  }, [loadTasks, loadProjects, loadCounts, notify]);

  // The user's chosen default project for Smart Add / quick add. Falls back to
  // the first favourite owned project, then the first owned project. Shared
  // (foreign-owned) projects are never eligible as a default.
  const defaultProjectId = principal?.default_project_id || null;
  const resolveDefaultPid = useCallback(() => {
    const owned = projects.filter((p) => !p.shared_by);
    const chosen = defaultProjectId && owned.find((p) => p.id === defaultProjectId)?.id;
    return chosen || owned.find((p) => p.is_favorite)?.id || owned[0]?.id || null;
  }, [projects, defaultProjectId]);

  // Today board quick-add: create a task due today, in the column's status,
  // filed under the user's default (favourite/first) owned project.
  const addTodayBoardTask = useCallback(async (status, title) => {
    const clean = (title || '').trim();
    if (!clean) { setBoardQuickCol(null); setBoardQuickVal(''); return; }
    const pid = resolveDefaultPid();
    if (!pid) { setNewProjectOpen(true); notify('Create a project first.'); return; }
    try {
      await notesApi.post('/notes/tasks/tasks', {
        project_id: pid, title: clean, status,
        due_at: dueIso(toLocalDate(new Date().toISOString())),
      });
      setBoardQuickVal('');
      await loadTasks(); loadProjects(); loadCounts();
    } catch { notify('Could not add task.'); }
  }, [resolveDefaultPid, loadTasks, loadProjects, loadCounts, notify]);

  // Per-column WIP limit persisted on the project.
  const setWipLimit = useCallback(async (project, colKey, value) => {
    const next = { ...(project.wip_limits || {}) };
    const n = parseInt(value, 10);
    if (!Number.isNaN(n) && n > 0) next[colKey] = n; else delete next[colKey];
    setWipEditCol(null); setWipEditVal('');
    setProjects((prev) => prev.map((x) => (x.id === project.id ? { ...x, wip_limits: next } : x)));
    try { await notesApi.patch(`/notes/tasks/projects/${project.id}`, { wip_limits: next }); }
    catch { notify('Could not update limit.'); loadProjects(); }
  }, [notify, loadProjects]);

  const toggleFavorite = useCallback(async (p, e) => {
    e?.stopPropagation();
    try { await notesApi.patch(`/notes/tasks/projects/${p.id}`, { is_favorite: !p.is_favorite }); loadProjects(); }
    catch { notify('Could not update project.'); }
  }, [loadProjects, notify]);

  // Toggle a project as the default (clicking the current default clears it).
  const setAsDefaultProject = useCallback(async (p, e) => {
    e?.stopPropagation();
    if (p.shared_by) { notify('Only projects you own can be the default.'); return; }
    const next = defaultProjectId === p.id ? null : p.id;
    const ok = await updateDefaultProject(next);
    if (ok) notify(next ? `“${p.name}” is now your default project.` : 'Default project cleared.');
    else notify('Could not update default project.');
  }, [defaultProjectId, updateDefaultProject, notify]);

  // Set/clear the default project directly by id (used by the Smart Add selector).
  const changeDefaultProject = useCallback(async (pid) => {
    const next = pid || null;
    const ok = await updateDefaultProject(next);
    if (ok) {
      const p = projects.find((x) => x.id === next);
      notify(next && p ? `“${p.name}” is now your default project.` : 'Default project cleared.');
    } else notify('Could not update default project.');
  }, [updateDefaultProject, projects, notify]);

  const deleteProject = useCallback(async (p, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete project “${p.name}” and its tasks?`)) return;
    try {
      await notesApi.delete(`/notes/tasks/projects/${p.id}`);
      if (view === p.id) setView('today');
      loadProjects(); loadTasks(); notify('Project deleted.');
    } catch { notify('Could not delete project.'); }
  }, [view, loadProjects, loadTasks, notify]);

  const addTask = useCallback(async (projectId, title, dueOverride = 'auto') => {
    const pid = projectId || resolveDefaultPid();
    if (!pid) { setNewProjectOpen(true); notify('Create a project first.'); return; }
    const due = dueOverride !== 'auto'
      ? dueOverride
      : ((view === 'today' || view === 'upcoming') ? dueIso(toLocalDate(new Date().toISOString())) : null);
    try {
      const { data } = await notesApi.post('/notes/tasks/tasks', { project_id: pid, title: title || 'Untitled task', due_at: due });
      await loadTasks(); loadTags(); loadProjects();
      // Open the inline editor directly on the new row (Upcoming has no inline
      // editor, so fall back to the detail panel there).
      if (view === 'upcoming') openTask(data);
      else setExpandedRows((s) => { const n = new Set(s); n.add(data.id); return n; });
    } catch { notify('Could not add task.'); }
  }, [resolveDefaultPid, view, loadTasks, loadTags, loadProjects, openTask, notify]);

  // Representative due date (local end-of-day ISO) for a due-date bucket, so the
  // "Add task" under a group lands the new task inside that same group.
  const bucketDueIso = useCallback((key) => {
    const t0 = startOfDay(new Date());
    const dow = t0.getDay();
    const wIdx = (dow + 6) % 7;
    const endThisWeek = startOfDay(addDays(t0, 6 - wIdx));
    const endNextWeek = addDays(endThisWeek, 7);
    const endThisMonth = startOfDay(new Date(t0.getFullYear(), t0.getMonth() + 1, 0));
    const endNextMonth = startOfDay(new Date(t0.getFullYear(), t0.getMonth() + 2, 0));
    const map = {
      today: t0,
      tomorrow: addDays(t0, 1),
      this_week: endThisWeek,
      next_week: endNextWeek,
      this_month: endThisMonth,
      next_month: endNextMonth,
      later: addDays(endNextMonth, 1),
    };
    const d = map[key];
    return d ? dueIso(toLocalDate(d.toISOString())) : null;
  }, []);

  const addTaskInBucket = useCallback((bucketKey, projectId) => {
    addTask(projectId, null, bucketKey === 'no_due' ? null : bucketDueIso(bucketKey));
  }, [addTask, bucketDueIso]);

  const toggleBucket = useCallback((key) => setCollapsedBuckets((s) => {
    const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n;
  }), []);

  // All Tasks (grid) date-group expand/collapse. Only "Today" is open by
  // default; the set of expanded bucket keys persists for the session.
  const toggleAllGrid = useCallback((key) => setAllGridExpanded((s) => {
    const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n;
  }), []);

  // Today project-group expand/collapse (collapsed by default). Persist the
  // set of expanded project ids for the session so navigating away and back
  // keeps the user's choice.
  const toggleTodayProject = useCallback((pid) => setTodayExpandedProjects((s) => {
    const n = new Set(s); n.has(pid) ? n.delete(pid) : n.add(pid);
    try { sessionStorage.setItem('tasks:todayExpandedProjects', JSON.stringify([...n])); } catch { /* ignore */ }
    return n;
  }), []);
  const toggleCompletedProject = useCallback((pid) => setCompletedExpandedProjects((s) => {
    const n = new Set(s); n.has(pid) ? n.delete(pid) : n.add(pid);
    try { sessionStorage.setItem('tasks:completedExpandedProjects', JSON.stringify([...n])); } catch { /* ignore */ }
    return n;
  }), []);

  const patchTask = useCallback(async (id, patch) => {
    try {
      const { data } = await notesApi.patch(`/notes/tasks/tasks/${id}`, patch);
      applyLocal(data);
      if ('tags' in patch) loadTags();
      // Reconcile ordering / sectioning after structural changes, but do it
      // SILENTLY (no skeleton flash) so inline editing doesn't feel like the
      // whole page keeps refreshing. applyLocal already updated the row.
      if ('due_at' in patch || 'project_id' in patch || 'is_completed' in patch || 'status' in patch) {
        loadProjects(); loadCounts();
        // If THIS row's inline editor is open, defer the list re-sort until it
        // closes — re-sorting now would move the row to another date bucket and
        // unmount the editor (closing the native date picker mid-interaction).
        if (expandedRowsRef.current.has(id)) pendingResortRef.current = true;
        else loadTasks(true);
      }
      if ('reminder_at' in patch || 'reminder_rule' in patch) reminders.refreshUpcoming();
      return data;
    } catch (e) { notify(e?.response?.data?.detail || 'Could not save changes.'); }
  }, [applyLocal, loadTags, loadTasks, loadProjects, loadCounts, notify, reminders]);

  // Content-field edits on a recurring task can optionally apply to every future
  // occurrence (updates the series template) when "apply to future" is on.
  const patchTaskScoped = useCallback((id, patch) => {
    const recurring = selectedTask && selectedTask.recurrence && selectedTask.recurrence !== 'none';
    return patchTask(id, recurring && applyToSeries ? { ...patch, apply_to_series: true } : patch);
  }, [patchTask, selectedTask, applyToSeries]);

  const loadSeries = useCallback(async (id) => {
    try {
      const { data } = await notesApi.get(`/notes/tasks/tasks/${id}/series`);
      setSeriesData(data);
    } catch { setSeriesData(null); }
  }, []);

  const pauseSeries = useCallback(async (id, paused) => {
    try {
      await notesApi.post(`/notes/tasks/tasks/${id}/series/pause`, { paused });
      applyLocal({ id, recurrence_paused: paused });
      setSelectedTask((prev) => (prev && prev.id === id ? { ...prev, recurrence_paused: paused } : prev));
      loadSeries(id);
      notify(paused ? 'Series paused — no new copies until you resume.' : 'Series resumed.');
    } catch (e) { notify(e?.response?.data?.detail || 'Could not update the series.'); }
  }, [applyLocal, loadSeries, notify]);

  // When the open task changes (or its recurrence flips), reset the edit-scope
  // and (re)load the series history for recurring tasks.
  const selRecur = selectedTask?.recurrence;
  const selId = selectedTask?.id;
  useEffect(() => {
    setApplyToSeries(false); setSeriesOpen(false); setSeriesData(null);
    if (selId && selRecur && selRecur !== 'none') loadSeries(selId);
  }, [selId, selRecur, loadSeries]);

  const moveTaskToStatus = useCallback((taskId, status) => {
    const t = tasks.find((x) => x.id === taskId);
    if (!t || statusOf(t) === status) return;
    applyLocal({ id: taskId, status, is_completed: status === 'done' });
    patchTask(taskId, { status });
  }, [tasks, applyLocal, patchTask]);

  // Upcoming board drag-to-reschedule: dropping a task card on another day sets
  // its due date to that day, preserving the original time-of-day when present.
  const rescheduleTaskToDay = useCallback((taskId, day) => {
    const t = tasks.find((x) => x.id === taskId);
    if (!t) return;
    const targetDateStr = toLocalDate(day.toISOString());
    if (t.due_at && toLocalDate(t.due_at) === targetDateStr) return; // already on this day
    let newIso;
    if (t.due_at) {
      const cur = new Date(t.due_at);
      const nd = new Date(day);
      nd.setHours(cur.getHours(), cur.getMinutes(), cur.getSeconds(), cur.getMilliseconds());
      newIso = nd.toISOString();
    } else {
      newIso = dueIso(targetDateStr);
    }
    applyLocal({ id: taskId, due_at: newIso });
    patchTask(taskId, { due_at: newIso });
    notify(`Moved to ${day.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}.`);
  }, [tasks, applyLocal, patchTask, notify]);

  const toggleComplete = useCallback(async (t) => {
    applyLocal({ id: t.id, is_completed: !t.is_completed });
    try {
      const { data } = await notesApi.post(`/notes/tasks/tasks/${t.id}/toggle-complete`); applyLocal(data);
      loadProjects(); loadCounts();
      // Move the row between the active list and the "Completed" section
      // (silent = no skeleton flash) on views that split completed tasks.
      loadTasks(true);
    }
    catch { notify('Could not update task.'); applyLocal({ id: t.id, is_completed: t.is_completed }); }
  }, [applyLocal, loadProjects, loadCounts, loadTasks, notify]);

  // ---- Mobile long-press quick actions (mark done / due date / comment) ----
  // Change a card's due date straight from the long-press panel.
  const quickSetDue = useCallback((t, iso) => {
    patchTask(t.id, { due_at: iso });
    notify(iso ? 'Due date updated.' : 'Due date cleared.');
  }, [patchTask, notify]);

  // Change a card's priority straight from the long-press panel.
  const quickSetPriority = useCallback((t, p) => {
    patchTask(t.id, { priority: p });
    notify(`Priority set to ${PRIO[p]?.label || p}.`);
  }, [patchTask, notify]);

  // Post a plain comment (no @mentions) from the long-press panel, and bump the
  // card's comment badge optimistically so the change is visible right away.
  const quickComment = useCallback(async (t, body) => {
    await notesApi.post(`/notes/tasks/tasks/${t.id}/comments`, { body, mentions: [], invite_user_ids: [] });
    applyLocal({ id: t.id, comment_count: (t.comment_count || 0) + 1 });
    notify('Comment added.');
  }, [applyLocal, notify]);

  // Ping screen (long-press / right-click): load per-collaborator status, and
  // send a single ping. Only the owner of a shared task can reach these.
  const quickPingStatus = useCallback(async (t) => {
    const { data } = await notesApi.get(`/notes/tasks/tasks/${t.id}/pings`);
    return data;
  }, []);
  const quickPing = useCallback(async (t, userId) => {
    const { data } = await notesApi.post(`/notes/tasks/tasks/${t.id}/ping`, { user_id: userId });
    notify('Ping sent.');
    return data;
  }, [notify]);

  // ---- Bulk selection + complete ----
  const toggleSelectTask = useCallback((t) => setSelectedTaskIds((prev) => {
    const nx = new Set(prev);
    if (nx.has(t.id)) nx.delete(t.id); else nx.add(t.id);
    return nx;
  }), []);
  const clearSelection = useCallback(() => setSelectedTaskIds(new Set()), []);
  const bulkCompleteSelected = useCallback(async () => {
    const ids = [...selectedTaskIds];
    if (!ids.length) return;
    // optimistic
    ids.forEach((id) => applyLocal({ id, is_completed: true, status: 'done' }));
    try {
      const { data } = await notesApi.post('/notes/tasks/tasks/bulk-complete', { ids, completed: true });
      const n = data?.updated || 0;
      const sp = data?.spawned || 0;
      notify(`Marked ${n} task${n === 1 ? '' : 's'} done.${sp ? ` ${sp} recurring copy${sp === 1 ? '' : 's'} created.` : ''}`);
      clearSelection(); loadTasks(); loadProjects(); loadCounts();
    } catch { notify('Could not complete tasks.'); loadTasks(); }
  }, [selectedTaskIds, applyLocal, notify, clearSelection, loadTasks, loadProjects, loadCounts]);

  // Completed view: bulk delete (owner-only, enforced by the backend) + select all.
  const ownedCompletedIds = useMemo(
    () => (view === 'completed' ? tasks.filter((t) => !t.shared_by).map((t) => t.id) : []),
    [view, tasks],
  );
  const allOwnedSelected = ownedCompletedIds.length > 0 && ownedCompletedIds.every((id) => selectedTaskIds.has(id));
  const someOwnedSelected = ownedCompletedIds.some((id) => selectedTaskIds.has(id));
  const toggleSelectAllCompleted = useCallback(() => {
    setSelectedTaskIds((prev) => {
      const nx = new Set(prev);
      const all = ownedCompletedIds.length > 0 && ownedCompletedIds.every((id) => nx.has(id));
      if (all) ownedCompletedIds.forEach((id) => nx.delete(id));
      else ownedCompletedIds.forEach((id) => nx.add(id));
      return nx;
    });
  }, [ownedCompletedIds]);
  // Completed view: per-project-group select — acts only on OWNED tasks in that group.
  const isGroupAllSelected = useCallback((items) => {
    const ids = (items || []).filter((t) => !t.shared_by).map((t) => t.id);
    return ids.length > 0 && ids.every((id) => selectedTaskIds.has(id));
  }, [selectedTaskIds]);
  const isGroupAnySelected = useCallback((items) => {
    const ids = (items || []).filter((t) => !t.shared_by).map((t) => t.id);
    return ids.some((id) => selectedTaskIds.has(id));
  }, [selectedTaskIds]);
  const toggleSelectGroup = useCallback((items) => {
    const ids = (items || []).filter((t) => !t.shared_by).map((t) => t.id);
    setSelectedTaskIds((prev) => {
      const nx = new Set(prev);
      const all = ids.length > 0 && ids.every((id) => nx.has(id));
      if (all) ids.forEach((id) => nx.delete(id));
      else ids.forEach((id) => nx.add(id));
      return nx;
    });
  }, []);
  const bulkDeleteSelected = useCallback(async () => {
    const ids = [...selectedTaskIds];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected task${ids.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
    try {
      const { data } = await notesApi.post('/notes/tasks/tasks/bulk-delete', { ids });
      const n = data?.deleted || 0;
      notify(`Deleted ${n} task${n === 1 ? '' : 's'}.`);
      clearSelection(); loadTasks(); loadProjects(); loadTags(); loadCounts();
    } catch { notify('Could not delete tasks.'); loadTasks(); }
  }, [selectedTaskIds, notify, clearSelection, loadTasks, loadProjects, loadTags, loadCounts]);

  // Completed view: reopen (mark not done) selected tasks.
  const bulkReopenSelected = useCallback(async () => {
    const ids = [...selectedTaskIds];
    if (!ids.length) return;
    try {
      const { data } = await notesApi.post('/notes/tasks/tasks/bulk-complete', { ids, completed: false });
      const n = data?.updated || 0;
      notify(`Reopened ${n} task${n === 1 ? '' : 's'}.`);
      clearSelection(); loadTasks(); loadProjects(); loadCounts();
    } catch { notify('Could not reopen tasks.'); loadTasks(); }
  }, [selectedTaskIds, notify, clearSelection, loadTasks, loadProjects, loadCounts]);

  // Selection is view-scoped: switching views (e.g. Completed → Today) clears it.
  useEffect(() => { clearSelection(); }, [view, clearSelection]);

  const deleteTask = useCallback(async (t) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await notesApi.delete(`/notes/tasks/tasks/${t.id}`);
      closeDetail();
      loadTasks(); loadTags(); loadProjects(); notify('Task deleted.');
    } catch { notify('Could not delete task.'); }
  }, [loadTasks, loadTags, loadProjects, notify, closeDetail]);

  const openShare = useCallback((resource) => setShareDialog({ open: true, resource }), []);

  // Available tag names for the inline editor's tag picker.
  const availableTagNames = useMemo(() => (tags || []).map((t) => (typeof t === 'string' ? t : t.tag)), [tags]);

  // Shared renderer for the expandable inline editor (list rows + kanban cards).
  const renderInlineEditor = useCallback((t) => (
    <TaskInlineEditor task={t} canEdit={!t.shared_by || t.permission === 'edit'}
      projects={projects} availableTags={availableTagNames}
      onPatch={(patch) => patchTask(t.id, patch)}
      onRenameProject={renameProject}
      onShare={() => openShare({ type: 'task', id: t.id, name: t.title })}
      onOpenDetail={() => openTask(t)}
      onSaveTemplate={() => setSaveTemplateTask(t)}
      onSubtaskSync={syncTaskFromSubtasks} />
  ), [projects, availableTagNames, patchTask, renameProject, openShare, openTask, syncTaskFromSubtasks]);

  const toggleTag = useCallback((tag) => setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag])), []);

  // ---- Filters & Labels: draft + saved-filter actions ----
  const resetFilterDraft = useCallback(() => {
    setFDueSet([]); setFDueStart(''); setFDueEnd(''); setFTags([]); setFTagMode('any'); setFProjects([]); setFStatuses([]); setFPriorities([]); setFShared('any');
  }, []);
  const openFiltersBlank = useCallback(() => {
    setActiveFilterId(null); resetFilterDraft(); setSavingFilter(false); setFilterName('');
    setView('filters'); setMobileNav(false);
  }, [resetFilterDraft]);
  const dueSetFromFilter = (f) => {
    if (Array.isArray(f.due_presets)) return f.due_presets.filter((x) => x && x !== 'any');
    return f.due_preset && f.due_preset !== 'any' ? [f.due_preset] : [];
  };
  const loadDraftFromFilter = useCallback((f) => {
    setFDueSet(dueSetFromFilter(f));
    setFDueStart(f.due_start ? toLocalDate(f.due_start) : '');
    setFDueEnd(f.due_end ? toLocalDate(f.due_end) : '');
    setFTags(f.tags || []);
    setFTagMode(f.tag_mode || 'any');
    setFProjects(f.project_ids || []);
    setFStatuses(f.statuses || []);
    setFPriorities(f.priorities || []);
    setFShared(f.shared_mode || 'any');
  }, []);
  const applySavedFilter = useCallback((f) => {
    setActiveFilterId(f.id); loadDraftFromFilter(f); setSavingFilter(false); setFilterName('');
    setView('filters'); setMobileNav(false);
  }, [loadDraftFromFilter]);
  const applyTagQuickFilter = useCallback((tag) => {
    setActiveFilterId(null); setFDueSet([]); setFDueStart(''); setFDueEnd('');
    setFTags([tag]); setFTagMode('any'); setFProjects([]); setFStatuses([]); setFPriorities([]); setFShared('any');
    setView('filters'); setMobileNav(false);
  }, []);
  // Overdue nudge → jump to a cross-project "overdue" filter so nothing slips through.
  const showOverdue = useCallback(() => {
    setActiveFilterId(null); setFDueSet(['overdue']); setFDueStart(''); setFDueEnd('');
    setFTags([]); setFTagMode('any'); setFProjects([]); setFStatuses([]); setFPriorities([]); setFShared('any');
    setCollapsedBuckets((s) => { const n = new Set(s); n.delete('overdue'); return n; });
    setView('filters'); setMobileNav(false);
  }, []);
  const toggleFTag = useCallback((tag) => setFTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag])), []);
  const toggleFProject = useCallback((pid) => setFProjects((prev) => (prev.includes(pid) ? prev.filter((p) => p !== pid) : [...prev, pid])), []);
  const toggleFDue = useCallback((v) => setFDueSet((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v])), []);
  const toggleFStatus = useCallback((v) => setFStatuses((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v])), []);
  const toggleFPriority = useCallback((v) => setFPriorities((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v])), []);

  // Apply a decoded natural-language filter (from the plain-English box / mic).
  const applyNlFilter = useCallback((p) => {
    setActiveFilterId(null);
    setFDueSet(p.dueScopes || []);
    setFDueStart(p.dueStart || '');
    setFDueEnd(p.dueEnd || '');
    setFTags(p.tags || []);
    setFTagMode('any');
    setFProjects(p.projectIds || []);
    setFStatuses(p.statuses || []);
    setFPriorities(p.priorities || []);
    setFShared('any');
    setView('filters');
    notify(p.matched && p.matched.length ? `Filter applied · ${p.matched.join(' · ')}` : 'Nothing recognised — mention a due date, tag or project.');
  }, [notify]);

  const filterDefBody = useCallback(() => ({
    due_presets: fDueSet,
    due_preset: fDueSet.length === 1 ? fDueSet[0] : 'any',
    due_start: fDueSet.includes('custom') && fDueStart ? startOfDay(new Date(`${fDueStart}T00:00:00`)).toISOString() : null,
    due_end: fDueSet.includes('custom') && fDueEnd ? endOfDay(new Date(`${fDueEnd}T00:00:00`)).toISOString() : null,
    tags: fTags,
    tag_mode: fTagMode,
    project_ids: fProjects,
    statuses: fStatuses,
    priorities: fPriorities,
    shared_mode: fShared,
  }), [fDueSet, fDueStart, fDueEnd, fTags, fTagMode, fProjects, fStatuses, fPriorities, fShared]);

  const saveNewFilter = useCallback(async () => {
    const name = filterName.trim();
    if (!name) { notify('Please name this filter.'); return; }
    try {
      const { data } = await notesApi.post('/notes/tasks/filters', { name, ...filterDefBody() });
      setSavingFilter(false); setFilterName(''); setActiveFilterId(data.id);
      await loadFilters(); notify('Filter saved.');
    } catch (e) { notify(e?.response?.data?.detail || 'Could not save filter.'); }
  }, [filterName, filterDefBody, loadFilters, notify]);

  // ---- Inline filters (Today / Project page / All Tasks) -------------------
  // The same filter state powers three surfaces, each exposing only the fields
  // that make sense there:
  //   • Today      → Project + Shared            (no Due — it's already "today")
  //   • Project    → Due + Shared                (no Project — you're inside one)
  //   • All Tasks  → Project + Due + Shared       (everything)
  // Shared applies everywhere. `view` is the project id on a Project page.
  const _projFilterView = view !== 'today' && view !== 'upcoming' && view !== 'filters' && view !== 'completed' && view !== 'groups' && view !== 'all';
  const inlineFilterView = view === 'today' || view === 'all' || _projFilterView;
  const showFxProject = view === 'today' || view === 'all';
  const showFxDue = view === 'all' || _projFilterView;
  const showFxShared = inlineFilterView;

  const clearInlineFilters = useCallback(() => {
    setTProject('all'); setTDueMode('any'); setTDueOn(''); setTDueFrom(''); setTDueTo(''); setTShared('any');
    setSelectedTags([]);
    setInlineFilterId(null); setInlineFilterKey(null);
  }, []);

  // A stable fingerprint of the CURRENT inline filter (fields + tags). Used to
  // tell whether the user has diverged from an applied saved filter so the
  // "Save filter" button can disable while unchanged and re-enable on edit.
  const inlineStateKey = useMemo(() => JSON.stringify({
    p: tProject, dm: tDueMode, don: tDueOn, df: tDueFrom, dt: tDueTo,
    s: tShared, tags: [...selectedTags].sort(),
  }), [tProject, tDueMode, tDueOn, tDueFrom, tDueTo, tShared, selectedTags]);
  // True while an applied saved filter is untouched → the filter is "already saved".
  const inlineUnchangedFromSaved = inlineFilterId != null && inlineFilterKey === inlineStateKey;

  // Apply a saved filter onto the All Tasks inline bar: clear everything, then
  // map the saved definition onto the inline fields (project / due / shared /
  // tags). Records a snapshot so the Save button knows nothing has changed yet.
  const applyInlineSavedFilter = useCallback((f) => {
    if (!f) return;
    const tags = [...(f.tags || [])].sort();
    const pid = (f.project_ids && f.project_ids.length) ? f.project_ids[0] : 'all';
    const presets = Array.isArray(f.due_presets)
      ? f.due_presets
      : (f.due_preset && f.due_preset !== 'any' ? [f.due_preset] : []);
    let dm = 'any', don = '', df = '', dt = '';
    if (presets.includes('none')) {
      dm = 'no_due';
    } else if (presets.includes('this_month')) {
      dm = 'this_month';
    } else if (presets.includes('next_month')) {
      dm = 'next_month';
    } else if (presets.includes('custom') && (f.due_start || f.due_end)) {
      const s = f.due_start ? toLocalDate(f.due_start) : '';
      const e = f.due_end ? toLocalDate(f.due_end) : '';
      if (s && e && s === e) { dm = 'on'; don = s; }
      else { dm = 'range'; df = s; dt = e; }
    }
    const shared = f.shared_mode || 'any';
    // Apply to state
    setSelectedTags(f.tags || []);
    setTProject(pid);
    setTDueMode(dm); setTDueOn(don); setTDueFrom(df); setTDueTo(dt);
    setTShared(shared);
    // Snapshot (must mirror inlineStateKey's shape/order exactly).
    const key = JSON.stringify({ p: pid, dm, don, df, dt, s: shared, tags });
    setInlineFilterKey(key);
    setInlineFilterId(f.id);
    setTSaving(false); setTSaveName(''); setTSaveMenuOpen(false);
  }, []);

  // ---- Command palette (⌘K) actions ---------------------------------------
  // Opens from any view. Selecting a Project or Tag REPLACES every active
  // filter and lands on the All Tasks grid; a Saved Filter applies in full on
  // the Filters view; a plain view just navigates. Each action closes the
  // palette + the mobile nav.
  const paletteGoView = useCallback((v) => {
    clearInlineFilters(); setSelectedTags([]); setActiveFilterId(null);
    setView(v); setMobileNav(false); setCmdOpen(false);
  }, [clearInlineFilters]);
  const paletteFilterProject = useCallback((pid) => {
    clearInlineFilters(); setSelectedTags([]); setActiveFilterId(null);
    setTProject(pid);
    setView('all'); setMobileNav(false); setCmdOpen(false);
  }, [clearInlineFilters]);
  const paletteFilterTag = useCallback((tag) => {
    clearInlineFilters(); setActiveFilterId(null);
    setSelectedTags([tag]);
    setView('all'); setMobileNav(false); setCmdOpen(false);
  }, [clearInlineFilters]);
  const paletteApplySavedFilter = useCallback((f) => {
    clearInlineFilters(); setSelectedTags([]);
    applySavedFilter(f); setCmdOpen(false);
  }, [clearInlineFilters, applySavedFilter]);

  // Whether any field RELEVANT to the current view is engaged.
  const inlineFiltersActive =
    (showFxProject && tProject !== 'all') ||
    (showFxDue && tDueMode !== 'any') ||
    (showFxShared && tShared !== 'any') ||
    selectedTags.length > 0;

  // Removable summary chips (only for fields visible on the current view).
  const prettyDay = (ymd) => { try { return new Date(`${ymd}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); } catch { return ymd; } };
  const inlineChips = [];
  if (showFxProject && tProject !== 'all') {
    inlineChips.push({ key: 'project', label: `Project · ${projectById[tProject]?.name || 'Shared'}`, dot: projectById[tProject]?.color || 'var(--n-accent)', onRemove: () => setTProject('all') });
  }
  if (showFxDue && tDueMode !== 'any') {
    let dueLabel = 'Due date';
    if (tDueMode === 'no_due') dueLabel = 'No due date';
    else if (tDueMode === 'on') dueLabel = tDueOn ? `Due ${prettyDay(tDueOn)}` : 'Due on a date';
    else if (tDueMode === 'range') dueLabel = `Due ${tDueFrom ? prettyDay(tDueFrom) : '…'} – ${tDueTo ? prettyDay(tDueTo) : '…'}`;
    else if (tDueMode === 'this_month') dueLabel = 'Due this month';
    else if (tDueMode === 'next_month') dueLabel = 'Due next month';
    inlineChips.push({ key: 'due', label: dueLabel, onRemove: () => { setTDueMode('any'); setTDueOn(''); setTDueFrom(''); setTDueTo(''); } });
  }
  if (showFxShared && tShared !== 'any') {
    inlineChips.push({ key: 'shared', label: (SHARED_MODE_OPTIONS.find((o) => o.value === tShared) || {}).label || 'Shared', onRemove: () => setTShared('any') });
  }
  // Tag filters live in the same unified chip row as the other filters.
  selectedTags.forEach((t) => inlineChips.push({ key: `tag-${t}`, icon: <Hash size={11} />, label: t, onRemove: () => toggleTag(t) }));

  const inlineFilterBody = useCallback(() => {
    // Map the inline filter (for the CURRENT view) onto the saved-filter schema.
    const body = { shared_mode: showFxShared ? tShared : 'any' };
    // Project scope: selector on Today/All Tasks; the current project on a Project page.
    if (showFxProject) body.project_ids = tProject === 'all' ? [] : [tProject];
    else if (_projFilterView) body.project_ids = [view];
    else body.project_ids = [];
    // Due scope: only Project + All Tasks expose it.
    if (showFxDue && tDueMode === 'no_due') {
      body.due_presets = ['none'];
    } else if (showFxDue && tDueMode === 'on' && tDueOn) {
      body.due_presets = ['custom'];
      body.due_start = startOfDay(new Date(`${tDueOn}T00:00:00`)).toISOString();
      body.due_end = endOfDay(new Date(`${tDueOn}T00:00:00`)).toISOString();
    } else if (showFxDue && tDueMode === 'range' && (tDueFrom || tDueTo)) {
      body.due_presets = ['custom'];
      if (tDueFrom) body.due_start = startOfDay(new Date(`${tDueFrom}T00:00:00`)).toISOString();
      if (tDueTo) body.due_end = endOfDay(new Date(`${tDueTo}T00:00:00`)).toISOString();
    } else if (showFxDue && (tDueMode === 'this_month' || tDueMode === 'next_month')) {
      // Dynamic calendar-month scopes — resolved relative to "now" at query time.
      body.due_presets = [tDueMode];
    } else {
      body.due_presets = [];
    }
    return body;
  }, [showFxShared, showFxProject, showFxDue, _projFilterView, view, tShared, tProject, tDueMode, tDueOn, tDueFrom, tDueTo]);

  const saveInlineFilter = useCallback(async () => {
    const name = tSaveName.trim();
    if (!name) { notify('Please name this filter.'); return; }
    try {
      await notesApi.post('/notes/tasks/filters', { name, ...inlineFilterBody() });
      setTSaving(false); setTSaveName('');
      // Reveal the Saved Filters section so the new entry is immediately visible.
      setSecOpen((s) => { const nx = { ...s, filters: true }; try { localStorage.setItem('tasks:secOpen', JSON.stringify(nx)); } catch { /* noop */ } return nx; });
      await loadFilters(); notify('Filter saved to Saved Filters.');
    } catch (e) { notify(e?.response?.data?.detail || 'Could not save filter.'); }
  }, [tSaveName, inlineFilterBody, loadFilters, notify]);

  // Overwrite an existing saved filter's definition with the current inline
  // criteria (name/tags/etc. are preserved — PATCH only sets provided fields).
  const updateInlineFilter = useCallback(async (fid, fname) => {
    try {
      await notesApi.patch(`/notes/tasks/filters/${fid}`, inlineFilterBody());
      setTSaveMenuOpen(false); setTSaving(false); setTSaveName('');
      setSecOpen((s) => { const nx = { ...s, filters: true }; try { localStorage.setItem('tasks:secOpen', JSON.stringify(nx)); } catch { /* noop */ } return nx; });
      await loadFilters(); notify(`Updated “${fname || 'filter'}”.`);
    } catch (e) { notify(e?.response?.data?.detail || 'Could not update filter.'); }
  }, [inlineFilterBody, loadFilters, notify]);


  const updateActiveFilter = useCallback(async () => {
    if (!activeFilterId) return;
    try {
      await notesApi.patch(`/notes/tasks/filters/${activeFilterId}`, filterDefBody());
      await loadFilters(); notify('Filter updated.');
    } catch (e) { notify(e?.response?.data?.detail || 'Could not update filter.'); }
  }, [activeFilterId, filterDefBody, loadFilters, notify]);

  const renameFilter = useCallback(async (fid) => {
    const name = renameVal.trim();
    if (!name) { setRenamingFilterId(null); return; }
    try { await notesApi.patch(`/notes/tasks/filters/${fid}`, { name }); await loadFilters(); }
    catch { notify('Could not rename filter.'); }
    setRenamingFilterId(null); setRenameVal('');
  }, [renameVal, loadFilters, notify]);

  const deleteSavedFilter = useCallback(async (fid) => {
    try {
      await notesApi.delete(`/notes/tasks/filters/${fid}`);
      if (activeFilterId === fid) { setActiveFilterId(null); resetFilterDraft(); }
      await loadFilters(); notify('Filter deleted.');
    } catch { notify('Could not delete filter.'); }
  }, [activeFilterId, resetFilterDraft, loadFilters, notify]);

  const handleFilterDrop = useCallback((targetId) => {
    const src = dragFilterRef.current;
    dragFilterRef.current = null;
    if (!src || src === targetId) return;
    setSavedFilters((prev) => {
      const arr = [...prev];
      const from = arr.findIndex((f) => f.id === src);
      const to = arr.findIndex((f) => f.id === targetId);
      if (from < 0 || to < 0) return prev;
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      notesApi.post('/notes/tasks/filters/reorder', { ids: arr.map((f) => f.id) }).catch(() => loadFilters());
      return arr;
    });
  }, [loadFilters]);

  // Delete a reminder from the bell panel (owned tasks only).
  const deleteReminder = useCallback(async (taskId) => {
    try {
      await notesApi.patch(`/notes/tasks/tasks/${taskId}`, { reminder_at: null, reminder_rule: 'none' });
      applyLocal({ id: taskId, reminder_at: null, reminder_rule: 'none' });
      reminders.refreshUpcoming(); notify('Reminder deleted.');
    } catch { notify('Could not delete reminder.'); }
  }, [applyLocal, reminders, notify]);

  // Snooze a reminder straight from the bell panel.
  const snoozeFromBell = useCallback(async (n, kind) => {
    if (kind === '10m') await reminders.snooze(n, 10, notify);
    else if (kind === '1h') await reminders.snooze(n, 60, notify);
    else if (kind === 'tomorrow') await reminders.snoozeUntilTomorrow(n, notify);
    reminders.refreshUpcoming();
  }, [reminders, notify]);


  const availableFilterTags = useMemo(() => {
    const counts = {};
    tasks.forEach((t) => (t.tags || []).forEach((tg) => { if (!selectedTags.includes(tg)) counts[tg] = (counts[tg] || 0) + 1; }));
    return Object.entries(counts).map(([tag, count]) => ({ tag, count })).sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
  }, [tasks, selectedTags]);

  // Reusable "Filter by tag" combo. `compact` sizes it to sit in line with the
  // inline/completed filter-bar controls; the default size matches the search row.
  const tagFilterCombo = (compact = false) => {
    const btnCls = compact
      ? 'n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-1.5 text-[13px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] transition-colors'
      : 'n-focus inline-flex items-center gap-2 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2.5 text-sm text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]';
    return (
      <div className="relative" ref={tagFilterRef}>
        <button data-testid="tasks-tag-filter-button" onClick={() => setTagFilterOpen((v) => { const nx = !v; if (nx) setTagFilterQuery(''); return nx; })}
          className={btnCls}>
          <Filter size={compact ? 13 : 15} /> Filter by tag{selectedTags.length > 0 ? ` · ${selectedTags.length}` : ''}
        </button>
        {tagFilterOpen && (() => {
          const tq = tagFilterQuery.trim().toLowerCase();
          const shownTags = tq ? availableFilterTags.filter(({ tag }) => tag.toLowerCase().includes(tq)) : availableFilterTags;
          return (
            <div className="absolute right-0 z-40 mt-1 w-64 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]">
              <div className="relative p-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--n-muted-2)]" />
                <input autoFocus value={tagFilterQuery} onChange={(e) => setTagFilterQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape') { setTagFilterQuery(''); setTagFilterOpen(false); } }}
                  placeholder="Search tags…" data-testid="tasks-tag-filter-search"
                  className="n-focus w-full rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas-2)] pl-8 pr-2 py-1.5 text-[13px] text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]" />
              </div>
              <div className="mt-1 max-h-64 overflow-y-auto n-scroll">
                {shownTags.length === 0 ? (
                  <p className="px-2.5 py-3 text-center text-[12px] text-[var(--n-muted-2)]">{availableFilterTags.length === 0 ? 'No more tags to narrow by.' : 'No tags match your search.'}</p>
                ) : shownTags.map(({ tag, count }) => (
                  <button key={tag} onClick={() => { toggleTag(tag); setTagFilterOpen(false); }} data-testid="tasks-filter-tag-option"
                    className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
                    <Hash size={12} className="text-[var(--n-muted-2)]" /><span className="flex-1 truncate">{tag}</span><span className="text-[11px] text-[var(--n-muted-2)]">{count}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  // The task set actually rendered. Today / Project / All Tasks apply the inline
  // filter bar (respecting which fields are visible on that view); the Filters
  // view honours a saved filter's sharing scope (server already narrows the
  // rest). Every other view is untouched.
  const visibleTasks = useMemo(() => {
    if (inlineFilterView) {
      const isAll = view === 'all';
      // Calendar-month bounds (local), recomputed each render so they stay
      // dynamic: "This month" always means the current month, etc.
      const _now = new Date();
      const monthLo = (off) => new Date(_now.getFullYear(), _now.getMonth() + off, 1).getTime();
      const monthHi = (off) => new Date(_now.getFullYear(), _now.getMonth() + off + 1, 1).getTime(); // exclusive
      return tasks.filter((t) => {
        if (showFxProject && tProject !== 'all' && t.project_id !== tProject) return false;
        if (showFxDue && tDueMode !== 'any') {
          // On All Tasks the month/date scopes constrain only DATED, active
          // tasks; no-due & completed tasks pass through to their own panel /
          // group so they can be viewed alongside a month selection.
          const bypass = isAll && (t.is_completed || !t.due_at);
          if (!bypass) {
            if (tDueMode === 'no_due') {
              if (t.due_at) return false;
            } else if (tDueMode === 'on') {
              if (tDueOn && toLocalDate(t.due_at) !== tDueOn) return false;
            } else if (tDueMode === 'range') {
              const d = toLocalDate(t.due_at);
              if (!d) return false;
              if (tDueFrom && d < tDueFrom) return false;
              if (tDueTo && d > tDueTo) return false;
            } else if (tDueMode === 'this_month' || tDueMode === 'next_month') {
              if (!t.due_at) return false;
              const off = tDueMode === 'next_month' ? 1 : 0;
              const ts = new Date(t.due_at).getTime();
              if (Number.isNaN(ts) || ts < monthLo(off) || ts >= monthHi(off)) return false;
            }
          }
        }
        if (showFxShared && !matchSharedMode(t, tShared)) return false;
        return true;
      });
    }
    if (view === 'filters' && fShared !== 'any') {
      return tasks.filter((t) => matchSharedMode(t, fShared));
    }
    return tasks;
  }, [tasks, view, inlineFilterView, showFxProject, showFxDue, showFxShared, tProject, tDueMode, tDueOn, tDueFrom, tDueTo, tShared, fShared]);

  const grouped = useMemo(() => {
    const map = {};
    visibleTasks.forEach((t) => { (map[t.project_id] = map[t.project_id] || []).push(t); });
    return projects.filter((p) => map[p.id]?.length).map((p) => ({ project: p, items: map[p.id] }))
      .concat(Object.keys(map).filter((pid) => !projectById[pid]).map((pid) => ({ project: { id: pid, name: 'Shared', color: '#B49BE0' }, items: map[pid] })));
  }, [visibleTasks, projects, projectById]);

  // Group an arbitrary task list by project (owned projects first, then shared).
  const groupItemsByProject = useCallback((items) => {
    const map = {};
    items.forEach((t) => { (map[t.project_id] = map[t.project_id] || []).push(t); });
    return projects.filter((p) => map[p.id]?.length).map((p) => ({ project: p, items: map[p.id] }))
      .concat(Object.keys(map).filter((pid) => !projectById[pid]).map((pid) => ({ project: { id: pid, name: 'Shared', color: '#B49BE0' }, items: map[pid] })));
  }, [projects, projectById]);

  // Bucket tasks by due date (Overdue → No Due Date). First matching bucket wins,
  // so buckets stay mutually exclusive and chronological even across month edges.
  const dateGrouped = useMemo(() => {
    const now = new Date();
    const today0 = startOfDay(now).getTime();
    const DAY = 86400000;
    const tomorrow0 = today0 + DAY;
    const dow = new Date(today0).getDay();          // 0 Sun … 6 Sat
    const wIdx = (dow + 6) % 7;                      // Mon=0 … Sun=6
    const endThisWeek = startOfDay(addDays(new Date(today0), 6 - wIdx)).getTime();
    const endNextWeek = endThisWeek + 7 * DAY;
    const d0 = new Date(today0);
    const endThisMonth = startOfDay(new Date(d0.getFullYear(), d0.getMonth() + 1, 0)).getTime();
    const endNextMonth = startOfDay(new Date(d0.getFullYear(), d0.getMonth() + 2, 0)).getTime();
    const bucketOf = (t) => {
      // Completed tasks always fall into their own trailing group — never a
      // date bucket — so a done task can never appear under "Overdue" etc.
      if (t.is_completed) return 'completed';
      if (!t.due_at) return 'no_due';
      const d = startOfDay(new Date(t.due_at)).getTime();
      if (Number.isNaN(d)) return 'no_due';
      if (d < today0) return 'overdue';
      if (d === today0) return 'today';
      if (d === tomorrow0) return 'tomorrow';
      if (d <= endThisWeek) return 'this_week';
      if (d <= endNextWeek) return 'next_week';
      if (d <= endThisMonth) return 'this_month';
      if (d <= endNextMonth) return 'next_month';
      return 'later';
    };
    const map = {};
    visibleTasks.forEach((t) => { const b = bucketOf(t); (map[b] = map[b] || []).push(t); });
    Object.values(map).forEach((arr) => arr.sort((a, b) => {
      const da = a.due_at ? new Date(a.due_at).getTime() : Infinity;
      const db = b.due_at ? new Date(b.due_at).getTime() : Infinity;
      return da - db || (a.title || '').localeCompare(b.title || '');
    }));
    return DATE_BUCKETS.filter((bk) => map[bk.key]?.length).map((bk) => ({ ...bk, items: map[bk.key] }));
  }, [visibleTasks]);

  const weekDays = useMemo(() => {
    const now = new Date();
    const byTime = (a, b) => new Date(a.due_at).getTime() - new Date(b.due_at).getTime();
    return Array.from({ length: upcomingRange }, (_, i) => {
      const day = addDays(now, i);
      const s = startOfDay(day).getTime(); const e = endOfDay(day).getTime();
      const inDay = tasks.filter((t) => t.due_at && new Date(t.due_at).getTime() >= s && new Date(t.due_at).getTime() <= e);
      // Uncompleted on top; completed grouped at the bottom (each time-sorted).
      const open = inDay.filter((t) => !t.is_completed).sort(byTime);
      const done = inDay.filter((t) => t.is_completed).sort(byTime);
      return { day, open, done, isToday: i === 0 };
    });
  }, [tasks, upcomingRange]);

  // Overdue tasks (due before the start of today) for the Upcoming board's rail.
  const overdueUpcoming = useMemo(() => {
    const s = startOfDay(new Date()).getTime();
    return tasks
      .filter((t) => t.due_at && !t.is_completed && new Date(t.due_at).getTime() < s)
      .sort((a, b) => new Date(a.due_at).getTime() - new Date(b.due_at).getTime());
  }, [tasks]);

  // All Tasks grid — date-sorted (soonest first; no-due last).
  const allTasksSorted = useMemo(() => {
    return [...visibleTasks].sort((a, b) => {
      const da = a.due_at ? new Date(a.due_at).getTime() : Infinity;
      const db = b.due_at ? new Date(b.due_at).getTime() : Infinity;
      return da - db || (a.title || '').localeCompare(b.title || '');
    });
  }, [visibleTasks]);

  // ---- Project Calendar view (month grid, Sunday-start) --------------------
  // Buckets the project's tasks by local due-date into a 5-/6-week grid for the
  // displayed month, plus a "No date" list. Also counts uncompleted tasks in the
  // PREVIOUS month and tasks due in the NEXT month for the arrow badges. Search /
  // tag filtering is already applied server-side, so `tasks` is pre-filtered.
  const calData = useMemo(() => {
    const first = new Date(calMonth.getFullYear(), calMonth.getMonth(), 1);
    const monthIdx = first.getMonth();
    const year = first.getFullYear();
    const todayKey = toLocalDate(new Date().toISOString());
    const prevMonth = new Date(year, monthIdx - 1, 1);
    const nextMonth = new Date(year, monthIdx + 1, 1);
    const byDay = {};
    const noDate = [];
    let prevUncompleted = 0;
    let nextDue = 0;
    visibleTasks.forEach((t) => {
      const d = t.due_at ? new Date(t.due_at) : null;
      if (!d || isNaN(d.getTime())) { noDate.push(t); return; }
      const key = toLocalDate(t.due_at);
      if (!byDay[key]) byDay[key] = [];
      byDay[key].push(t);
      if (d.getFullYear() === prevMonth.getFullYear() && d.getMonth() === prevMonth.getMonth() && !t.is_completed) prevUncompleted += 1;
      if (d.getFullYear() === nextMonth.getFullYear() && d.getMonth() === nextMonth.getMonth()) nextDue += 1;
    });
    // Sort each day's cards by time-of-day, then title.
    Object.keys(byDay).forEach((k) => byDay[k].sort((a, b) => {
      const ta = a.due_at ? new Date(a.due_at).getTime() : 0;
      const tb = b.due_at ? new Date(b.due_at).getTime() : 0;
      return ta - tb || (a.title || '').localeCompare(b.title || '');
    }));
    // Build the grid: pad from the Sunday on/before the 1st, fill full weeks.
    const startWeekday = first.getDay(); // 0 = Sunday
    const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
    const weeks = Math.ceil((startWeekday + daysInMonth) / 7);
    const gridStart = new Date(year, monthIdx, 1 - startWeekday);
    const cells = Array.from({ length: weeks * 7 }, (_, i) => {
      const date = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
      const key = toLocalDate(date.toISOString());
      return { date, key, inMonth: date.getMonth() === monthIdx, isToday: key === todayKey, items: byDay[key] || [] };
    });
    noDate.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    return { cells, noDate, prevUncompleted, nextDue, label: first.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) };
  }, [visibleTasks, calMonth]);

  const viewTitle = view === 'today' ? 'Today'
 : view === 'upcoming' ? 'Upcoming' : view === 'all' ? 'All Tasks' : view === 'filters' ? 'Filters & Labels' : view === 'completed' ? 'Completed' : view === 'groups' ? 'Shared with my groups' : (projectById[view]?.name || 'Project');
  const now = new Date();
  const viewSub = view === 'today' ? fmtDateLong(now)
    : view === 'upcoming' ? `Next ${upcomingRange} days${overdueUpcoming.length ? ` · ${overdueUpcoming.length} overdue` : ''}`
    : view === 'all' ? `${visibleTasks.length} task${visibleTasks.length === 1 ? '' : 's'} you own or that's shared with you`
    : view === 'filters' ? 'Search and filter across every project'
    : view === 'completed' ? `${tasks.length} completed ${tasks.length === 1 ? 'task' : 'tasks'} across all projects`
    : view === 'groups' ? `${tasks.length} task${tasks.length === 1 ? '' : 's'} shared with your groups`
    : `${(grouped[0]?.items?.length) || tasks.length} tasks`;

  const favProjects = projects.filter((p) => p.is_favorite);
  const otherProjects = projects.filter((p) => !p.is_favorite);

  const overdueCount = (reminders.upcomingReminders || []).filter((n) => !n.is_completed && n.reminder_at && new Date(n.reminder_at).getTime() <= Date.now()).length;
  const dueQueue = reminders.dueQueue || [];
  const isProjectView = view !== 'today' && view !== 'upcoming' && view !== 'filters' && view !== 'completed' && view !== 'groups' && view !== 'all';
  const isTodayBoard = view === 'today' && todayViewMode === 'board';
  const isTodayGrid = view === 'today' && todayViewMode === 'grid';
  const boardActive = (isProjectView && viewMode === 'board') || isTodayBoard;
  // Project List + Filters List group by due date. Filters also sub-groups by project.
  // Project GRID also uses date buckets (rendered as cards). Completed GRID uses the
  // project-grouped path (collapsible headers). Filters GRID uses date buckets as cards.
  const useDateGroups = view === 'filters' || (isProjectView && (viewMode === 'list' || viewMode === 'grid'));
  const dateSubgroupByProject = view === 'filters';
  const agendaGrid = (view === 'completed' && completedViewMode === 'grid')
    || (isProjectView && viewMode === 'grid')
    || (view === 'filters' && filtersViewMode === 'grid');
  const setTodayView = useCallback((mode) => {
    setTodayViewMode(mode);
    try { localStorage.setItem('tasks:todayViewMode', mode); } catch { /* noop */ }
  }, []);

  const setProjectView = useCallback((mode) => {
    setViewMode(mode);
    try { localStorage.setItem('tasks:projectViewMode', mode); } catch { /* noop */ }
  }, []);

  const setCompletedView = useCallback((mode) => {
    setCompletedViewMode(mode);
    try { localStorage.setItem('tasks:completedViewMode', mode); } catch { /* noop */ }
  }, []);

  const setAllView = useCallback((mode) => {
    setAllViewMode(mode);
    try { localStorage.setItem('tasks:allViewMode', mode); } catch { /* noop */ }
  }, []);

  const setFiltersView = useCallback((mode) => {
    setFiltersViewMode(mode);
    try { localStorage.setItem('tasks:filtersViewMode', mode); } catch { /* noop */ }
  }, []);

  // Create a task from a saved template — resolves the project the same way
  // Smart Add does, then asks the server to materialise the template + subtasks.
  // If the template carries its own project, the server drops the task there
  // (and auto-shares it with that project's members), so we don't require a
  // local fallback project in that case.
  const applyTemplate = useCallback(async (templateId) => {
    const tmpl = taskTemplates.find((t) => t.id === templateId);
    const pid = (isProjectView ? view : null) || resolveDefaultPid();
    if (!pid && !tmpl?.project_id) { setSmartAddOpen(false); setNewProjectOpen(true); notify('Create a project first.'); return false; }
    try {
      const { data } = await notesApi.post(`/notes/tasks/templates/${templateId}/apply`, { project_id: pid || null });
      await loadTasks(); loadTags(); loadProjects(); loadCounts();
      setSmartAddOpen(false);
      const subN = data.subtask_total || 0;
      notify(`Task created from template${subN ? ` with ${subN} sub-task${subN > 1 ? 's' : ''}` : ''}.`);
      openTask(data);
      return true;
    } catch (e) { notify(e?.response?.data?.detail || 'Could not apply template.'); return false; }
  }, [taskTemplates, resolveDefaultPid, isProjectView, view, loadTasks, loadTags, loadProjects, loadCounts, openTask, notify]);

  // Create a task from a decoded natural-language description: resolves the
  // project, sets due/tags/priority/recurrence, then spawns any sub-tasks.
  const createTaskFromNl = useCallback(async (parsed) => {
    const pid = parsed.projectId
      || (isProjectView ? view : null)
      || resolveDefaultPid();
    if (!pid) { setSmartAddOpen(false); setNewProjectOpen(true); notify('Create a project first.'); return false; }
    try {
      const { data } = await notesApi.post('/notes/tasks/tasks', {
        project_id: pid,
        title: parsed.title || 'Untitled task',
        due_at: parsed.dueAt || null,
        tags: parsed.tags || [],
        priority: parsed.priority || 'normal',
        recurrence: parsed.recurrence || 'none',
        recurrence_weekdays: parsed.recurrenceWeekdays || [],
        reminder_at: parsed.reminderAt || null,
        reminder_rule: parsed.reminderRule || 'none',
      });
      let madeSubs = 0;
      for (const st of (parsed.subtasks || [])) {
        try { await notesApi.post(`/notes/tasks/tasks/${data.id}/subtasks`, { title: st }); madeSubs += 1; } catch { /* skip one */ }
      }
      let sharedN = 0;
      for (const uid of (parsed.shareUserIds || [])) {
        try { await notesApi.post('/notes/tasks/shares', { resource_type: 'task', resource_id: data.id, target_user_id: uid, permission: 'edit' }); sharedN += 1; } catch { /* skip */ }
      }
      let sharedG = 0;
      for (const gid of (parsed.shareGroupIds || [])) {
        try { await notesApi.post('/notes/tasks/group-shares', { task_id: data.id, group_id: gid, permission: 'edit' }); sharedG += 1; } catch { /* skip */ }
      }
      let linkedN = 0;
      if (parsed.scheduleLinks && parsed.scheduleLinks.length) {
        try { const { data: lr } = await notesApi.post(`/planner/tasks/${data.id}/schedule-links`, { targets: parsed.scheduleLinks }); linkedN = lr?.added || 0; } catch { /* skip */ }
      }
      await loadTasks(); loadTags(); loadProjects(); loadCounts();
      setSmartAddOpen(false);
      const shareBits = [...(parsed.shareNames || []), ...(parsed.shareGroupNames || [])];
      const shareTotal = sharedN + sharedG;
      notify(`Task created${madeSubs ? ` with ${madeSubs} sub-task${madeSubs > 1 ? 's' : ''}` : ''}${shareTotal ? ` · shared with ${shareBits.join(', ') || `${shareTotal} ${shareTotal > 1 ? 'recipients' : 'recipient'}`}` : ''}${linkedN ? ` · linked to ${linkedN} item${linkedN > 1 ? 's' : ''}` : ''}.`);
      openTask(data);
      return true;
    } catch (e) { notify(e?.response?.data?.detail || 'Could not create task.'); return false; }
  }, [resolveDefaultPid, isProjectView, view, loadTasks, loadTags, loadProjects, loadCounts, openTask, notify]);

  // Bulk create: each plain-English line becomes its own task (with sub-tasks).
  const createTasksFromNlBulk = useCallback(async (lines) => {
    const cleaned = (lines || []).map((s) => String(s).trim()).filter(Boolean);
    if (!cleaned.length) return false;
    const fallback = (isProjectView ? view : null) || resolveDefaultPid();
    if (!fallback && !cleaned.some((l) => parseTaskText(l, { tags: availableTagNames, projects, users: directory, groups: taskGroups }).projectId)) {
      setSmartAddOpen(false); setNewProjectOpen(true); notify('Create a project first.'); return false;
    }
    let made = 0; let subs = 0;
    for (const line of cleaned) {
      const parsed = parseTaskText(line, { tags: availableTagNames, projects, users: directory, groups: taskGroups });
      const pid = parsed.projectId || fallback;
      if (!pid) continue;
      try {
        const { data } = await notesApi.post('/notes/tasks/tasks', {
          project_id: pid,
          title: parsed.title || 'Untitled task',
          due_at: parsed.dueAt || null,
          tags: parsed.tags || [],
          priority: parsed.priority || 'normal',
          recurrence: parsed.recurrence || 'none',
          recurrence_weekdays: parsed.recurrenceWeekdays || [],
          reminder_at: parsed.reminderAt || null,
          reminder_rule: parsed.reminderRule || 'none',
        });
        for (const st of (parsed.subtasks || [])) {
          try { await notesApi.post(`/notes/tasks/tasks/${data.id}/subtasks`, { title: st }); subs += 1; } catch { /* skip */ }
        }
        for (const uid of (parsed.shareUserIds || [])) {
          try { await notesApi.post('/notes/tasks/shares', { resource_type: 'task', resource_id: data.id, target_user_id: uid, permission: 'edit' }); } catch { /* skip */ }
        }
        for (const gid of (parsed.shareGroupIds || [])) {
          try { await notesApi.post('/notes/tasks/group-shares', { task_id: data.id, group_id: gid, permission: 'edit' }); } catch { /* skip */ }
        }
        made += 1;
      } catch { /* skip this line */ }
    }
    await loadTasks(); loadTags(); loadProjects(); loadCounts();
    setSmartAddOpen(false);
    notify(made ? `Created ${made} task${made !== 1 ? 's' : ''}${subs ? ` · ${subs} sub-task${subs !== 1 ? 's' : ''}` : ''}.` : 'Nothing to create.');
    return made > 0;
  }, [projects, resolveDefaultPid, isProjectView, view, availableTagNames, directory, taskGroups, loadTasks, loadTags, loadProjects, loadCounts, notify]);

  // ---- Global keyboard shortcuts -----------------------------------------
  // Safe: never fire while typing in a field or when a modal/dialog is open.
  useEffect(() => {
    const onKey = (e) => {
      // ⌘K / Ctrl+K opens the command palette from anywhere (even while typing).
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setCmdOpen((v) => !v);
        return;
      }
      // Escape closes the command palette, then the help overlay, then the detail panel.
      if (e.key === 'Escape') {
        if (cmdOpen) { setCmdOpen(false); return; }
        if (helpOpen) { setHelpOpen(false); return; }
        if (selectedIdRef.current) { closeDetail(); return; }
        return;
      }
      const el = e.target;
      const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      if (shareDialog.open) return; // don't hijack keys while a dialog is open
      if (e.key === '?') { e.preventDefault(); setHelpOpen((v) => !v); return; }
      switch (e.key) {
        case 't': e.preventDefault(); setSmartAddOpen(true); break;
        case '/': e.preventDefault(); searchRef.current?.focus(); break;
        case 'd': e.preventDefault(); setView('today'); setMobileNav(false); break;
        case 'u': e.preventDefault(); setView('upcoming'); setMobileNav(false); break;
        case 'f': e.preventDefault(); openFiltersBlank(); break;
        case '[': e.preventDefault(); setRailHidden((v) => !v); break;
        default: break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [helpOpen, cmdOpen, shareDialog.open, isProjectView, view, addTask, closeDetail, openFiltersBlank]);

  // ---- Triple-tap / triple-click opens the command palette ------------------
  // Three quick clicks/taps in the same spot (<500ms apart) open ⌘K — handy on
  // phones (no keyboard) and works on desktop too. Ignored while typing.
  const tripleTapRef = useRef({ count: 0, last: 0, x: 0, y: 0 });
  useEffect(() => {
    const onTap = (e) => {
      const el = e.target;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)) return;
      const now = Date.now();
      const s = tripleTapRef.current;
      const near = Math.abs(e.clientX - s.x) < 40 && Math.abs(e.clientY - s.y) < 40;
      s.count = (now - s.last < 500 && near) ? s.count + 1 : 1;
      s.last = now; s.x = e.clientX; s.y = e.clientY;
      if (s.count >= 3) {
        s.count = 0;
        setCmdOpen(true);
      }
    };
    window.addEventListener('click', onTap);
    return () => window.removeEventListener('click', onTap);
  }, []);

  const NavItem = ({ icon: Icon, label, id, count, testid, onClick, active, badge }) => (
    <button data-testid={testid} onClick={onClick || (() => { setView(id); setMobileNav(false); })}
      className={`n-focus flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm transition-colors
        ${(active != null ? active : view === id) ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
      <Icon size={16} className="shrink-0" /><span className="flex-1 text-left">{label}</span>
      {badge != null && badge > 0 && (
        <span data-testid={`${testid}-overdue-badge`} title={`${badge} overdue`}
          className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-[var(--n-danger)] px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-white">{badge}</span>
      )}
      {count != null && <span className="text-[12px] tabular-nums text-[var(--n-muted-2)]">{count}</span>}
    </button>
  );

  // Collapsible section header with a chevron + optional trailing action.
  const SectionHeader = ({ label, section, action, testid }) => (
    <div className="mt-5 flex items-center justify-between px-1.5">
      <button type="button" onClick={() => toggleSec(section)} data-testid={testid}
        className="n-focus flex items-center gap-1 rounded-md px-1 py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)] hover:text-[var(--n-ink-soft)]">
        <ChevronRight size={13} className={`transition-transform ${secOpen[section] ? 'rotate-90' : ''}`} />
        {label}
      </button>
      {action}
    </div>
  );

  const ProjectRow = ({ p }) => {
    const mine = !p.shared_by;
    return (
      <div data-testid="tasks-sidebar-project-row"
        className={`group flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm cursor-pointer transition-colors
          ${view === p.id ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}
        onClick={() => { setView(p.id); setMobileNav(false); }}>
        {mine ? (
          <span className="relative shrink-0">
            <button data-testid="tasks-sidebar-project-color" title="Change colour"
              onClick={(e) => { e.stopPropagation(); setColorMenu((cur) => (cur === p.id ? null : p.id)); }}
              className="n-focus grid h-3.5 w-3.5 place-items-center rounded-full ring-offset-1 hover:ring-2 hover:ring-[var(--n-accent-soft)]" style={{ background: p.color || 'var(--n-accent)' }}>
              <Palette size={8} className="text-white/0 group-hover:text-white/80" />
            </button>
            {colorMenu === p.id && (
              <div className="absolute left-0 top-full z-50 mt-1 grid grid-cols-4 gap-1.5 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-2 shadow-[var(--n-shadow)]" data-testid="tasks-project-color-menu" onClick={(e) => e.stopPropagation()}>
                {PROJECT_COLORS.map((c) => (
                  <button key={c} data-testid="tasks-project-color-option" title={c} onClick={(e) => { e.stopPropagation(); recolorProject(p, c); }}
                    className="grid h-5 w-5 place-items-center rounded-full ring-offset-1 hover:ring-2 hover:ring-[var(--n-accent-soft)]" style={{ background: c }}>
                    {(p.color || '').toLowerCase() === c.toLowerCase() && <Check size={11} className="text-white" strokeWidth={3} />}
                  </button>
                ))}
              </div>
            )}
          </span>
        ) : (
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: p.color || 'var(--n-accent)' }} />
        )}
        <span className="flex-1 truncate">{p.name}</span>
        {p.shared_by && <span title={`Shared by ${p.shared_by}`} className="shrink-0 text-[var(--n-accent)]"><Users2 size={12} /></span>}
        {mine && (
          <button data-testid="tasks-sidebar-project-share" onClick={(e) => { e.stopPropagation(); openShare({ type: 'project', id: p.id, name: p.name }); }} title="Share project"
            className={`shrink-0 ${p.is_shared ? 'text-[var(--n-accent)]' : 'text-[var(--n-muted-2)] opacity-0 group-hover:opacity-100'} transition-opacity hover:text-[var(--n-accent)]`}>
            <Share2 size={13} />
          </button>
        )}
        {mine && (
          <button data-testid="tasks-sidebar-project-default-toggle" onClick={(e) => setAsDefaultProject(p, e)}
            title={defaultProjectId === p.id ? 'Default project for new tasks · click to clear' : 'Set as default project for new tasks'}
            className={`shrink-0 transition-opacity ${defaultProjectId === p.id ? 'text-[var(--n-accent)] opacity-100' : 'text-[var(--n-muted-2)] opacity-0 group-hover:opacity-100 hover:text-[var(--n-accent)]'}`}>
            <Inbox size={13} fill={defaultProjectId === p.id ? 'currentColor' : 'none'} />
          </button>
        )}
        {mine && (
          <button data-testid="tasks-sidebar-project-favorite-toggle" onClick={(e) => toggleFavorite(p, e)} title={p.is_favorite ? 'Unfavourite' : 'Favourite'}
            className={`shrink-0 ${p.is_favorite ? 'text-[var(--n-accent)]' : 'text-[var(--n-muted-2)] opacity-0 group-hover:opacity-100'} transition-opacity`}>
            <Star size={13} fill={p.is_favorite ? 'currentColor' : 'none'} />
          </button>
        )}
        {mine && (
          <button onClick={(e) => deleteProject(p, e)} title="Delete project" data-testid="tasks-sidebar-project-delete"
            className="shrink-0 text-[var(--n-muted-2)] opacity-0 group-hover:opacity-100 hover:text-[var(--n-danger)] transition-opacity"><Trash2 size={13} /></button>
        )}
        <span className="shrink-0 text-[12px] tabular-nums text-[var(--n-muted-2)]">{p.task_count}</span>
      </div>
    );
  };

  const Sidebar = (
    <div className="flex h-full flex-col p-3">
      <button data-testid="tasks-new-task-button" onClick={() => setSmartAddOpen(true)}
        className="n-focus flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--n-accent)] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 transition-opacity">
        <Plus size={17} /> New task
      </button>
      <div className="mt-4 flex flex-col gap-0.5">
        <NavItem icon={LayoutGrid} label="All Tasks" id="all" testid="tasks-sidebar-all" />
        <NavItem icon={Clock} label="Today" id="today" badge={overdueTaskCount} count={todayOpenCount} testid="tasks-sidebar-today" />
        <NavItem icon={CalendarDays} label="Upcoming" id="upcoming" testid="tasks-sidebar-upcoming" />
        <NavItem icon={Users2} label="Shared with my groups" id="groups" testid="tasks-sidebar-groups" />
        <NavItem icon={Filter} label="Filters & Labels" onClick={openFiltersBlank} active={view === 'filters' && !activeFilterId} testid="tasks-sidebar-filters" />
      </div>

      <div className="mt-1.5 flex-1 overflow-y-auto n-scroll">
        {/* Saved filters */}
        <SectionHeader label="Saved filters" section="filters" testid="tasks-sec-filters-toggle" />
        {secOpen.filters && (
          <div className="mt-1 space-y-0.5" data-testid="tasks-saved-filters">
            {savedFilters.length === 0 ? (
              <p className="px-2.5 py-1 text-[12px] text-[var(--n-muted-2)]">Save a view from Filters &amp; Labels.</p>
            ) : savedFilters.map((f) => {
              const active = view === 'filters' && activeFilterId === f.id;
              const renaming = renamingFilterId === f.id;
              return (
                <div key={f.id} data-testid="tasks-saved-filter-row"
                  draggable={!renaming}
                  onDragStart={() => { dragFilterRef.current = f.id; }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleFilterDrop(f.id)}
                  className={`group flex items-center gap-1.5 rounded-xl px-2 py-2 text-sm cursor-pointer transition-colors
                    ${active ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}
                  onClick={() => { if (!renaming) applySavedFilter(f); }}>
                  <span data-testid="tasks-saved-filter-grip" title="Drag to reorder"
                    onClick={(e) => e.stopPropagation()}
                    className="shrink-0 cursor-grab text-[var(--n-muted-2)] opacity-0 group-hover:opacity-100 active:cursor-grabbing transition-opacity"><GripVertical size={13} /></span>
                  <Bookmark size={14} className="shrink-0 text-[var(--n-accent)]" fill={active ? 'currentColor' : 'none'} />
                  {renaming ? (
                    <input autoFocus value={renameVal} onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setRenameVal(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') renameFilter(f.id); else if (e.key === 'Escape') { setRenamingFilterId(null); setRenameVal(''); } }}
                      onBlur={() => renameFilter(f.id)} data-testid="tasks-saved-filter-rename-input"
                      className="n-focus min-w-0 flex-1 rounded border border-[var(--n-border)] bg-[var(--n-surface)] px-1.5 py-0.5 text-[13px] text-[var(--n-ink)]" />
                  ) : (
                    <span className="min-w-0 flex-1 truncate" data-testid="tasks-saved-filter-name">{f.name}</span>
                  )}
                  {!renaming && (
                    <>
                      <button data-testid="tasks-saved-filter-rename" title="Rename" onClick={(e) => { e.stopPropagation(); setRenamingFilterId(f.id); setRenameVal(f.name); }}
                        className="shrink-0 text-[var(--n-muted-2)] opacity-0 group-hover:opacity-100 hover:text-[var(--n-accent)] transition-opacity"><Pencil size={13} /></button>
                      <button data-testid="tasks-saved-filter-delete" title="Delete" onClick={(e) => { e.stopPropagation(); deleteSavedFilter(f.id); }}
                        className="shrink-0 text-[var(--n-muted-2)] opacity-0 group-hover:opacity-100 hover:text-[var(--n-danger)] transition-opacity"><Trash2 size={13} /></button>
                      {f.overdue_count > 0 && (
                        <span data-testid="tasks-saved-filter-overdue-badge" title={`${f.overdue_count} overdue`}
                          className="inline-flex min-w-[18px] shrink-0 items-center justify-center rounded-full bg-[var(--n-danger)] px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-white">{f.overdue_count}</span>
                      )}
                      <span data-testid="tasks-saved-filter-count" title={`${f.open_count || 0} open`} className="shrink-0 text-[12px] tabular-nums text-[var(--n-muted-2)]">{f.open_count || 0}</span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Completed — sits directly below Saved filters, above Projects */}
        <div className="mt-2">
          <NavItem icon={CheckCircle2} label="Completed" id="completed" count={view === 'completed' ? tasks.length : undefined} testid="tasks-sidebar-completed" />
        </div>

        {/* Projects */}
        <SectionHeader label="Projects" section="projects" testid="tasks-sec-projects-toggle"
          action={(
            <button data-testid="tasks-sidebar-new-project-button" onClick={() => { setNewProjectOpen((v) => !v); setNewProjectName(''); if (!secOpen.projects) toggleSec('projects'); }}
              className="n-focus text-[var(--n-muted)] hover:text-[var(--n-accent)]" title="New project"><Plus size={15} /></button>
          )} />
        {secOpen.projects && (
          <>
            {newProjectOpen && (
              <div className="mt-2 px-1" data-testid="tasks-new-project-box">
                <input autoFocus value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') createProject(); else if (e.key === 'Escape') { setNewProjectOpen(false); setNewProjectName(''); } }}
                  placeholder="Project name…" data-testid="tasks-new-project-input"
                  className="n-focus w-full rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-1.5 text-[13px] text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]" />
                <div className="mt-2 flex items-center gap-1.5" data-testid="tasks-new-project-colors">
                  {PROJECT_COLORS.map((c) => (
                    <button key={c} type="button" data-testid="tasks-new-project-color-option" title={c} onClick={() => setNewProjectColor(c)}
                      className={`grid h-5 w-5 place-items-center rounded-full transition-transform hover:scale-110 ${newProjectColor === c ? 'ring-2 ring-offset-1 ring-[var(--n-accent)]' : ''}`} style={{ background: c }}>
                      {newProjectColor === c && <Check size={11} className="text-white" strokeWidth={3} />}
                    </button>
                  ))}
                  <button type="button" onClick={createProject} disabled={!newProjectName.trim()} data-testid="tasks-new-project-create"
                    className="n-focus ml-auto rounded-lg bg-[var(--n-accent)] px-2.5 py-1 text-[12px] font-semibold text-white hover:opacity-90 disabled:opacity-50">Create</button>
                </div>
              </div>
            )}
            <div className="mt-1 space-y-0.5">
              {projects.length === 0 && !newProjectOpen && (
                <p className="px-2.5 py-1 text-[12px] text-[var(--n-muted-2)]">No projects yet.</p>
              )}
              {favProjects.map((p) => <ProjectRow key={p.id} p={p} />)}
              {favProjects.length > 0 && otherProjects.length > 0 && <div className="my-1 h-px bg-[var(--n-line)]" />}
              {otherProjects.map((p) => <ProjectRow key={p.id} p={p} />)}
            </div>
          </>
        )}

        {/* Tags */}
        <SectionHeader label="Tags" section="tags" testid="tasks-sec-tags-toggle" />
        {secOpen.tags && (
          <div className="mt-1.5 flex flex-wrap gap-1.5 px-1 pb-2" data-testid="tasks-sidebar-tags">
            {tags.length === 0 ? (
              <p className="px-1.5 py-1 text-[12px] text-[var(--n-muted-2)]">No tags yet.</p>
            ) : tags.map((t) => {
              const label = typeof t === 'string' ? t : t.tag;
              const sharedOnly = typeof t === 'object' && t.shared_only;
              const active = view === 'filters' && fTags.includes(label);
              if (tagEditing === label) {
                return (
                  <input key={label} autoFocus value={tagEditVal}
                    onChange={(e) => setTagEditVal(e.target.value)}
                    onBlur={() => renameTag(label, tagEditVal)}
                    onKeyDown={(e) => { if (e.key === 'Enter') renameTag(label, tagEditVal); else if (e.key === 'Escape') { setTagEditing(null); setTagEditVal(''); } }}
                    data-testid="tasks-sidebar-tag-rename-input" maxLength={48}
                    className="n-focus w-28 rounded-full border border-[var(--n-accent)] bg-[var(--n-surface)] px-2 py-0.5 text-[12px] text-[var(--n-ink)]" />
                );
              }
              return (
                <span key={label} data-testid="tasks-sidebar-tag"
                  className={`group inline-flex items-center gap-0.5 rounded-full border pl-2 pr-1 py-0.5 text-[12px] transition-colors
                    ${active ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'border-[var(--n-border)] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                  <button onClick={() => applyTagQuickFilter(label)} data-testid="tasks-sidebar-tag-toggle"
                    className="n-focus inline-flex items-center gap-1"><Hash size={10} />{label}</button>
                  {!sharedOnly && (
                    <button onClick={(e) => { e.stopPropagation(); setTagEditing(label); setTagEditVal(label); }}
                      title={`Rename “${label}”`} aria-label={`Rename tag ${label}`} data-testid="tasks-sidebar-tag-rename"
                      className={`grid h-4 w-4 place-items-center rounded-full opacity-100 transition-opacity ${active ? 'text-[var(--n-accent)] hover:bg-[var(--n-accent)]/15' : 'text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]'}`}>
                      <Pencil size={11} />
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // ---- Task detail panel ----
  const detailProjectName = selectedTask ? (projectById[selectedTask.project_id]?.name || (selectedTask.shared_by ? 'Shared project' : 'Shared')) : '';
  const isOwner = selectedTask ? (selectedTask.role ? selectedTask.role === 'owner' : !selectedTask.shared_by) : false;
  const canEdit = selectedTask ? (selectedTask.permission ? selectedTask.permission === 'edit' : !selectedTask.shared_by) : false;
  const detailPresence = selectedTask ? (presence[selectedTask.id] || []) : [];

  const Detail = selectedTask && (
    <aside data-testid="task-detail-panel" className="fixed inset-0 z-[200] flex flex-col overflow-y-auto n-scroll bg-[var(--n-surface)]" style={{ right: chatReserve || undefined }}>
      <div className="flex items-center justify-between border-b border-[var(--n-line)] px-5 py-3.5 text-[12px] text-[var(--n-muted)]">
        <span className="inline-flex items-center gap-2 min-w-0">
          <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: projectById[selectedTask.project_id]?.color || '#B49BE0' }} />
          <span className="truncate">{detailProjectName}</span>
          {selectedTask.shared_by && <span className="inline-flex items-center gap-1 text-[var(--n-accent)]" data-testid="task-detail-shared-by"><Users2 size={12} /> {selectedTask.shared_by}</span>}
        </span>
        <div className="flex items-center gap-1.5">
          {selectedTask.is_shared && <AvatarStack people={selectedTask.shared_with} size={22} max={4} testid="task-detail-avatars" />}
          {isOwner && selectedTask.is_shared && (
            <PingButton task={selectedTask} onFetchPingStatus={quickPingStatus} onPing={quickPing} />
          )}
          {isOwner && (
            <button onClick={() => openShare({ type: 'task', id: selectedTask.id, name: selectedTask.title })} title="Share task" data-testid="task-detail-share"
              className="n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] px-2 py-1 text-[12px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] transition-colors"><Share2 size={13} /> Share</button>
          )}
          {isOwner && (
            <button onClick={() => setSaveTemplateTask(selectedTask)} title="Save this task as a reusable template" data-testid="task-detail-save-template"
              className="n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] px-2 py-1 text-[12px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] transition-colors"><Bookmark size={13} /> Save as template</button>
          )}
          {isOwner && (
            <button onClick={() => deleteTask(selectedTask)} title="Delete task" data-testid="task-detail-delete" className="n-focus rounded-md p-1 hover:text-[var(--n-danger)]"><Trash2 size={15} /></button>
          )}
          <button onClick={closeDetail} title="Close (Esc)" data-testid="task-detail-close" className="n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] px-2 py-1 text-[12px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] transition-colors"><X size={15} /> Close</button>
        </div>
      </div>
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-5 p-5">
        <div className="flex items-start gap-3">
          <button type="button" data-testid="task-detail-complete-toggle" onClick={() => toggleComplete(selectedTask)}
            className={`n-focus mt-1 grid h-5 w-5 shrink-0 place-items-center rounded-md border transition-colors ${selectedTask.is_completed ? 'border-[var(--n-accent)] bg-[var(--n-accent)] text-white' : 'border-[var(--n-muted-2)] hover:border-[var(--n-accent)]'}`}>
            {selectedTask.is_completed && <Check size={13} strokeWidth={3} />}
          </button>
          <textarea data-testid="task-detail-title-input" value={selectedTask.title} rows={1} readOnly={!canEdit}
            onChange={(e) => setSelectedTask({ ...selectedTask, title: e.target.value })}
            onBlur={(e) => canEdit && patchTaskScoped(selectedTask.id, { title: e.target.value })}
            className={`n-focus flex-1 resize-none bg-transparent font-display text-2xl font-semibold leading-tight text-[var(--n-ink)] outline-none ${selectedTask.is_completed ? 'text-[var(--n-muted-2)] line-through' : ''}`} />
        </div>
        {selectedTask.is_completed && selectedTask.completed_by_name && (
          <div className="-mt-3 ml-8 inline-flex items-center gap-1.5 text-[11.5px] text-[var(--n-accent)]" data-testid="task-detail-completed-by"><Check size={12} /> Completed by {selectedTask.completed_by_name}</div>
        )}
        {selectedTask.shared_by && !canEdit && (
          <div className="-mt-2 ml-8 inline-flex w-fit items-center gap-1.5 rounded-full bg-[var(--n-canvas-2)] px-2 py-0.5 text-[11px] text-[var(--n-muted)]" data-testid="task-detail-viewonly-badge">
            <Eye size={12} /> View only — you can tick it complete, but not change its details
          </div>
        )}

        {/* Meta */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3 text-[13px]">
            <span className="w-[74px] shrink-0 text-[var(--n-muted)]">Priority</span>
            <div data-testid="task-detail-priority-select" className="inline-flex rounded-lg border border-[var(--n-border)] p-0.5">
              {PRIO_ORDER.map((p) => (
                <button key={p} data-testid={`task-detail-priority-${p}`} disabled={!canEdit} onClick={() => patchTaskScoped(selectedTask.id, { priority: p })}
                  className={`n-focus inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors disabled:opacity-50
                    ${selectedTask.priority === p ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                  {p === 'high' && <Flag size={12} className="text-[var(--n-danger)]" fill="currentColor" />}
                  {p === 'medium' && <Circle size={8} className="text-[var(--n-accent-2)]" fill="currentColor" />}
                  {PRIO[p].label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3 text-[13px]">
            <span className="w-[74px] shrink-0 text-[var(--n-muted)]">Status</span>
            <MiniSelect value={statusOf(selectedTask)} disabled={!canEdit}
              onChange={(v) => moveTaskToStatus(selectedTask.id, v)} options={STATUS_OPTS} testid="task-detail-status" width="w-44"
              leading={<span className="h-2 w-2 rounded-full" style={{ background: STATUS_META[statusOf(selectedTask)]?.dot || 'var(--n-muted-2)' }} />} />
          </div>
          <div className="flex items-center gap-3 text-[13px]">
            <span className="w-[74px] shrink-0 text-[var(--n-muted)]">Due</span>
            <input type="date" data-testid="task-detail-due-date" value={toLocalDate(selectedTask.due_at)} disabled={!canEdit}
              onChange={(e) => patchTask(selectedTask.id, { due_at: dueIso(e.target.value) })}
              className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-2.5 py-1.5 text-[13px] text-[var(--n-ink)] disabled:opacity-50" />
            {selectedTask.due_at && canEdit && (
              <button onClick={() => patchTask(selectedTask.id, { due_at: null })} title="Clear due date" className="n-focus text-[var(--n-muted-2)] hover:text-[var(--n-danger)]"><X size={14} /></button>
            )}
          </div>
          <div className="relative flex items-center gap-3 text-[13px]" ref={projectPickerRef} data-project-picker>
            <span className="w-[74px] shrink-0 text-[var(--n-muted)]">Project</span>
            <button data-testid="task-detail-project-select" disabled={!canEdit} onClick={() => setProjectPickerOpen((v) => !v)}
              className="n-focus inline-flex items-center gap-2 rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-2.5 py-1.5 text-[13px] text-[var(--n-ink-soft)] disabled:opacity-50">
              <span className="h-2 w-2 rounded-full" style={{ background: projectById[selectedTask.project_id]?.color || '#B49BE0' }} />
              {detailProjectName} <ChevronDown size={13} />
            </button>
            {projectPickerOpen && (
              <div className="absolute left-[86px] top-full z-40 mt-1 w-64 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]" data-testid="task-detail-project-menu">
                {projects.filter((p) => !p.shared_by).map((p) => (
                  renamingProjectId === p.id ? (
                    <div key={p.id} className="flex items-center gap-2 px-2.5 py-1.5" onClick={(e) => e.stopPropagation()}>
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: p.color }} />
                      <input autoFocus value={projectRenameVal} onChange={(e) => setProjectRenameVal(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') renameProjectInline(p.id); else if (e.key === 'Escape') { setRenamingProjectId(null); setProjectRenameVal(''); } }}
                        onBlur={() => renameProjectInline(p.id)} data-testid="task-detail-project-rename-input"
                        className="n-focus min-w-0 flex-1 rounded border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-1.5 py-1 text-[13px] text-[var(--n-ink)]" />
                    </div>
                  ) : (
                    <div key={p.id} className="group flex items-center gap-1 rounded-lg pr-1 hover:bg-[var(--n-canvas-2)]">
                      <button onClick={() => { patchTaskScoped(selectedTask.id, { project_id: p.id }); setProjectPickerOpen(false); }} data-testid="task-detail-project-option"
                        className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] text-[var(--n-ink-soft)]">
                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: p.color }} /><span className="truncate">{p.name}</span>
                        {selectedTask.project_id === p.id && <Check size={13} className="ml-auto shrink-0 text-[var(--n-accent)]" strokeWidth={3} />}
                      </button>
                      <button data-testid="task-detail-project-rename" title="Rename project"
                        onClick={(e) => { e.stopPropagation(); setRenamingProjectId(p.id); setProjectRenameVal(p.name); }}
                        className="n-focus shrink-0 rounded-md p-1 text-[var(--n-muted-2)] opacity-0 group-hover:opacity-100 hover:text-[var(--n-accent)] transition-opacity"><Pencil size={12} /></button>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
          {/* Repeat (recurring task) */}
          <div className="flex flex-col gap-2 text-[13px]">
            <div className="flex items-center gap-3">
              <span className="w-[74px] shrink-0 text-[var(--n-muted)]">Repeat</span>
              <MiniSelect value={selectedTask.recurrence || 'none'} disabled={!canEdit}
                onChange={(v) => {
                  if (v === 'weekdays') {
                    const cur = selectedTask.recurrence_weekdays || [];
                    patchTask(selectedTask.id, { recurrence: 'weekdays', recurrence_weekdays: cur.length ? cur : [defaultWeekdayFor(selectedTask.due_at)] });
                  } else { patchTask(selectedTask.id, { recurrence: v }); }
                }} options={RECUR_TASK} testid="task-detail-recurrence" width="w-48" />
              {selectedTask.recurrence && selectedTask.recurrence !== 'none' && (
                <span className="text-[11px] text-[var(--n-muted-2)]">A fresh copy appears when you complete it.</span>
              )}
            </div>
            {selectedTask.recurrence === 'weekdays' && (
              <div className="flex flex-wrap items-center gap-2" data-testid="task-detail-recurrence-weekdays">
                <span className="w-[74px] shrink-0 text-[var(--n-muted)]">On</span>
                <div className="flex flex-wrap gap-1">
                  {WEEKDAY_CHIPS.map((wd) => {
                    const on = (selectedTask.recurrence_weekdays || []).includes(wd.v);
                    return (
                      <button key={wd.v} type="button" disabled={!canEdit} data-testid={`task-detail-weekday-${wd.v}`}
                        aria-pressed={on} title={wd.name}
                        onClick={() => {
                          const cur = new Set(selectedTask.recurrence_weekdays || []);
                          if (on) { if (cur.size <= 1) return; cur.delete(wd.v); } else cur.add(wd.v);
                          patchTask(selectedTask.id, { recurrence: 'weekdays', recurrence_weekdays: [...cur].sort((a, b) => a - b) });
                        }}
                        className={`n-focus grid h-8 w-8 place-items-center rounded-full border text-[12px] font-semibold transition-colors disabled:opacity-50 ${on ? 'border-[var(--n-accent)] bg-[var(--n-accent)] text-white' : 'border-[var(--n-border)] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                        {wd.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {selectedTask.recurrence && selectedTask.recurrence !== 'none' && (
              <div className="flex flex-wrap items-center gap-2" data-testid="task-detail-recurrence-end">
                <span className="w-[74px] shrink-0 text-[var(--n-muted)]">Ends</span>
                <MiniSelect value={selectedTask.recurrence_end_mode || 'never'} disabled={!canEdit}
                  onChange={(v) => patchTask(selectedTask.id, { recurrence_end_mode: v })} options={RECUR_END} testid="task-detail-recurrence-end-mode" width="w-40" />
                {selectedTask.recurrence_end_mode === 'on' && (
                  <input type="date" data-testid="task-detail-recurrence-end-date" disabled={!canEdit}
                    value={selectedTask.recurrence_end_date ? toLocalDate(selectedTask.recurrence_end_date) : ''}
                    onChange={(e) => patchTask(selectedTask.id, { recurrence_end_date: e.target.value ? new Date(`${e.target.value}T00:00:00`).toISOString() : null })}
                    className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-2.5 py-1.5 text-[13px] text-[var(--n-ink)] disabled:opacity-50" />
                )}
                {selectedTask.recurrence_end_mode === 'after' && (
                  <span className="inline-flex items-center gap-1.5">
                    <input type="number" min="1" max="365" data-testid="task-detail-recurrence-count" disabled={!canEdit}
                      value={selectedTask.recurrence_count || ''}
                      onChange={(e) => patchTask(selectedTask.id, { recurrence_count: e.target.value ? Math.max(1, parseInt(e.target.value, 10) || 1) : null })}
                      className="n-focus w-16 rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-2.5 py-1.5 text-[13px] text-[var(--n-ink)] disabled:opacity-50" />
                    <span className="text-[12px] text-[var(--n-muted)]">occurrences</span>
                  </span>
                )}
              </div>
            )}
            {/* Series management: edit scope + pause + history */}
            {selectedTask.recurrence && selectedTask.recurrence !== 'none' && (
              <div className="mt-1 rounded-xl border border-[var(--n-border)] bg-[var(--n-canvas-2)]/50 p-2.5" data-testid="task-detail-series">
                {canEdit && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-medium text-[var(--n-muted)]">Edits apply to</span>
                    <div className="inline-flex rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] p-0.5" data-testid="task-detail-series-scope">
                      {[['This task', false], ['This & future', true]].map(([lbl, val]) => (
                        <button key={lbl} onClick={() => setApplyToSeries(val)} data-testid={`task-detail-series-scope-${val ? 'future' : 'this'}`}
                          className={`n-focus rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors ${applyToSeries === val ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>{lbl}</button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {selectedTask.role === 'owner' && (
                    <button onClick={() => pauseSeries(selectedTask.id, !selectedTask.recurrence_paused)} data-testid="task-detail-series-pause"
                      className={`n-focus inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] font-medium transition-colors ${selectedTask.recurrence_paused ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'border-[var(--n-border)] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                      {selectedTask.recurrence_paused ? (<><Play size={13} /> Resume series</>) : (<><Pause size={13} /> Pause series</>)}
                    </button>
                  )}
                  {selectedTask.recurrence_paused && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-[var(--n-danger)]/10 px-2 py-0.5 text-[11px] font-semibold text-[var(--n-danger)]" data-testid="task-detail-series-paused-badge">Paused</span>
                  )}
                  <button onClick={() => { setSeriesOpen((v) => !v); if (!seriesData) loadSeries(selectedTask.id); }} data-testid="task-detail-series-toggle"
                    className="n-focus ml-auto inline-flex items-center gap-1 text-[12px] font-medium text-[var(--n-accent)] hover:underline">
                    <ChevronRight size={13} className={`transition-transform ${seriesOpen ? 'rotate-90' : ''}`} />
                    Series history{seriesData?.occurrences?.length ? ` (${seriesData.occurrences.length})` : ''}
                  </button>
                </div>
                {seriesOpen && (
                  <div className="mt-2 space-y-1 border-t border-[var(--n-border)] pt-2" data-testid="task-detail-series-history">
                    {(seriesData?.occurrences || []).length === 0 ? (
                      <p className="py-1 text-center text-[12px] text-[var(--n-muted-2)]">No occurrences yet.</p>
                    ) : (seriesData.occurrences.map((o) => (
                      <button key={o.id} onClick={() => { if (!o.is_current) openTask({ id: o.id }); }} data-testid="task-detail-series-occurrence"
                        className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12.5px] transition-colors ${o.is_current ? 'bg-[var(--n-accent-bg)]' : 'hover:bg-[var(--n-canvas-2)]'}`}>
                        <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] font-bold ${o.is_completed ? 'bg-[var(--n-accent)] text-white' : 'border border-[var(--n-border)] text-[var(--n-muted)]'}`}>{o.is_completed ? <Check size={11} strokeWidth={3} /> : o.recurrence_index}</span>
                        <span className={`min-w-0 flex-1 truncate ${o.is_completed ? 'text-[var(--n-muted-2)] line-through' : 'text-[var(--n-ink)]'}`}>{o.title}</span>
                        <span className="shrink-0 text-[11px] text-[var(--n-muted-2)]">{o.is_completed ? 'Done' : (o.due_at ? fmtDueDate(o.due_at) : '')}{o.is_current ? ' · current' : ''}</span>
                      </button>
                    )))}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Reminder — a date plus a time you can type OR pick */}
          <div className="flex items-start gap-3 text-[13px]">
            <span className="mt-1.5 w-[74px] shrink-0 text-[var(--n-muted)]">Reminder</span>
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <input type="date" data-testid="task-detail-reminder-date" disabled={!canEdit}
                  value={toLocalDate(selectedTask.reminder_at)}
                  onChange={(e) => patchTask(selectedTask.id, e.target.value
                    ? { reminder_at: combineDueAt(e.target.value, toLocalTime(selectedTask.reminder_at) || '09:00') }
                    : { reminder_at: null, reminder_rule: 'none' })}
                  className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-2.5 py-1.5 text-[13px] text-[var(--n-ink)] disabled:opacity-50" />
                <input type="time" data-testid="task-detail-reminder-time" step="60" placeholder="HH:MM"
                  disabled={!canEdit || !selectedTask.reminder_at}
                  value={toLocalTime(selectedTask.reminder_at)}
                  onChange={(e) => patchTask(selectedTask.id, { reminder_at: combineDueAt(toLocalDate(selectedTask.reminder_at) || toLocalDate(new Date().toISOString()), e.target.value || '09:00') })}
                  className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-2.5 py-1.5 text-[13px] text-[var(--n-ink)] disabled:opacity-50" />
                <MiniSelect value={selectedTask.reminder_rule || 'none'} disabled={!canEdit || !selectedTask.reminder_at}
                  onChange={(v) => patchTask(selectedTask.id, { reminder_rule: v })} options={RECUR} testid="task-detail-reminder-recurrence" />
                {selectedTask.reminder_at && canEdit && (
                  <button onClick={() => patchTask(selectedTask.id, { reminder_at: null, reminder_rule: 'none' })} title="Clear reminder" className="n-focus text-[var(--n-muted-2)] hover:text-[var(--n-danger)]"><X size={14} /></button>
                )}
              </div>
              {selectedTask.reminder_at && canEdit && (
                <div className="flex flex-wrap items-center gap-1.5" data-testid="task-detail-reminder-time-presets">
                  <span className="text-[11px] text-[var(--n-muted-2)]">Quick time</span>
                  {[['9:00 AM', '09:00'], ['12:00 PM', '12:00'], ['5:00 PM', '17:00'], ['9:00 PM', '21:00']].map(([lbl, val]) => (
                    <button key={val} data-testid={`task-detail-reminder-preset-${val}`}
                      onClick={() => patchTask(selectedTask.id, { reminder_at: combineDueAt(toLocalDate(selectedTask.reminder_at), val) })}
                      className="n-focus rounded-md border border-[var(--n-border)] px-1.5 py-0.5 text-[11px] text-[var(--n-ink-soft)] hover:border-[var(--n-accent)] hover:text-[var(--n-accent)] transition-colors">{lbl}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tags */}
        <div>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Tags</div>
          <div className="flex flex-wrap items-center gap-1.5">
            {(selectedTask.tags || []).map((t) => (
              <span key={t} className="group inline-flex items-center gap-1 rounded-full border border-[var(--n-border)] px-2 py-0.5 text-[12px] text-[var(--n-ink-soft)]">
                <Hash size={10} />{t}
                {canEdit && <button onClick={() => patchTaskScoped(selectedTask.id, { tags: (selectedTask.tags || []).filter((x) => x !== t) })} className="n-focus text-[var(--n-muted-2)] hover:text-[var(--n-danger)]"><X size={11} /></button>}
              </span>
            ))}
            {canEdit && <TagAdder existing={selectedTask.tags || []} options={tags.map((t) => (typeof t === 'string' ? t : t.tag))} onAdd={(t) => patchTaskScoped(selectedTask.id, { tags: [...(selectedTask.tags || []), t] })} />}
            {(!selectedTask.tags || selectedTask.tags.length === 0) && !canEdit && <span className="text-[12px] text-[var(--n-muted-2)]">No tags</span>}
          </div>
        </div>

        {/* Notes (rich text body) */}
        <div>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Notes</div>
          <TaskBodyEditor key={selectedTask.id} initialHtml={selectedTask.body_html || ''} canEdit={canEdit}
            onSave={(html) => patchTaskScoped(selectedTask.id, { body_html: html })} />
        </div>

        {/* Sub-tasks (multi-level checklist) */}
        <TaskSubtasks key={`sub-${selectedTask.id}`} taskId={selectedTask.id} canEdit={canEdit}
          onTaskSync={syncTaskFromSubtasks} />

        {/* Attachments */}
        <TaskAttachments taskId={selectedTask.id} initial={selectedTask.attachments || []} readOnly={!canEdit}
          onChange={(next) => setSelectedTask((s) => (s && s.id === selectedTask.id ? { ...s, attachments: next, attachment_count: next.length, has_attachments: next.length > 0 } : s))} />

        <div className="h-px bg-[var(--n-line)]" />

        {/* Linked Notes — below description/attachments, above comments (per-user visibility) */}
        <TaskLinkedNotes taskId={selectedTask.id}
          event={linkEvent && linkEvent.task_id === selectedTask.id ? linkEvent.ts : 0}
          onOpenNote={(nid) => navigate(`/notes?note=${nid}`)} notify={notify} />

        <div className="h-px bg-[var(--n-line)]" />

        {/* Linked events & meetings — associate the task with planner items */}
        <TaskLinkedEvents taskId={selectedTask.id}
          onOpenItem={(date) => navigate(`/notes/planner?view=daily&date=${date}`)} notify={notify} />

        <div className="h-px bg-[var(--n-line)]" />

        {/* Comments (live) */}
        <TaskComments taskId={selectedTask.id} principal={principal} event={commentEvent} presence={detailPresence} people={selectedTask.shared_with || []} canInvite={false} focusCommentId={pendingCommentFocus} onInvited={() => scheduleReload()} />

        <div className="h-px bg-[var(--n-line)]" />

        {/* Activity history */}
        <TaskActivity taskId={selectedTask.id} event={activityEvent} />
      </div>
    </aside>
  );

  // ---- Filters & Labels builder ----
  const activeFilterObj = savedFilters.find((f) => f.id === activeFilterId) || null;
  const filterHasCriteria = fDueSet.length > 0 || fTags.length > 0 || fProjects.length > 0 || fStatuses.length > 0 || fPriorities.length > 0;
  const myProjects = projects.filter((p) => !p.shared_by);
  const sharedProjects = projects.filter((p) => p.shared_by);
  const FilterPanel = (
    <div className="px-6 pt-1" data-testid="tasks-filter-builder">
      <div className="rounded-2xl border border-[var(--n-border)] bg-[var(--n-surface)] p-4 md:p-5">
        <div className="mb-4 flex items-center gap-2">
          <button type="button" data-testid="tasks-filter-builder-toggle" aria-expanded={filterBuilderOpen}
            onClick={() => setFilterBuilderOpen((v) => { const nx = !v; try { localStorage.setItem('tasks:filterBuilderOpen', nx ? '1' : '0'); } catch { /* noop */ } return nx; })}
            className="n-focus flex items-center gap-2 text-left">
            <ChevronRight size={15} className={`shrink-0 text-[var(--n-muted-2)] transition-transform ${filterBuilderOpen ? 'rotate-90' : ''}`} />
            <SlidersHorizontal size={16} className="text-[var(--n-accent)]" />
            <span className="text-[13px] font-semibold text-[var(--n-ink)]">Build a filter</span>
          </button>
          {activeFilterObj && (
            <span className="inline-flex items-center gap-1 rounded-full bg-[var(--n-accent-bg)] px-2 py-0.5 text-[11px] font-medium text-[var(--n-accent)]" data-testid="tasks-filter-active-name">
              <Bookmark size={10} fill="currentColor" />{activeFilterObj.name}
            </span>
          )}
          {!filterBuilderOpen && filterHasCriteria && (
            <span className="rounded-full bg-[var(--n-canvas-2)] px-2 py-0.5 text-[11px] font-medium text-[var(--n-muted)]" data-testid="tasks-filter-active-hint">Filters active</span>
          )}
          {filterHasCriteria && (
            <button onClick={() => { setActiveFilterId(null); resetFilterDraft(); }} data-testid="tasks-filter-reset"
              className="n-focus ml-auto text-[12px] font-medium text-[var(--n-muted)] hover:text-[var(--n-accent)]">Reset</button>
          )}
        </div>

        {filterBuilderOpen && (<>
        {/* Natural-language filter box */}
        <NlFilterBox onApply={applyNlFilter} availableTags={tags} projects={projects} />
        {/* Due date */}
        <div className="mb-4">
          <div className="mb-1.5 flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Due date</span>
            <span className="rounded-full bg-[var(--n-canvas-2)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--n-muted-2)]" title="Selected due-date chips are combined with OR">match any · OR</span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="tasks-filter-due-presets">
            {DUE_PRESETS.filter((d) => d.value !== 'any').map((d) => {
              const on = fDueSet.includes(d.value);
              return (
                <button key={d.value} data-testid={`tasks-filter-due-${d.value}`} aria-pressed={on} onClick={() => toggleFDue(d.value)}
                  className={`n-focus inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[12.5px] font-medium transition-colors
                    ${on ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'border-[var(--n-border)] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                  {d.label}{on && <Check size={11} strokeWidth={3} />}
                </button>
              );
            })}
          </div>
          {fDueSet.includes('custom') && (
            <div className="mt-2.5 flex flex-wrap items-center gap-2 text-[13px]" data-testid="tasks-filter-due-custom">
              <span className="text-[var(--n-muted)]">From</span>
              <input type="date" value={fDueStart} onChange={(e) => setFDueStart(e.target.value)} data-testid="tasks-filter-due-start"
                className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-2.5 py-1.5 text-[13px] text-[var(--n-ink)]" />
              <span className="text-[var(--n-muted)]">to</span>
              <input type="date" value={fDueEnd} onChange={(e) => setFDueEnd(e.target.value)} data-testid="tasks-filter-due-end"
                className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-2.5 py-1.5 text-[13px] text-[var(--n-ink)]" />
            </div>
          )}
        </div>

        {/* Status */}
        <div className="mb-4">
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Status</div>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="tasks-filter-statuses">
            {STATUS_OPTS.map((s) => {
              const on = fStatuses.includes(s.value);
              return (
                <button key={s.value} data-testid={`tasks-filter-status-${s.value}`} aria-pressed={on} onClick={() => toggleFStatus(s.value)}
                  className={`n-focus inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12.5px] font-medium transition-colors
                    ${on ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'border-[var(--n-border)] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                  <span className="h-2 w-2 rounded-full" style={{ background: STATUS_META[s.value]?.dot || 'var(--n-muted-2)' }} />{s.label}{on && <Check size={11} strokeWidth={3} />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Priority */}
        <div className="mb-4">
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Priority</div>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="tasks-filter-priorities">
            {PRIO_ORDER.map((p) => {
              const on = fPriorities.includes(p);
              return (
                <button key={p} data-testid={`tasks-filter-priority-${p}`} aria-pressed={on} onClick={() => toggleFPriority(p)}
                  className={`n-focus inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12.5px] font-medium transition-colors
                    ${on ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'border-[var(--n-border)] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                  <Flag size={11} className={PRIO[p]?.cls} />{PRIO[p]?.label}{on && <Check size={11} strokeWidth={3} />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tags */}
        <div className="mb-4">
          <div className="mb-1.5 flex items-center gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Tags</span>
            {fTags.length > 1 && (
              <div className="inline-flex items-center rounded-lg border border-[var(--n-border)] p-0.5" data-testid="tasks-filter-tag-mode">
                {['any', 'all'].map((m) => (
                  <button key={m} data-testid={`tasks-filter-tag-mode-${m}`} onClick={() => setFTagMode(m)}
                    className={`n-focus rounded-md px-2 py-0.5 text-[11px] font-medium transition-colors ${fTagMode === m ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                    Match {m === 'any' ? 'any' : 'all'}
                  </button>
                ))}
              </div>
            )}
          </div>
          {tags.length === 0 ? (
            <p className="text-[12px] text-[var(--n-muted-2)]">No tags yet.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {tags.map((t) => {
                const label = typeof t === 'string' ? t : t.tag;
                const on = fTags.includes(label);
                return (
                  <button key={label} data-testid="tasks-filter-tag-chip" onClick={() => toggleFTag(label)}
                    className={`n-focus inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[12.5px] transition-colors
                      ${on ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'border-[var(--n-border)] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                    <Hash size={10} />{label}{on && <Check size={11} strokeWidth={3} />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Projects */}
        <div className="mb-4">
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Projects</div>
          {projects.length === 0 ? (
            <p className="text-[12px] text-[var(--n-muted-2)]">No projects yet.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {[...myProjects, ...sharedProjects].map((p) => {
                const on = fProjects.includes(p.id);
                return (
                  <button key={p.id} data-testid="tasks-filter-project-chip" onClick={() => toggleFProject(p.id)}
                    className={`n-focus inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12.5px] transition-colors
                      ${on ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] text-[var(--n-ink)]' : 'border-[var(--n-border)] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                    <span className="h-2 w-2 rounded-full" style={{ background: p.color || 'var(--n-accent)' }} />{p.name}{on && <Check size={11} strokeWidth={3} />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Save actions */}
        <div className="flex flex-wrap items-center gap-2 border-t border-[var(--n-line)] pt-3">
          {savingFilter ? (
            <div className="flex flex-1 flex-wrap items-center gap-2" data-testid="tasks-filter-save-box">
              <input autoFocus value={filterName} onChange={(e) => setFilterName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveNewFilter(); else if (e.key === 'Escape') { setSavingFilter(false); setFilterName(''); } }}
                placeholder="Name this filter…" data-testid="tasks-filter-name-input"
                className="n-focus min-w-[200px] flex-1 rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-3 py-2 text-[13px] text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]" />
              <button onClick={saveNewFilter} disabled={!filterName.trim()} data-testid="tasks-filter-save-confirm"
                className="n-focus rounded-lg bg-[var(--n-accent)] px-3 py-2 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50">Save</button>
              <button onClick={() => { setSavingFilter(false); setFilterName(''); }} className="n-focus rounded-lg px-3 py-2 text-[13px] font-medium text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)]">Cancel</button>
            </div>
          ) : (
            <>
              <button onClick={() => { setSavingFilter(true); setFilterName(activeFilterObj ? `${activeFilterObj.name} copy` : ''); }} disabled={!filterHasCriteria}
                data-testid="tasks-filter-save" className="n-focus inline-flex items-center gap-1.5 rounded-lg bg-[var(--n-accent)] px-3 py-2 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50">
                <Bookmark size={14} /> Save as new filter
              </button>
              {activeFilterObj && (
                <button onClick={updateActiveFilter} data-testid="tasks-filter-update"
                  className="n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] px-3 py-2 text-[13px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
                  <Check size={14} /> Update “{activeFilterObj.name}”
                </button>
              )}
            </>
          )}
        </div>
        </>)}
      </div>
    </div>
  );

  const filterChips = (
    <div className="flex flex-wrap items-center gap-1.5 px-6 pt-3" data-testid="tasks-filter-chips">
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Active</span>
      {fDueSet.map((s) => (
        <FilterChip key={`cd-${s}`} testid="tasks-filter-chip-due" icon={<CalendarDays size={11} />}
          label={s === 'custom' ? `Due: ${fDueStart || '…'} → ${fDueEnd || '…'}` : `Due: ${(DUE_PRESETS.find((d) => d.value === s) || {}).label}`}
          onRemove={() => toggleFDue(s)} />
      ))}
      {fTags.map((t) => (
        <FilterChip key={`ct-${t}`} testid="tasks-filter-chip-tag" icon={<Hash size={11} />} label={t} onRemove={() => toggleFTag(t)} />
      ))}
      {fProjects.map((p) => (
        <FilterChip key={`cp-${p}`} testid="tasks-filter-chip-project" dot={projectById[p]?.color || 'var(--n-accent)'} label={projectById[p]?.name || 'Project'} onRemove={() => toggleFProject(p)} />
      ))}
      {fStatuses.map((s) => (
        <FilterChip key={`cs-${s}`} testid="tasks-filter-chip-status" dot={STATUS_META[s]?.dot || 'var(--n-muted-2)'} label={`Status: ${STATUS_META[s]?.label || s}`} onRemove={() => toggleFStatus(s)} />
      ))}
      {fPriorities.map((p) => (
        <FilterChip key={`cpr-${p}`} testid="tasks-filter-chip-priority" icon={<Flag size={11} />} label={`Priority: ${PRIO[p]?.label || p}`} onRemove={() => toggleFPriority(p)} />
      ))}
    </div>
  );



  // ---- Content region ----
  // Renders a list of task rows + their inline editors (shared by project & date groups).
  const renderTaskRows = (items) => items.map((t) => (
    <React.Fragment key={t.id}>
      <TaskRow task={t} active={selectedId === t.id} onToggle={toggleComplete} onOpen={openTask}
        onDelete={deleteTask}
        selected={selectedTaskIds.has(t.id)}
        onSelect={(view === 'completed' && t.shared_by) ? undefined : toggleSelectTask}
        selectionActive={view === 'completed' || selectedTaskIds.size > 0}
        expanded={expandedRows.has(t.id)} onToggleExpand={toggleRowExpand}
        subtasksOpen={subtaskRows.has(t.id)} onToggleSubtasks={toggleSubtaskRow} />
      {expandedRows.has(t.id) && renderInlineEditor(t)}
      {subtaskRows.has(t.id) && !expandedRows.has(t.id) && (
        <div className="pl-9 pr-2" data-testid="task-row-subtasks-quick">
          <TaskSubtasks compact taskId={t.id} canEdit={!t.shared_by || t.permission === 'edit'} onTaskSync={syncTaskFromSubtasks} />
        </div>
      )}
    </React.Fragment>
  ));

  // Gentle, tappable overdue nudge shown at the top of the list (not on the
  // Completed view, Upcoming, or when already viewing the overdue filter).
  const overdueNudge = (!loading && overdueTaskCount > 0 && view !== 'completed' && view !== 'upcoming' && !(view === 'filters' && fDueSet.length === 1 && fDueSet[0] === 'overdue')) ? (
    <button type="button" data-testid="tasks-overdue-nudge" onClick={showOverdue}
      className="mt-3 flex w-full items-center gap-2.5 rounded-xl border border-[var(--n-danger)]/30 bg-[var(--n-danger)]/10 px-3.5 py-2.5 text-left transition-colors hover:bg-[var(--n-danger)]/15 n-focus">
      <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[var(--n-danger)]/15 text-[var(--n-danger)]"><AlertTriangle size={15} /></span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-semibold text-[var(--n-ink)]" data-testid="tasks-overdue-nudge-count">{overdueTaskCount} {overdueTaskCount === 1 ? 'task is' : 'tasks are'} overdue</span>
        <span className="block text-[11.5px] text-[var(--n-muted)]">Take a look so nothing slips through.</span>
      </span>
      <span className="inline-flex shrink-0 items-center gap-1 text-[12px] font-medium text-[var(--n-danger)]">Review <ChevronRight size={13} /></span>
    </button>
  ) : null;

  // Renders a group's tasks as either compact rows (list) or a responsive card
  // grid — driven by `agendaGrid` (Completed/Project/Filters grid modes).
  function renderTaskItems(items) {
    if (agendaGrid) {
      return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4" data-testid="tasks-agenda-card-grid">
          {items.map(renderAllCard)}
        </div>
      );
    }
    return <div className="space-y-2">{renderTaskRows(items)}</div>;
  }

  const AgendaContent = (
    <div className="flex-1 overflow-visible md:overflow-y-auto n-scroll px-6 pb-24 md:pb-8">
      {overdueNudge}
      {loading ? (
        <div className="space-y-3 pt-2" data-testid="tasks-loading">
          {[0, 1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-[var(--n-canvas-2)]" />)}
        </div>
      ) : (visibleTasks.length === 0 && completedToday.length === 0) ? (
        <div className="mx-auto mt-16 max-w-md rounded-2xl border border-[var(--n-border)] bg-[var(--n-surface)] p-8 text-center" data-testid="tasks-empty-state">
          <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-[var(--n-accent-bg)] text-[var(--n-accent)]">{view === 'completed' ? <CheckCircle2 size={22} /> : <ListChecks size={22} />}</div>
          <p className="font-display text-lg text-[var(--n-ink)]">{inlineFiltersActive ? 'No tasks match these filters' : view === 'today' ? 'Nothing due today' : view === 'completed' ? 'No completed tasks yet' : 'No tasks here yet'}</p>
          <p className="mt-1 text-[13px] text-[var(--n-muted)]">{inlineFiltersActive ? 'Try clearing a filter to see more.' : view === 'today' ? 'Enjoy the calm — or add something to tackle.' : view === 'completed' ? 'Finish a task and it will show up here.' : 'Create a task to get started.'}</p>
          {view !== 'completed' && (
            <button data-testid="tasks-empty-primary-cta" onClick={() => setSmartAddOpen(true)}
              className="n-focus mt-4 inline-flex items-center gap-2 rounded-xl bg-[var(--n-accent)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"><Plus size={16} /> Add a task</button>
          )}
        </div>
      ) : (
        <div className="space-y-7 pt-2">
          {view === 'completed' && ownedCompletedIds.length > 0 && (
            <div className="flex items-center gap-2.5 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2.5" data-testid="tasks-completed-select-all-bar">
              <button type="button" onClick={toggleSelectAllCompleted} data-testid="tasks-completed-select-all-master"
                aria-pressed={allOwnedSelected} aria-label={allOwnedSelected ? 'Clear selection' : 'Select all completed tasks'}
                className={`n-focus grid h-[18px] w-[18px] shrink-0 place-items-center rounded-md border transition-all ${(allOwnedSelected || someOwnedSelected) ? 'border-[var(--n-accent)] bg-[var(--n-accent)] text-white' : 'border-[var(--n-muted-2)] hover:border-[var(--n-accent)]'}`}>
                {allOwnedSelected ? <Check size={12} strokeWidth={3} /> : someOwnedSelected ? <Minus size={12} strokeWidth={3} /> : null}
              </button>
              <button type="button" onClick={toggleSelectAllCompleted} data-testid="tasks-completed-select-all-label"
                className="n-focus text-[13px] font-semibold text-[var(--n-ink-soft)] hover:text-[var(--n-ink)]">
                {allOwnedSelected ? 'Clear selection' : 'Select all'}
              </button>
              <span className="text-[12px] text-[var(--n-muted-2)]" data-testid="tasks-completed-select-all-hint">
                {selectedTaskIds.size > 0 ? `${selectedTaskIds.size} of ${ownedCompletedIds.length} selected` : `${ownedCompletedIds.length} completed ${ownedCompletedIds.length === 1 ? 'task' : 'tasks'}`}
              </span>
            </div>
          )}
          {useDateGroups ? (
            dateGrouped.map((bk) => {
              const collapsed = collapsedBuckets.has(bk.key);
              return (
              <section key={bk.key} data-testid="tasks-date-group" data-bucket={bk.key}
                className="border-t border-[var(--n-line)] pt-5 first:border-t-0 first:pt-0">
                <div className="mb-3 flex items-center gap-2.5">
                  <button type="button" data-testid="tasks-date-group-toggle" aria-expanded={!collapsed}
                    title={collapsed ? 'Expand group' : 'Collapse group'}
                    onClick={() => toggleBucket(bk.key)}
                    className="n-focus flex items-center gap-2 rounded-md">
                    <ChevronRight size={14} className={`shrink-0 text-[var(--n-muted-2)] transition-transform ${collapsed ? '' : 'rotate-90'}`} />
                    <span className={`inline-flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-[0.08em] ${bk.tone}`} data-testid="tasks-date-group-header">
                      {bk.key === 'overdue' ? <AlertTriangle size={14} /> : <CalendarDays size={14} />}
                      {bk.label}
                    </span>
                    <span className="rounded-full bg-[var(--n-canvas-2)] px-1.5 py-0.5 text-[11px] tabular-nums text-[var(--n-muted)]" data-testid="tasks-date-group-count">{bk.items.length}</span>
                  </button>
                </div>
                {!collapsed && (
                  <>
                    {dateSubgroupByProject ? (
                      <div className="space-y-4">
                        {groupItemsByProject(bk.items).map(({ project, items }) => (
                          <div key={project.id} data-testid="tasks-date-project-subgroup">
                            <div className="mb-2 flex items-center gap-2 pl-0.5">
                              <span className="h-2 w-2 rounded-full" style={{ background: project.color || 'var(--n-accent)' }} />
                              <h4 className="text-[13px] font-semibold text-[var(--n-ink-soft)]">{project.name}</h4>
                              <span className="text-[11px] text-[var(--n-muted-2)]">{items.length}</span>
                            </div>
                            {renderTaskItems(items)}
                          </div>
                        ))}
                      </div>
                    ) : (
                      renderTaskItems(bk.items)
                    )}
                    {bk.key !== 'overdue' && bk.key !== 'completed' && (
                      <button type="button" data-testid="tasks-date-group-add-task"
                        onClick={() => addTaskInBucket(bk.key, isProjectView ? view : null)}
                        className="n-focus mt-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[13px] text-[var(--n-muted)] hover:text-[var(--n-accent)]">
                        <Plus size={15} /> Add task{bk.key === 'no_due' ? '' : ` for ${bk.label.toLowerCase()}`}
                      </button>
                    )}
                  </>
                )}
              </section>
              );
            })
          ) : (
            grouped.map(({ project, items }) => {
              // Today & Completed groups collapse; BOTH default to collapsed
              // (session-remembered per view). Other agenda views stay open.
              const collapsible = view === 'today' || view === 'completed';
              const isCompletedView = view === 'completed';
              const expandedSet = isCompletedView ? completedExpandedProjects : todayExpandedProjects;
              const toggleGroup = isCompletedView ? toggleCompletedProject : toggleTodayProject;
              const expanded = !collapsible || expandedSet.has(project.id);
              const showGroupSelect = isCompletedView && items.some((t) => !t.shared_by);
              return (
              <section key={project.id} data-testid="tasks-agenda-group">
                {collapsible ? (
                  <div className="mb-3 flex items-center gap-2.5">
                    {showGroupSelect && (
                      <button type="button" onClick={() => toggleSelectGroup(items)} data-testid="tasks-completed-group-select"
                        aria-pressed={isGroupAllSelected(items)}
                        title={isGroupAllSelected(items) ? 'Deselect this group' : 'Select this group'}
                        className={`n-focus grid h-[18px] w-[18px] shrink-0 place-items-center rounded-md border transition-all ${(isGroupAllSelected(items) || isGroupAnySelected(items)) ? 'border-[var(--n-accent)] bg-[var(--n-accent)] text-white' : 'border-[var(--n-muted-2)] hover:border-[var(--n-accent)]'}`}>
                        {isGroupAllSelected(items) ? <Check size={12} strokeWidth={3} /> : isGroupAnySelected(items) ? <Minus size={12} strokeWidth={3} /> : null}
                      </button>
                    )}
                    <button type="button" data-testid="tasks-agenda-group-toggle" aria-expanded={expanded}
                      title={expanded ? 'Collapse project' : 'Expand project'}
                      onClick={() => toggleGroup(project.id)}
                      className="n-focus flex min-w-0 flex-1 items-center gap-2.5 rounded-md text-left">
                      <ChevronRight size={15} className={`shrink-0 text-[var(--n-muted-2)] transition-transform ${expanded ? 'rotate-90' : ''}`} />
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: project.color || 'var(--n-accent)' }} />
                      <h3 className="truncate text-[15px] font-semibold text-[var(--n-ink)]">{project.name}</h3>
                      <span className="shrink-0 text-[12px] text-[var(--n-muted-2)]" data-testid="tasks-agenda-group-count">{items.length} {items.length === 1 ? 'task' : 'tasks'}</span>
                    </button>
                  </div>
                ) : (
                  <div className="mb-3 flex items-center gap-2.5">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: project.color || 'var(--n-accent)' }} />
                    <h3 className="text-[15px] font-semibold text-[var(--n-ink)]">{project.name}</h3>
                    <span className="text-[12px] text-[var(--n-muted-2)]">{items.length} {items.length === 1 ? 'task' : 'tasks'}</span>
                  </div>
                )}
                {expanded && (
                  <>
                    {renderTaskItems(items)}
                    {(view === project.id) && (
                      <button onClick={() => addTask(project.id)} data-testid="tasks-group-add-task"
                        className="n-focus mt-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[13px] text-[var(--n-muted)] hover:text-[var(--n-accent)]"><Plus size={15} /> Add task</button>
                    )}
                  </>
                )}
              </section>
              );
            })
          )}
          {/* Completed section (Today: completed today · Project list: completed in project) */}
          {completedToday.length > 0 && (view === 'today' || (isProjectView && viewMode === 'list')) && (
            <section data-testid="tasks-today-completed">
              <button type="button" onClick={() => setTodayCompletedOpen((v) => !v)} data-testid="tasks-today-completed-toggle"
                className="n-focus mb-3 flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)] hover:text-[var(--n-ink-soft)]">
                <ChevronRight size={14} className={`transition-transform ${todayCompletedOpen ? 'rotate-90' : ''}`} />
                <CheckCheck size={14} className="text-[var(--n-accent)]" /> {view === 'today' ? 'Completed today' : 'Completed'}
                <span className="rounded-full bg-[var(--n-canvas-2)] px-1.5 py-0.5 text-[11px] tabular-nums text-[var(--n-muted)]">{completedToday.length}</span>
              </button>
              {todayCompletedOpen && (
                <div className="space-y-2">
                  {completedToday.map((t) => <TaskRow key={t.id} task={t} active={selectedId === t.id} onToggle={toggleComplete} onOpen={openTask} onDelete={deleteTask} />)}
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </div>
  );

  // Shared card for the All Tasks grid + its No-Due-Date pane (styling unchanged).
  // Clicking opens the task's focus detail panel. Declared as a hoisted function so
  // AgendaContent (defined earlier) can render it in the Completed/Project/Filters grids.
  function renderAllCard(t) {
    const st = dueStatusOf(t);
    const dateLabel = t.is_completed
      ? (t.due_at ? new Date(t.due_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : 'No date')
      : st === 'none' ? 'No date'
      : st === 'today' ? 'Today'
      : (st === 'overdue' ? 'Overdue · ' : '') + new Date(t.due_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    const metaColor = st === 'overdue' ? 'text-[var(--n-danger)]' : st === 'today' ? 'text-[var(--n-success)]' : 'text-[var(--n-muted)]';
    const proj = projectById[t.project_id];
    const sharedWithMe = !!t.shared_by;
    // Task-level share → sharer chip only (no project). Project-level share →
    // BOTH the project chip and the sharer chip. Owned tasks → project chip.
    const showProjectChip = proj && (!sharedWithMe || t.share_scope === 'project');
    const selectable = view === 'completed' && !t.shared_by; // only owned completed cards can be bulk-acted
    const isSel = selectedTaskIds.has(t.id);
    return (
      <div key={t.id} role="button" tabIndex={0} data-testid="task-card"
        onClick={(e) => { if (longPressRef.current.fired) { longPressRef.current.fired = false; e.preventDefault(); e.stopPropagation(); return; } openTask(t); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openTask(t); } }}
        onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setQuickPanel({ task: t, x: e.clientX, y: e.clientY }); }}
        onTouchStart={(e) => {
          if (e.touches.length !== 1) return;
          const tp = e.touches[0];
          const lp = longPressRef.current;
          lp.fired = false; lp.x = tp.clientX; lp.y = tp.clientY;
          if (lp.timer) clearTimeout(lp.timer);
          lp.timer = window.setTimeout(() => {
            lp.fired = true; lp.timer = null;
            try { if (navigator.vibrate) navigator.vibrate(12); } catch { /* noop */ }
            setQuickPanel({ task: t, x: lp.x, y: lp.y });
          }, 500);
        }}
        onTouchMove={(e) => {
          const lp = longPressRef.current;
          if (!lp.timer) return;
          const tp = e.touches[0];
          if (Math.abs(tp.clientX - lp.x) > 10 || Math.abs(tp.clientY - lp.y) > 10) { clearTimeout(lp.timer); lp.timer = null; }
        }}
        onTouchEnd={() => { const lp = longPressRef.current; if (lp.timer) { clearTimeout(lp.timer); lp.timer = null; } }}
        style={{ WebkitTouchCallout: 'none' }}
        className={`group relative cursor-pointer overflow-hidden rounded-xl border-[1.5px] bg-[var(--n-surface)] px-2.5 py-2.5 text-left outline-none transition-colors hover:bg-[var(--n-canvas-2)] focus-visible:ring-2 focus-visible:ring-[var(--n-accent)] ${dueBorderCls(st)} ${(selectedId === t.id || isSel) ? 'ring-1 ring-[var(--n-accent)]' : ''}`}>
        <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${t.priority === 'high' ? 'bg-[var(--n-danger)]' : t.priority === 'medium' ? 'bg-[var(--n-accent-2)]' : 'bg-[var(--n-accent)]'}`} />
        <div className="flex items-stretch gap-2">
          {selectable && (
            <button type="button" data-testid="task-card-select"
              onClick={(e) => { e.stopPropagation(); toggleSelectTask(t.id); }}
              aria-pressed={isSel} title={isSel ? 'Deselect task' : 'Select task'}
              className={`n-focus mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded-md border transition-all ${isSel ? 'border-[var(--n-accent)] bg-[var(--n-accent)] text-white' : 'border-[var(--n-muted-2)] hover:border-[var(--n-accent)]'}`}>
              {isSel ? <Check size={12} strokeWidth={3} /> : null}
            </button>
          )}
          <div className="min-w-0 flex-1">
            <div className={`truncate text-[13px] font-medium ${t.is_completed ? 'text-[var(--n-muted-2)] line-through' : 'text-[var(--n-ink)]'}`}>{t.title}</div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
              <span className={`shrink-0 font-medium ${metaColor}`}>{dateLabel}</span>
              {showProjectChip && (
                <span data-testid="task-card-project-chip"
                  className="inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium"
                  style={{ color: proj.color || 'var(--n-accent)', borderColor: proj.color ? `${proj.color}59` : 'var(--n-border)', background: proj.color ? `${proj.color}1f` : 'transparent' }}>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ background: proj.color || 'var(--n-accent)' }} />
                  <span className="max-w-[110px] truncate">{proj.name}</span>
                </span>
              )}
              {sharedWithMe && (
                <span data-testid="task-card-shared-chip" title={`Shared by ${t.shared_by}`}
                  className="inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold"
                  style={{ color: N_GOLD, borderColor: N_GOLD_BORDER, background: N_GOLD_BG }}>
                  <Users2 size={9} />
                  <span className="max-w-[120px] truncate">{t.shared_by}</span>
                </span>
              )}
              <span className="ml-auto flex shrink-0 items-center gap-1.5">
                {t.linked_notes_count > 0 && (
                  <span data-testid="task-card-linked-notes"
                    title={`${t.linked_notes_count} linked note${t.linked_notes_count > 1 ? 's' : ''}`}
                    className="inline-flex items-center gap-0.5 text-[var(--n-muted-2)]">
                    <NotebookText size={11} />{t.linked_notes_count > 1 ? t.linked_notes_count : ''}
                  </span>
                )}
                {t.comment_count > 0 && (
                  <span data-testid="task-card-comment-count" data-unread={t.unread_mention ? 'true' : 'false'}
                    title={t.unread_mention ? `${t.comment_count} comment(s) · you were mentioned` : `${t.comment_count} comment(s)`}
                    className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 transition-colors ${t.unread_mention
                      ? 'bg-[var(--n-accent-bg)] font-bold text-[var(--n-accent)]'
                      : 'text-[var(--n-muted-2)]'}`}>
                    {t.unread_mention ? <AtSign size={11} /> : <MessageSquare size={11} />}
                    {t.comment_count}
                  </span>
                )}
                {t.subtask_total > 0 ? (
                  <button type="button" data-testid="task-card-subtask-count"
                    onClick={(e) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setSubtaskPopover((p) => (p && p.id === t.id) ? null : { id: t.id, anchor: { top: r.top, bottom: r.bottom, left: r.left, right: r.right }, canEdit: (!t.shared_by || t.permission === 'edit') }); }}
                    title={`${t.subtask_done}/${t.subtask_total} sub-tasks done · tap to view`}
                    className={`n-focus inline-flex items-center rounded-full px-1 py-0.5 transition-colors hover:bg-[var(--n-canvas-2)] ${t.subtask_done === t.subtask_total ? 'text-[var(--n-accent)]' : 'text-[var(--n-ink-soft)] hover:text-[var(--n-accent)]'}`}>
                    <ListChecks size={13} />
                  </button>
                ) : (!t.shared_by || t.permission === 'edit') ? (
                  <button type="button" data-testid="task-card-subtask-add"
                    onClick={(e) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setSubtaskPopover((p) => (p && p.id === t.id) ? null : { id: t.id, anchor: { top: r.top, bottom: r.bottom, left: r.left, right: r.right }, canEdit: true }); }}
                    title="Add sub-tasks"
                    className="n-focus inline-flex items-center rounded-full px-1 py-0.5 text-[var(--n-muted-2)] transition-colors hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-accent)]">
                    <ListChecks size={12} />
                  </button>
                ) : null}
              </span>
            </div>
            {(t.tags || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {t.tags.slice(0, 3).map((tag) => (
                  <span key={tag} className="inline-flex items-center gap-0.5 rounded-full border border-[var(--n-border)] px-1.5 py-0.5 text-[10px] text-[var(--n-muted)]"><Hash size={9} />{tag}</span>
                ))}
                {t.tags.length > 3 && <span className="text-[10px] text-[var(--n-muted-2)]">+{t.tags.length - 3}</span>}
              </div>
            )}
          </div>
          {t.is_shared && <AvatarColumn people={t.shared_with} size={18} />}
        </div>
      </div>
    );
  }

  // Date-bucket groups for the grid (No-Due-Date lives in the side pane).
  const allMainGroups = dateGrouped.filter((bk) => bk.key !== 'no_due');
  const allNoDue = (dateGrouped.find((bk) => bk.key === 'no_due') || {}).items || [];

  const AllTasksContent = (
    <div className="flex-1 overflow-visible md:overflow-auto n-scroll px-6 pb-24 md:pb-8">
      {loading ? (
        <div className="grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-7" data-testid="tasks-loading">
          {Array.from({ length: 14 }).map((_, i) => <div key={i} className="h-24 animate-pulse rounded-xl bg-[var(--n-canvas-2)]" />)}
        </div>
      ) : (allTasksSorted.length === 0 && !(isTodayGrid && completedToday.length > 0)) ? (
        <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="tasks-all-empty">
          <LayoutGrid size={30} className="mb-3 text-[var(--n-muted-2)]" />
          <p className="text-[15px] font-medium text-[var(--n-ink)]">{selectedTags.length || debounced ? 'No tasks match your search or tags.' : 'No tasks yet.'}</p>
          <p className="mt-1 text-[13px] text-[var(--n-muted)]">{selectedTags.length || debounced ? 'Try clearing the filter.' : 'Create a task to see it here.'}</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4 pt-2 lg:flex-row lg:items-start" data-testid="tasks-all-grid">
          {/* Main pane — date-bucket groups (Overdue on top, in red) */}
          <div className="min-w-0 flex-1">
            {allMainGroups.length === 0 && !(isTodayGrid && completedToday.length > 0) ? (
              <div className="rounded-2xl border border-dashed border-[var(--n-border)] p-8 text-center text-[13px] text-[var(--n-muted)]" data-testid="tasks-all-nodated-empty">
                No dated tasks — everything lives under “No due date”.
              </div>
            ) : (
              <>
              {allMainGroups.map((bk) => {
              const expanded = allGridExpanded.has(bk.key);
              const isOverdue = bk.key === 'overdue';
              const isCompletedGrp = bk.key === 'completed';
              const openCount = isCompletedGrp ? bk.items.length : bk.items.filter((t) => !t.is_completed).length;
              return (
                <section key={bk.key} data-testid="tasks-all-group" data-bucket={bk.key}
                  className="border-b border-[var(--n-line)] py-4 first:pt-1">
                  <button type="button" onClick={() => toggleAllGrid(bk.key)} data-testid="tasks-all-group-toggle" aria-expanded={expanded}
                    title={expanded ? 'Collapse group' : 'Expand group'} className="n-focus mb-1 flex w-full items-center gap-2.5 rounded-md">
                    <ChevronRight size={15} className={`shrink-0 text-[var(--n-muted-2)] transition-transform ${expanded ? 'rotate-90' : ''}`} />
                    <span className={`inline-flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-[0.08em] ${isOverdue ? 'text-[var(--n-danger)]' : isCompletedGrp ? 'text-[var(--n-accent)]' : bk.tone}`} data-testid="tasks-all-group-header">
                      {isOverdue ? <AlertTriangle size={14} /> : isCompletedGrp ? <CheckCheck size={14} /> : <CalendarDays size={14} />}
                      {bk.label}
                    </span>
                    <span data-testid="tasks-all-group-count"
                      className={`rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums ${isOverdue ? 'bg-[var(--n-danger)]/12 text-[var(--n-danger)]' : 'bg-[var(--n-canvas-2)] text-[var(--n-muted)]'}`}>{openCount}</span>
                  </button>
                  {expanded && (
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                      {bk.items.map(renderAllCard)}
                    </div>
                  )}
                </section>
              );
            })}
              {isTodayGrid && completedToday.length > 0 && (
                <section data-testid="tasks-today-completed-group" data-bucket="completed_today"
                  className="border-b border-[var(--n-line)] py-4">
                  <button type="button" onClick={() => setTodayCompletedOpen((v) => !v)} data-testid="tasks-today-completed-grid-toggle"
                    aria-expanded={todayCompletedOpen} title={todayCompletedOpen ? 'Collapse group' : 'Expand group'}
                    className="n-focus mb-1 flex w-full items-center gap-2.5 rounded-md">
                    <ChevronRight size={15} className={`shrink-0 text-[var(--n-muted-2)] transition-transform ${todayCompletedOpen ? 'rotate-90' : ''}`} />
                    <span className="inline-flex items-center gap-1.5 text-[13px] font-bold uppercase tracking-[0.08em] text-[var(--n-accent)]" data-testid="tasks-today-completed-header">
                      <CheckCheck size={14} /> Completed today
                    </span>
                    <span data-testid="tasks-today-completed-grid-count"
                      className="rounded-full bg-[var(--n-canvas-2)] px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-[var(--n-muted)]">{completedToday.length}</span>
                  </button>
                  {todayCompletedOpen && (
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                      {completedToday.map(renderAllCard)}
                    </div>
                  )}
                </section>
              )}
              </>
            )}
          </div>

          {/* Side pane — No Due Date (cards), mirrors the Calendar view. Toggled
              by the "No due" switch, independent of the due-date dropdown. */}
          {tNoDue && (
          <div className="shrink-0 lg:w-64" data-testid="tasks-all-nodue-pane">
            <div className="rounded-2xl border border-[var(--n-border)] bg-[var(--n-surface)] p-2.5 lg:sticky lg:top-2">
              <div className="mb-1.5 flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted)]">
                <CalendarClock size={12} /> No due date · {allNoDue.filter((t) => !t.is_completed).length}
              </div>
              {allNoDue.length === 0 ? (
                <p className="px-1 py-2 text-[12px] text-[var(--n-muted-2)]">Every task has a due date.</p>
              ) : (
                <div className="flex max-h-[68vh] flex-col gap-2 overflow-y-auto n-scroll pb-0.5 lg:max-h-[calc(100vh-240px)]">
                  {allNoDue.map(renderAllCard)}
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );

  // A single draggable day-card used by the Upcoming board (open + done lists).
  const renderUpcomingCard = (t) => (
    <button key={t.id} onClick={() => openTask(t)} data-testid="task-card"
      draggable
      onDragStart={(e) => { setDragTaskId(t.id); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', t.id); } catch { /* noop */ } }}
      onDragEnd={() => { setDragTaskId(null); setDragOverDay(null); }}
      title="Drag to another day to change the due date"
      className={`relative cursor-grab overflow-hidden rounded-xl border bg-[var(--n-surface-2,var(--n-canvas-2))] px-2.5 py-2 text-left transition-colors hover:bg-[var(--n-canvas-2)] active:cursor-grabbing ${dragTaskId === t.id ? 'opacity-40' : ''} ${selectedId === t.id ? 'border-[var(--n-accent)]' : 'border-[var(--n-border)]'}`}>
      <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${t.priority === 'high' ? 'bg-[var(--n-danger)]' : t.priority === 'medium' ? 'bg-[var(--n-accent-2)]' : 'bg-[var(--n-accent)]'}`} />
      <div className={`truncate text-[12.5px] font-medium ${t.is_completed ? 'text-[var(--n-muted-2)] line-through' : 'text-[var(--n-ink)]'}`}>{t.title}</div>
      <div className="mt-1 flex items-center gap-2 truncate text-[11px] text-[var(--n-muted)]">
        <span className="truncate">{fmtTime(t.due_at)}{projectById[t.project_id] ? ` · ${projectById[t.project_id].name}` : ''}</span>
        {t.is_shared && <span className="ml-auto shrink-0"><AvatarStack people={t.shared_with} size={16} max={2} /></span>}
      </div>
    </button>
  );

  const UpcomingContent = (
    <div className="flex-1 overflow-visible md:overflow-auto n-scroll px-6 pb-24 md:pb-8">
      {/* Board toolbar: hint + show-completed toggle + 7/14-day range toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 pb-1">
        <div className="text-[12px] text-[var(--n-muted)]" data-testid="tasks-upcoming-hint">
          {overdueUpcoming.length > 0 ? `${overdueUpcoming.length} overdue · drag onto a day to reschedule` : 'Drag a card to another day to change its due date'}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggleShowCompletedUpcoming} data-testid="tasks-upcoming-show-completed-toggle" aria-pressed={showCompletedUpcoming}
            title={showCompletedUpcoming ? 'Hide completed tasks' : 'Show completed tasks'}
            className={`n-focus inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[12px] font-medium transition-colors ${showCompletedUpcoming ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'border-[var(--n-border)] text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
            {showCompletedUpcoming ? <CheckCircle2 size={13} /> : <Circle size={13} />} Completed
          </button>
          <div className="inline-flex rounded-lg border border-[var(--n-border)] p-0.5" data-testid="tasks-upcoming-range-toggle">
            {[7, 14].map((n) => (
              <button key={n} onClick={() => setUpcomingRangePersist(n)} data-testid={`tasks-upcoming-range-${n}`}
                className={`n-focus rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors ${upcomingRange === n ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                {n} days
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Overdue rail — late tasks you can drag forward onto any day */}
      {!loading && overdueUpcoming.length > 0 && (
        <div data-testid="tasks-upcoming-overdue-rail"
          className="mb-3 rounded-2xl border border-[var(--n-danger)]/40 bg-[var(--n-danger)]/[0.04] p-2.5">
          <div className="mb-1.5 flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--n-danger)]">
            <AlertTriangle size={12} /> Overdue · {overdueUpcoming.length}
          </div>
          <div className="flex gap-2 overflow-x-auto n-scroll pb-1">
            {overdueUpcoming.map((t) => (
              <button key={t.id} onClick={() => openTask(t)} data-testid="task-card" draggable
                onDragStart={(e) => { setDragTaskId(t.id); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', t.id); } catch { /* noop */ } }}
                onDragEnd={() => { setDragTaskId(null); setDragOverDay(null); }}
                title="Drag onto a day to reschedule"
                className={`relative w-44 shrink-0 cursor-grab overflow-hidden rounded-xl border border-[var(--n-danger)]/50 bg-[var(--n-surface)] px-2.5 py-2 text-left transition-colors hover:bg-[var(--n-canvas-2)] active:cursor-grabbing ${dragTaskId === t.id ? 'opacity-40' : ''}`}>
                <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[var(--n-danger)]" />
                <div className={`truncate text-[12.5px] font-medium ${t.is_completed ? 'text-[var(--n-muted-2)] line-through' : 'text-[var(--n-ink)]'}`}>{t.title}</div>
                <div className="mt-1 truncate text-[11px] text-[var(--n-danger)]">
                  {new Date(t.due_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}{projectById[t.project_id] ? ` · ${projectById[t.project_id].name}` : ''}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 gap-3 pt-2 md:grid-cols-4 xl:grid-cols-7" data-testid="tasks-loading">
          {Array.from({ length: upcomingRange }).map((_, i) => <div key={i} className="h-52 animate-pulse rounded-2xl bg-[var(--n-canvas-2)]" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-7" data-testid="tasks-upcoming-grid">
          {weekDays.map(({ day, open, done, isToday }) => {
            const dayKey = day.toISOString();
            const isDropTarget = dragOverDay === dayKey && dragTaskId;
            return (
            <div key={dayKey} data-testid="tasks-upcoming-day"
              onDragOver={(e) => { if (dragTaskId) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverDay !== dayKey) setDragOverDay(dayKey); } }}
              onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOverDay((d) => (d === dayKey ? null : d)); }}
              onDrop={(e) => { e.preventDefault(); if (dragTaskId) rescheduleTaskToDay(dragTaskId, day); setDragTaskId(null); setDragOverDay(null); }}
              className={`flex min-h-[180px] flex-col rounded-2xl border transition-colors ${isDropTarget ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : isToday ? 'border-[var(--n-accent)] bg-[var(--n-surface)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'border-[var(--n-border)] bg-[var(--n-surface)]'}`}>
              <div className="flex items-baseline justify-between border-b border-[var(--n-border)] px-3 py-2.5">
                <span className="text-[11px] uppercase tracking-[0.1em] text-[var(--n-muted)]">{day.toLocaleDateString(undefined, { weekday: 'short' })}</span>
                <span className={`font-display text-lg ${isToday ? 'text-[var(--n-accent)]' : 'text-[var(--n-ink)]'}`} data-testid="tasks-upcoming-day-date">{day.getDate()}</span>
              </div>
              <div className="flex flex-1 flex-col gap-2 p-2.5">
                {/* Uncompleted tasks first */}
                {open.map(renderUpcomingCard)}
                {/* Completed tasks grouped at the bottom (behind the show-completed toggle) */}
                {showCompletedUpcoming && done.length > 0 && (
                  <div className="mt-1 flex items-center gap-2 pt-0.5" data-testid="tasks-upcoming-done-divider">
                    <span className="h-px flex-1 bg-[var(--n-line)]" />
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]"><CheckCircle2 size={11} /> Done · {done.length}</span>
                    <span className="h-px flex-1 bg-[var(--n-line)]" />
                  </div>
                )}
                {showCompletedUpcoming && done.map(renderUpcomingCard)}
                {/* Per-day quick add — opens the full Focus detail panel with the
                    due date pre-set to this day so users can flesh out the task. */}
                <button type="button" data-testid="tasks-upcoming-add-task"
                  onClick={() => addTask(null, null, dueIso(toLocalDate(day.toISOString())))}
                  title={`Add a task due ${day.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' })}`}
                  className="mt-auto inline-flex items-center justify-center gap-1.5 rounded-xl border border-dashed border-[var(--n-border)] px-2 py-2 text-[11.5px] font-medium text-[var(--n-muted)] transition-colors hover:border-[var(--n-accent)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-accent)]">
                  <Plus size={13} /> Add task
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Project Calendar view — month grid styled like the All Tasks cards, with
  // drag-to-reschedule (reuses rescheduleTaskToDay) and a "No date" side rail.
  const CalendarContent = (
    <div className="flex-1 overflow-visible md:overflow-auto n-scroll px-6 pb-24 md:pb-8" data-testid="tasks-calendar">
      {/* Month navigation — arrows carry badges for prev-month backlog / next-month load */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2 pb-3">
        <div className="flex items-center gap-2">
          <button onClick={() => setCalMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
            data-testid="tasks-calendar-prev" title={calData.prevUncompleted > 0 ? `${calData.prevUncompleted} unfinished last month` : 'Previous month'}
            className="n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1.5 text-[var(--n-ink-soft)] transition-colors hover:bg-[var(--n-canvas-2)]">
            <ChevronLeft size={16} />
            {calData.prevUncompleted > 0 && (
              <span data-testid="tasks-calendar-prev-count" className="rounded-full bg-[var(--n-danger)]/12 px-1.5 py-0.5 text-[11px] font-semibold text-[var(--n-danger)]">{calData.prevUncompleted}</span>
            )}
          </button>
          <h3 data-testid="tasks-calendar-month" className="min-w-[150px] text-center font-display text-lg font-semibold text-[var(--n-ink)]">{calData.label}</h3>
          <button onClick={() => setCalMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
            data-testid="tasks-calendar-next" title={calData.nextDue > 0 ? `${calData.nextDue} due next month` : 'Next month'}
            className="n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1.5 text-[var(--n-ink-soft)] transition-colors hover:bg-[var(--n-canvas-2)]">
            {calData.nextDue > 0 && (
              <span data-testid="tasks-calendar-next-count" className="rounded-full bg-[var(--n-accent-bg)] px-1.5 py-0.5 text-[11px] font-semibold text-[var(--n-accent)]">{calData.nextDue}</span>
            )}
            <ChevronRight size={16} />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] p-0.5" data-testid="tasks-calendar-density-toggle">
            <button onClick={() => setCalDensityPref('comfortable')} data-testid="tasks-calendar-density-comfortable" title="Comfortable — larger cards"
              className={`n-focus grid h-7 w-7 place-items-center rounded-md transition-colors ${calDensity === 'comfortable' ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
              <Rows3 size={15} />
            </button>
            <button onClick={() => setCalDensityPref('compact')} data-testid="tasks-calendar-density-compact" title="Compact — fit more tasks per day"
              className={`n-focus grid h-7 w-7 place-items-center rounded-md transition-colors ${calDensity === 'compact' ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
              <Menu size={15} />
            </button>
          </div>
          <button onClick={() => { const n = new Date(); setCalMonth(new Date(n.getFullYear(), n.getMonth(), 1)); }}
            data-testid="tasks-calendar-today"
            className="n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-1.5 text-[13px] font-medium text-[var(--n-ink-soft)] transition-colors hover:bg-[var(--n-canvas-2)]">
            <CalendarDays size={14} /> Today
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-3 lg:flex-row">
        {/* Month grid */}
        <div className="min-w-0 flex-1">
          <div className="grid grid-cols-7 gap-1.5 pb-1.5">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d} className="px-1 text-center text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted)]">{d}</div>
            ))}
          </div>
          {loading ? (
            <div className="grid grid-cols-7 gap-1.5" data-testid="tasks-loading">
              {Array.from({ length: 35 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-xl bg-[var(--n-canvas-2)]" />)}
            </div>
          ) : (
            <div className="grid grid-cols-7 gap-1.5" data-testid="tasks-calendar-grid">
              {calData.cells.map((cell) => {
                const isDrop = dragOverDay === cell.key && dragTaskId;
                return (
                  <div key={cell.key} data-testid="tasks-calendar-day"
                    onDragOver={cell.inMonth ? (e) => { if (dragTaskId) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverDay !== cell.key) setDragOverDay(cell.key); } } : undefined}
                    onDragLeave={cell.inMonth ? (e) => { if (e.currentTarget === e.target) setDragOverDay((d) => (d === cell.key ? null : d)); } : undefined}
                    onDrop={cell.inMonth ? (e) => { e.preventDefault(); if (dragTaskId) rescheduleTaskToDay(dragTaskId, cell.date); setDragTaskId(null); setDragOverDay(null); } : undefined}
                    className={`flex ${calDensity === 'compact' ? 'min-h-[150px]' : 'min-h-[118px]'} flex-col rounded-xl border transition-colors ${!cell.inMonth ? 'border-transparent bg-[var(--n-canvas-2)]/30' : isDrop ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : cell.isToday ? 'border-[var(--n-accent)] bg-[var(--n-surface)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'border-[var(--n-border)] bg-[var(--n-surface)]'}`}>
                    <div className="flex items-center justify-between px-2 pt-1.5">
                      <span className={`grid h-6 min-w-[24px] place-items-center rounded-full px-1 text-[12px] font-semibold ${!cell.inMonth ? 'text-[var(--n-muted-2)]' : cell.isToday ? 'bg-[var(--n-accent)] text-white' : 'text-[var(--n-ink)]'}`} data-testid="tasks-calendar-day-date">{cell.date.getDate()}</span>
                      {cell.inMonth && cell.items.length > 0 && (
                        <span data-testid="tasks-calendar-day-count" title={`${cell.items.length} task${cell.items.length === 1 ? '' : 's'}`}
                          className="rounded-full bg-[var(--n-canvas-2)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--n-muted)]">{cell.items.length}</span>
                      )}
                    </div>
                    {cell.inMonth && (
                      <div className={`mt-1 flex-1 overflow-y-auto n-scroll px-1.5 pb-1.5 ${calDensity === 'compact' ? 'space-y-[3px]' : 'space-y-1'}`} style={{ maxHeight: calDensity === 'compact' ? 132 : 84 }}>
                        {cell.items.map((t) => {
                          const st = dueStatusOf(t);
                          return (
                            <button key={t.id} onClick={() => openTask(t)} data-testid="task-card" draggable
                              onDragStart={(e) => { setDragTaskId(t.id); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', t.id); } catch { /* noop */ } }}
                              onDragEnd={() => { setDragTaskId(null); setDragOverDay(null); }}
                              title={t.title}
                              className={`relative block w-full cursor-grab overflow-hidden rounded-md border-[1.5px] bg-[var(--n-surface)] text-left transition-colors hover:bg-[var(--n-canvas-2)] active:cursor-grabbing ${calDensity === 'compact' ? 'py-[3px] pl-2 pr-1' : 'py-1 pl-2 pr-1'} ${dueBorderCls(st)} ${dragTaskId === t.id ? 'opacity-40' : ''} ${selectedId === t.id ? 'ring-1 ring-[var(--n-accent)]' : ''}`}>
                              <span className={`absolute left-0 top-0 bottom-0 w-[2px] ${t.priority === 'high' ? 'bg-[var(--n-danger)]' : t.priority === 'medium' ? 'bg-[var(--n-accent-2)]' : 'bg-[var(--n-accent)]'}`} />
                              <div className="flex items-center gap-1">
                                {(view === 'all' || view === 'filters') && <span className="h-1.5 w-1.5 shrink-0 rounded-full" title={projectById[t.project_id]?.name || ''} style={{ background: projectById[t.project_id]?.color || 'var(--n-muted-2)' }} />}
                                <span className={`truncate font-medium ${calDensity === 'compact' ? 'text-[10px]' : 'text-[11px]'} ${t.is_completed ? 'text-[var(--n-muted-2)] line-through' : 'text-[var(--n-ink)]'}`}>{t.title}</span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* No-date side rail — drag a card onto a day to give it a due date.
            On All Tasks it honours the "No due" toggle (hidden when off). */}
        {(view !== 'all' || tNoDue) && (
        <div className="shrink-0 lg:w-60" data-testid="tasks-calendar-nodate-rail">
          <div className="rounded-2xl border border-[var(--n-border)] bg-[var(--n-surface)] p-2.5">
            <div className="mb-1.5 flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted)]">
              <CalendarClock size={12} /> No date · {calData.noDate.length}
            </div>
            {calData.noDate.length === 0 ? (
              <p className="px-1 py-2 text-[12px] text-[var(--n-muted-2)]">Every task has a due date.</p>
            ) : (
              <div className="flex max-h-[60vh] flex-col gap-1.5 overflow-y-auto n-scroll pb-0.5 lg:max-h-[calc(100vh-260px)]">
                {calData.noDate.map((t) => (
                  <button key={t.id} onClick={() => openTask(t)} data-testid="task-card" draggable
                    onDragStart={(e) => { setDragTaskId(t.id); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', t.id); } catch { /* noop */ } }}
                    onDragEnd={() => { setDragTaskId(null); setDragOverDay(null); }}
                    title="Drag onto a day to set a due date"
                    className={`relative w-full cursor-grab overflow-hidden rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] py-1.5 pl-2.5 pr-2 text-left transition-colors hover:bg-[var(--n-canvas-2)] active:cursor-grabbing ${dragTaskId === t.id ? 'opacity-40' : ''} ${selectedId === t.id ? 'ring-1 ring-[var(--n-accent)]' : ''}`}>
                    <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${t.priority === 'high' ? 'bg-[var(--n-danger)]' : t.priority === 'medium' ? 'bg-[var(--n-accent-2)]' : 'bg-[var(--n-accent)]'}`} />
                    <div className="flex items-center gap-1">
                      {(view === 'all' || view === 'filters') && <span className="h-1.5 w-1.5 shrink-0 rounded-full" title={projectById[t.project_id]?.name || ''} style={{ background: projectById[t.project_id]?.color || 'var(--n-muted-2)' }} />}
                      <span className={`truncate text-[12px] font-medium ${t.is_completed ? 'text-[var(--n-muted-2)] line-through' : 'text-[var(--n-ink)]'}`}>{t.title}</span>
                    </div>
                    {(t.tags || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {t.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="inline-flex items-center gap-0.5 rounded-full border border-[var(--n-border)] px-1.5 py-0.5 text-[10px] text-[var(--n-muted)]"><Hash size={9} />{tag}</span>
                        ))}
                        {t.tags.length > 2 && <span className="text-[10px] text-[var(--n-muted-2)]">+{t.tags.length - 2}</span>}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );


  const BoardContent = (
    <div className="flex-1 overflow-hidden px-4 pb-20 sm:px-6 md:pb-6" data-testid="tasks-board">
      {loading ? (
        <div className="flex h-full gap-3 overflow-hidden pt-2 md:grid md:grid-cols-3" data-testid="tasks-loading">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex min-w-[80%] shrink-0 flex-col rounded-2xl border border-[var(--n-border)] bg-[var(--n-canvas-2)]/40 sm:min-w-[320px] md:min-w-0">
              <div className="h-10 animate-pulse rounded-t-2xl bg-[var(--n-canvas-2)]" />
              <div className="flex flex-col gap-2 p-2.5">
                {[0, 1, 2].map((j) => <div key={j} className="h-14 animate-pulse rounded-xl bg-[var(--n-canvas-2)]" />)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex h-full snap-x gap-3 overflow-x-auto n-scroll pt-2 md:grid md:grid-cols-3 md:overflow-x-visible">
          {BOARD_COLUMNS.map((col) => {
            const items = visibleTasks.filter((t) => statusOf(t) === col.key);
            const over = dragOverCol === col.key;
            const boardProject = isTodayBoard ? {} : (projectById[view] || {});
            const canEditBoard = isTodayBoard ? true : !boardProject.shared_by; // limits editable on owned projects
            const limit = isTodayBoard ? 0 : ((boardProject.wip_limits || {})[col.key] || 0); // Today board has no WIP limits
            const overLimit = limit > 0 && items.length > limit;
            return (
              <div key={col.key} data-testid={`tasks-board-column-${col.key}`}
                onDragOver={(e) => { e.preventDefault(); if (dragOverCol !== col.key) setDragOverCol(col.key); }}
                onDragLeave={(e) => { if (e.currentTarget === e.target) setDragOverCol(null); }}
                onDrop={(e) => { e.preventDefault(); if (dragTaskId) moveTaskToStatus(dragTaskId, col.key); setDragTaskId(null); setDragOverCol(null); }}
                className={`flex min-h-0 min-w-[80%] shrink-0 snap-start flex-col rounded-2xl border bg-[var(--n-canvas-2)]/40 transition-colors sm:min-w-[320px] md:min-w-0 md:shrink ${over ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)]' : overLimit ? 'border-[var(--n-danger)]/60' : 'border-[var(--n-border)]'}`}>
                <div className="relative flex items-center justify-between border-b border-[var(--n-border)] px-3.5 py-2.5">
                  <span className="flex items-center gap-2 text-[13px] font-semibold text-[var(--n-ink)]">
                    <span className={`h-2 w-2 rounded-full ${col.key === 'done' ? 'bg-[var(--n-accent)]' : col.key === 'in_progress' ? 'bg-[var(--n-accent-2)]' : 'bg-[var(--n-muted-2)]'}`} />
                    {col.label}
                    {overLimit && <AlertTriangle size={13} className="text-[var(--n-danger)]" data-testid={`tasks-board-wip-warning-${col.key}`} title={`Over the ${limit}-task limit`} />}
                  </span>
                  <button type="button" data-testid={`tasks-board-count-${col.key}`}
                    onClick={() => { if (!canEditBoard || isTodayBoard) return; setWipEditCol((c) => (c === col.key ? null : col.key)); setWipEditVal(limit ? String(limit) : ''); }}
                    title={canEditBoard && !isTodayBoard ? 'Set a WIP limit' : undefined}
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums transition-colors ${overLimit ? 'bg-[var(--n-danger)]/15 text-[var(--n-danger)]' : 'bg-[var(--n-surface)] text-[var(--n-muted)]'} ${canEditBoard && !isTodayBoard ? 'hover:text-[var(--n-ink)]' : 'cursor-default'}`}>
                    {items.length}{limit ? ` / ${limit}` : ''}
                  </button>
                  {wipEditCol === col.key && canEditBoard && !isTodayBoard && (
                    <div className="absolute right-2 top-full z-40 mt-1 w-52 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-2.5 shadow-[var(--n-shadow)]" data-testid={`tasks-board-wip-editor-${col.key}`}>
                      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]">WIP limit — {col.label}</div>
                      <div className="flex items-center gap-1.5">
                        <input type="number" min="0" autoFocus value={wipEditVal} placeholder="none"
                          onChange={(e) => setWipEditVal(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') setWipLimit(boardProject, col.key, wipEditVal); else if (e.key === 'Escape') { setWipEditCol(null); } }}
                          data-testid={`tasks-board-wip-input-${col.key}`}
                          className="n-focus w-full rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1.5 text-[13px] text-[var(--n-ink)]" />
                        <button onClick={() => setWipLimit(boardProject, col.key, wipEditVal)} data-testid={`tasks-board-wip-save-${col.key}`}
                          className="n-focus rounded-lg bg-[var(--n-accent)] px-2.5 py-1.5 text-[12px] font-semibold text-white hover:opacity-90">Set</button>
                      </div>
                      <button onClick={() => setWipLimit(boardProject, col.key, '')} className="n-focus mt-1.5 text-[11px] text-[var(--n-muted)] hover:text-[var(--n-danger)]">Clear limit</button>
                    </div>
                  )}
                </div>
                <div className="flex flex-1 flex-col gap-2 overflow-y-auto n-scroll p-2.5">
                  {items.length === 0 && boardQuickCol !== col.key ? (
                    <div className="mt-2 rounded-xl border border-dashed border-[var(--n-border)] px-3 py-6 text-center text-[12px] text-[var(--n-muted-2)]">Drop tasks here</div>
                  ) : items.map((t) => (
                    <div key={t.id} draggable={!expandedRows.has(t.id) && !subtaskRows.has(t.id)} data-testid="task-board-card"
                      onDragStart={(e) => { setDragTaskId(t.id); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', t.id); } catch { /* ignore */ } }}
                      onDragEnd={() => { setDragTaskId(null); setDragOverCol(null); }}
                      onClick={() => toggleRowExpand(t.id)}
                      className={`group relative overflow-hidden rounded-xl border bg-[var(--n-surface)] px-3 py-2.5 transition-colors cursor-pointer ${expandedRows.has(t.id) ? 'border-[var(--n-accent)]' : 'hover:border-[var(--n-accent)]'} ${selectedId === t.id && !expandedRows.has(t.id) ? 'shadow-[inset_0_0_0_1px_var(--n-accent)]' : expandedRows.has(t.id) ? '' : 'border-[var(--n-border)]'} ${dragTaskId === t.id ? 'opacity-50' : ''}`}>
                      <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${t.priority === 'high' ? 'bg-[var(--n-danger)]' : t.priority === 'medium' ? 'bg-[var(--n-accent-2)]' : 'bg-transparent'}`} />
                      <div className="flex items-start gap-2">
                        <GripVertical size={14} className="mt-0.5 shrink-0 text-[var(--n-muted-2)] opacity-0 group-hover:opacity-100" />
                        <div className="min-w-0 flex-1">
                          <div className={`text-[13px] font-medium ${t.is_completed ? 'text-[var(--n-muted-2)] line-through' : 'text-[var(--n-ink)]'}`}>{t.title}</div>
                          <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-[var(--n-muted-2)]">
                            {t.due_at && <span className="inline-flex items-center gap-0.5"><Clock size={10} />{fmtTime(t.due_at)}</span>}
                            {t.recurrence && t.recurrence !== 'none' && <span className="inline-flex items-center gap-0.5 text-[var(--n-accent)]"><Repeat size={10} />{recurShort(t)}</span>}
                            {t.subtask_total > 0 && (
                              <button type="button" data-testid="task-board-card-subtask-progress"
                                onClick={(e) => { e.stopPropagation(); toggleSubtaskRow(t.id); }}
                                title={subtaskRows.has(t.id) ? 'Hide sub-tasks' : 'Tick sub-tasks without opening the editor'}
                                aria-expanded={subtaskRows.has(t.id)}
                                className={`inline-flex items-center gap-0.5 rounded-full px-1 py-0.5 transition-colors hover:bg-[var(--n-canvas-2)] ${subtaskRows.has(t.id) ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'hover:text-[var(--n-accent)]'}`}>
                                <ListChecks size={10} />{t.subtask_done}/{t.subtask_total}
                                <ChevronRight size={10} className={`transition-transform ${subtaskRows.has(t.id) ? 'rotate-90' : ''}`} />
                              </button>
                            )}
                            {t.attachment_count > 0 && <span className="inline-flex items-center gap-0.5"><Paperclip size={10} />{t.attachment_count}</span>}
                            {t.linked_notes_count > 0 && <span data-testid="task-board-card-linked-notes" title={`${t.linked_notes_count} linked note${t.linked_notes_count > 1 ? 's' : ''}`} className="inline-flex items-center gap-0.5"><NotebookText size={10} />{t.linked_notes_count > 1 ? t.linked_notes_count : ''}</span>}
                            {t.comment_count > 0 && <span className="inline-flex items-center gap-0.5"><MessageSquare size={10} />{t.comment_count}</span>}
                            <span className="ml-auto inline-flex items-center gap-2">
                              {t.is_shared && <AvatarStack people={t.shared_with} size={16} max={2} />}
                              <button type="button" data-testid="task-board-card-show-details" title="Open the detail panel (notes, comments, activity)"
                                onClick={(e) => { e.stopPropagation(); openTask(t); }}
                                className="n-focus inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-[10.5px] font-medium text-[var(--n-muted)] transition-colors hover:text-[var(--n-accent)] hover:underline">
                                <PanelRight size={10} /> Details
                              </button>
                            </span>
                          </div>
                        </div>
                        {!t.shared_by && (
                          <button type="button" data-testid="task-board-card-delete-button"
                            title="Delete task"
                            onClick={(e) => { e.stopPropagation(); deleteTask(t); }}
                            className="n-focus grid h-6 w-6 shrink-0 place-items-center rounded-md text-[var(--n-muted-2)] opacity-0 transition-all hover:bg-[var(--n-danger)]/10 hover:text-[var(--n-danger)] group-hover:opacity-100">
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                      {expandedRows.has(t.id) && (
                        <div onClick={(e) => e.stopPropagation()} onDragStart={(e) => e.stopPropagation()}>
                          {renderInlineEditor(t)}
                        </div>
                      )}
                      {subtaskRows.has(t.id) && !expandedRows.has(t.id) && (
                        <div onClick={(e) => e.stopPropagation()} onDragStart={(e) => e.stopPropagation()} data-testid="task-board-card-subtasks-quick">
                          <TaskSubtasks compact taskId={t.id} canEdit={!t.shared_by || t.permission === 'edit'} onTaskSync={syncTaskFromSubtasks} />
                        </div>
                      )}
                    </div>
                  ))}
                  {/* Quick add */}
                  {boardQuickCol === col.key ? (
                    <input autoFocus value={boardQuickVal} placeholder="Task title, Enter to add…" data-testid={`tasks-board-quickadd-input-${col.key}`}
                      onChange={(e) => setBoardQuickVal(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { if (isTodayBoard) addTodayBoardTask(col.key, boardQuickVal); else addBoardTask(view, col.key, boardQuickVal); } else if (e.key === 'Escape') { setBoardQuickCol(null); setBoardQuickVal(''); } }}
                      onBlur={() => { if (!boardQuickVal.trim()) { setBoardQuickCol(null); setBoardQuickVal(''); } }}
                      className="n-focus mt-1 w-full rounded-xl border border-[var(--n-accent)] bg-[var(--n-surface)] px-3 py-2 text-[13px] text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]" />
                  ) : (
                    <button onClick={() => { setBoardQuickCol(col.key); setBoardQuickVal(''); }} data-testid={`tasks-board-quickadd-${col.key}`}
                      className="n-focus mt-1 inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-[12.5px] font-medium text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-accent)]"><Plus size={14} /> Add task</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Compact icon rail shown when the sidebar is collapsed (ERP-style) — keeps
  // primary navigation reachable instead of hiding it outright.
  const railItems = [
    { icon: LayoutGrid, label: 'All Tasks', id: 'all' },
    { icon: Clock, label: 'Today', id: 'today', badge: overdueTaskCount },
    { icon: CalendarDays, label: 'Upcoming', id: 'upcoming' },
    { icon: Users2, label: 'Shared with my groups', id: 'groups' },
    { icon: Filter, label: 'Filters & Labels', onClick: openFiltersBlank, active: view === 'filters' && !activeFilterId },
    { icon: CheckCircle2, label: 'Completed', id: 'completed' },
  ];
  const RailNav = (
    <aside data-testid="tasks-sidebar-rail" className="hidden md:flex w-[60px] shrink-0 flex-col items-center gap-1.5 border-r border-[var(--n-line)] bg-[var(--n-canvas)] py-3">
      <button onClick={() => setRailHidden(false)} title="Expand sidebar" data-testid="tasks-rail-expand"
        className="n-focus mb-1 grid h-10 w-10 place-items-center rounded-xl text-[var(--n-ink-soft)] transition-colors hover:bg-[var(--n-canvas-2)]"><PanelLeftOpen size={18} /></button>
      {railItems.map((it) => {
        const isActive = it.active != null ? it.active : view === it.id;
        return (
          <button key={it.label} title={it.label} aria-label={it.label} data-testid={`tasks-rail-${it.id || 'filters'}`}
            onClick={it.onClick || (() => { setView(it.id); setMobileNav(false); })}
            className={`n-focus relative grid h-10 w-10 place-items-center rounded-xl transition-colors ${isActive ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
            <it.icon size={18} />
            {it.badge != null && it.badge > 0 && (
              <span title={`${it.badge} overdue`} className="absolute -right-0.5 -top-0.5 grid h-4 min-w-[16px] place-items-center rounded-full bg-[var(--n-danger)] px-1 text-[9.5px] font-semibold tabular-nums text-white">{it.badge}</span>
            )}
          </button>
        );
      })}
      <button onClick={() => { setSmartAddOpen(true); }} title="Add a task" data-testid="tasks-rail-add"
        className="n-focus mt-1 grid h-10 w-10 place-items-center rounded-xl bg-[var(--n-accent)] text-white transition-opacity hover:opacity-90"><Plus size={18} /></button>
    </aside>
  );


  return (
    <div className={`notes-scope ${isDark ? 'dark' : ''} flex h-screen w-full flex-col overflow-hidden`} style={{ background: 'var(--n-canvas)', paddingRight: chatReserve || undefined, transition: 'padding-right 160ms ease' }}>
      {/* Header */}
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-[var(--n-line)] bg-[var(--n-surface)] px-3 sm:px-4">
        <button onClick={() => setMobileNav((v) => !v)} className="n-focus -ml-1 inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] md:hidden" aria-label="Menu"><Menu size={18} /></button>
        <button onClick={() => setRailHidden((v) => !v)} title={railHidden ? 'Expand sidebar' : 'Collapse sidebar'} data-testid="tasks-sidebar-collapse-toggle"
          className="n-focus -ml-1 hidden md:inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
          {railHidden ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
        <div className="flex items-center gap-2">
          <ListChecks size={20} className="text-[var(--n-accent)]" />
          <span className="font-display text-xl tracking-wide text-[var(--n-ink)] hidden sm:inline">Tasks</span>
        </div>
        <div className="ml-1 sm:ml-3"><ModuleSwitch /></div>
        <div className="ml-auto flex items-center gap-1 sm:gap-1.5">
          {/* @mentions inbox */}
          <div className="relative" ref={mentionsRef}>
            <button onClick={() => { setMentionInboxOpen((o) => { const nx = !o; if (nx) loadMentions(); return nx; }); }} title="Mentions" data-testid="tasks-mentions-button"
              className="n-focus relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
              <AtSign size={17} />
              {mentionInbox.count > 0 && <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-[var(--n-accent)] px-1 text-[9px] font-bold text-white" data-testid="tasks-mentions-badge">{mentionInbox.count}</span>}
            </button>
            {mentionInboxOpen && (
              <div className="fixed right-3 top-[calc(env(safe-area-inset-top,0px)+3.75rem)] z-[120] w-80 max-w-[calc(100vw-1.5rem)] rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-2 shadow-[var(--n-shadow)] sm:absolute sm:right-0 sm:top-auto sm:mt-1" data-testid="tasks-mentions-panel">
                <div className="px-2 pb-2 pt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Mentions</div>
                {mentionInbox.items.length === 0 ? (
                  <div className="px-2.5 py-6 text-center text-[12px] text-[var(--n-muted)]" data-testid="tasks-mentions-empty">You&apos;re all caught up.</div>
                ) : (
                  <div className="max-h-80 space-y-0.5 overflow-y-auto n-scroll">
                    {mentionInbox.items.map((m) => (
                      <button key={m.comment_id} data-testid="tasks-mention-item" onClick={() => openMention(m)}
                        className="n-focus group flex w-full items-start gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-[var(--n-canvas-2)] transition-colors">
                        <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[var(--n-accent-bg)] text-[10px] font-bold text-[var(--n-accent)]">{(m.author_name || '?').slice(0, 1).toUpperCase()}</span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[12.5px] text-[var(--n-ink)]"><span className="font-semibold">{m.author_name}</span> in <span className="font-medium">{m.task_title}</span></span>
                          <span className="mt-0.5 block truncate text-[11.5px] text-[var(--n-muted)]">{m.snippet}</span>
                        </span>
                        <span className="mt-1 shrink-0 text-[var(--n-muted-2)] opacity-0 group-hover:opacity-100 transition-opacity"><ChevronRight size={14} /></span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Reminders bell */}
          <div className="relative" ref={remindersRef}>
            <button onClick={() => { setRemindersOpen((o) => { const nx = !o; if (nx) reminders.refreshUpcoming(); return nx; }); }} title="Reminders" data-testid="tasks-reminders-button"
              className="n-focus relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
              <Bell size={17} />
              {overdueCount > 0 && <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-[var(--n-danger)] px-1 text-[9px] font-bold text-white" data-testid="tasks-reminders-badge">{overdueCount}</span>}
            </button>
            {remindersOpen && (
              <div className="fixed right-3 top-[calc(env(safe-area-inset-top,0px)+3.75rem)] z-[120] w-80 max-w-[calc(100vw-1.5rem)] rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-2 shadow-[var(--n-shadow)] sm:absolute sm:right-0 sm:top-auto sm:mt-1" data-testid="tasks-reminders-panel">
                <div className="px-2 pb-2 pt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Upcoming reminders</div>
                {(reminders.upcomingReminders || []).filter((n) => !n.is_completed).length === 0 ? (
                  <div className="px-2.5 py-5 text-center text-[12px] text-[var(--n-muted)]" data-testid="tasks-reminders-empty">No reminders set.</div>
                ) : (
                  <div className="max-h-80 space-y-0.5 overflow-y-auto n-scroll">
                    {(reminders.upcomingReminders || []).filter((n) => !n.is_completed).map((n) => {
                      const overdue = new Date(n.reminder_at).getTime() <= Date.now();
                      return (
                        <div key={n.id} data-testid="tasks-reminder-item"
                          className="group rounded-lg px-2.5 py-2 hover:bg-[var(--n-canvas-2)] transition-colors">
                          <div className="flex w-full items-start gap-2">
                            <button onClick={() => { openTask({ id: n.id }); setRemindersOpen(false); }} data-testid="tasks-reminder-open"
                              className="n-focus flex min-w-0 flex-1 items-start gap-2 text-left">
                              <span className={`mt-0.5 shrink-0 ${overdue ? 'text-[var(--n-danger)]' : 'text-[var(--n-accent)]'}`}>{overdue ? <BellRing size={14} /> : <Clock size={14} />}</span>
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-[13px] text-[var(--n-ink)]">{n.title}</span>
                                <span className={`block text-[11px] ${overdue ? 'text-[var(--n-danger)]' : 'text-[var(--n-muted)]'}`}>{overdue ? 'Overdue · ' : ''}{formatWhen(n.reminder_at)}{n.reminder_rule && n.reminder_rule !== 'none' ? ` · ${RECUR_LABEL[n.reminder_rule]}` : ''}</span>
                              </span>
                            </button>
                            {!n.owned && n.shared_by ? (
                              <span className="mt-0.5 inline-flex shrink-0 items-center gap-0.5 text-[10px] text-[var(--n-accent)]"><Users2 size={10} />{n.shared_by}</span>
                            ) : (
                              <button data-testid="tasks-reminder-delete" title="Delete reminder"
                                onClick={(e) => { e.stopPropagation(); deleteReminder(n.id); }}
                                className="n-focus mt-0.5 shrink-0 rounded-md p-1 text-[var(--n-muted-2)] opacity-0 group-hover:opacity-100 hover:text-[var(--n-danger)] transition-opacity"><Trash2 size={14} /></button>
                            )}
                          </div>
                          {n.owned !== false && (
                            <div className="mt-1.5 flex flex-wrap items-center gap-1 pl-6 opacity-0 group-hover:opacity-100 transition-opacity" data-testid="tasks-reminder-snooze-bell">
                              <span className="text-[10px] text-[var(--n-muted-2)]">Snooze</span>
                              {[['10m', '10m'], ['1h', '1h'], ['Tomorrow', 'tomorrow']].map(([lbl, kind]) => (
                                <button key={kind} data-testid={`tasks-reminder-snooze-bell-${kind}`} onClick={(e) => { e.stopPropagation(); snoozeFromBell(n, kind); }}
                                  className="n-focus rounded-md border border-[var(--n-border)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--n-ink-soft)] hover:border-[var(--n-accent)] hover:text-[var(--n-accent)] transition-colors">{lbl}</button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          {chatEnabled && chatReserve === 0 && (
            <button onClick={() => setChatOpen(true)} title="Chat" aria-label="Chat" data-testid="tasks-chat-toggle"
              className="relative n-focus inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
              <MessageSquare size={18} />
              {chatUnread > 0 && (
                <span data-testid="sidebar-chat-unread-badge" className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[9px] font-bold text-white" style={{ background: 'var(--n-danger)' }}>{chatUnread > 99 ? '99+' : chatUnread}</span>
              )}
            </button>
          )}
          <PushBell onToast={notify} />
          <button onClick={() => setCmdOpen(true)} title="Command palette (⌘K / Ctrl+K)" data-testid="tasks-cmd-button"
            className="n-focus hidden sm:inline-flex h-9 items-center gap-1.5 rounded-lg border border-[var(--n-border)] px-2.5 text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]">
            <Search size={15} />
            <kbd className="hidden md:inline-flex items-center gap-0.5 font-mono text-[11px] font-semibold text-[var(--n-muted-2)]"><CmdIcon size={11} />K</kbd>
          </button>
          <button onClick={() => setHelpOpen(true)} title="Keyboard shortcuts ( ? )" data-testid="tasks-shortcuts-button"
            className="n-focus hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]"><Keyboard size={17} /></button>
          <button onClick={() => setTemplatesOpen(true)} title="Task templates" data-testid="tasks-templates-button"
            className="n-focus hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]"><Bookmark size={17} /></button>
          <button onClick={() => setGroupsOpen(true)} title="Groups" data-testid="tasks-groups-button"
            className="n-focus hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]"><Users2 size={17} /></button>
          {isSuper && (
            <button onClick={() => setAdminOpen(true)} title="Tasks backup & restore" data-testid="tasks-admin-backup-button"
              className="n-focus hidden sm:inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]"><DatabaseBackup size={17} /></button>
          )}
          <button onClick={toggleTheme} title="Toggle theme" data-testid="tasks-theme-toggle"
            className="n-focus inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">{isDark ? <Sun size={17} /> : <Moon size={17} />}</button>
          <div className="ml-1 hidden h-8 w-8 place-items-center rounded-full bg-[var(--n-accent)] text-[13px] font-bold text-white sm:grid" title={principal?.name}>{(principal?.name || 'U').charAt(0).toUpperCase()}</div>
          <button onClick={logout} title="Sign out" data-testid="tasks-logout"
            className="n-focus inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]"><LogOut size={17} /></button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {railHidden ? RailNav : <aside className="hidden md:flex w-[272px] shrink-0 flex-col border-r border-[var(--n-line)] bg-[var(--n-canvas)]" data-testid="tasks-sidebar">{Sidebar}</aside>}
        {mobileNav && (
          <div className="fixed inset-0 z-[200] md:hidden" onClick={() => setMobileNav(false)}>
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute left-0 top-0 flex h-full w-[280px] flex-col border-r border-[var(--n-line)] bg-[var(--n-surface)]" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-[var(--n-line)] px-3 py-2.5" data-testid="tasks-mobile-drawer-switch">
                <ModuleSwitch />
                <button onClick={() => setMobileNav(false)} aria-label="Close menu" className="n-focus grid h-8 w-8 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]"><X size={16} /></button>
              </div>
              <div className="flex-1 overflow-y-auto n-scroll">{Sidebar}</div>
            </div>
          </div>
        )}

        <main className="flex min-w-0 flex-1">
          <section className="relative flex min-w-0 flex-1 flex-col overflow-y-auto md:overflow-visible min-h-0" data-testid="tasks-content-section">
            <div className="flex items-end justify-between px-6 pb-1 pt-5">
              <div>
                <h1 className="font-display text-3xl font-semibold text-[var(--n-ink)]">{viewTitle}</h1>
                <p className="mt-0.5 text-[13px] text-[var(--n-muted)]">{viewSub}</p>
              </div>
            </div>

            <div className="px-6 py-3" data-testid="tasks-search-filter-bar">
              <div className="flex flex-wrap items-center gap-2">
                <div className={`relative min-w-[220px] max-w-[440px] flex-1 ${mobileSearchOpen ? '' : 'hidden'} sm:block`}>
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--n-muted-2)]" />
                  <input value={search} ref={searchRef} onChange={(e) => setSearch(e.target.value)} placeholder="Search tasks…  ( / )" data-testid="tasks-search-input"
                    className="n-focus w-full rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] pl-9 pr-8 py-2.5 text-sm text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]" />
                  {search && <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--n-muted-2)] hover:text-[var(--n-ink)]"><X size={14} /></button>}
                </div>
                {/* Tag filter lives in the search row only on views without a
                    filter bar (Upcoming / Groups); elsewhere it sits inside the
                    filter bar in line with the other filter controls. */}
                {(view === 'upcoming' || view === 'groups') && tagFilterCombo()}
                {view === 'today' && (
                  <div className="inline-flex items-center rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-0.5" data-testid="tasks-today-view-toggle">
                    <button onClick={() => setTodayView('list')} data-testid="tasks-today-view-list" title="List view"
                      className={`n-focus inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors ${todayViewMode === 'list' ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                      <List size={15} /> List
                    </button>
                    <button onClick={() => setTodayView('grid')} data-testid="tasks-today-view-grid" title="Grid view"
                      className={`n-focus inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors ${todayViewMode === 'grid' ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                      <LayoutGrid size={15} /> Grid
                    </button>
                    <button onClick={() => setTodayView('board')} data-testid="tasks-today-view-board" title="Kanban view"
                      className={`n-focus inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors ${todayViewMode === 'board' ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                      <Columns3 size={15} /> Kanban
                    </button>
                  </div>
                )}
                {isProjectView && (
                  <div className="inline-flex items-center rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-0.5" data-testid="tasks-view-mode-toggle">
                    <button onClick={() => setProjectView('grid')} data-testid="tasks-view-mode-grid" title="Grid view"
                      className={`n-focus inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors ${viewMode === 'grid' ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                      <LayoutGrid size={15} /> Grid
                    </button>
                    <button onClick={() => setProjectView('list')} data-testid="tasks-view-mode-list" title="List view"
                      className={`n-focus inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors ${viewMode === 'list' ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                      <List size={15} /> List
                    </button>
                    <button onClick={() => setProjectView('board')} data-testid="tasks-view-mode-board" title="Board view"
                      className={`n-focus inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors ${viewMode === 'board' ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                      <Columns3 size={15} /> Board
                    </button>
                    <button onClick={() => setProjectView('calendar')} data-testid="tasks-view-mode-calendar" title="Calendar view"
                      className={`n-focus inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors ${viewMode === 'calendar' ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                      <CalendarDays size={15} /> Calendar
                    </button>
                  </div>
                )}
                {view === 'all' && (
                  <div className="inline-flex items-center rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-0.5" data-testid="tasks-all-view-toggle">
                    <button onClick={() => setAllView('grid')} data-testid="tasks-all-view-grid" title="Grid view"
                      className={`n-focus inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors ${allViewMode === 'grid' ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                      <LayoutGrid size={15} /> Grid
                    </button>
                    <button onClick={() => setAllView('calendar')} data-testid="tasks-all-view-calendar" title="Calendar view"
                      className={`n-focus inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors ${allViewMode === 'calendar' ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                      <CalendarDays size={15} /> Calendar
                    </button>
                  </div>
                )}
                {view === 'filters' && (
                  <div className="inline-flex items-center rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-0.5" data-testid="tasks-filters-view-toggle">
                    <button onClick={() => setFiltersView('grid')} data-testid="tasks-filters-view-grid" title="Grid view"
                      className={`n-focus inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors ${filtersViewMode === 'grid' ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                      <LayoutGrid size={15} /> Grid
                    </button>
                    <button onClick={() => setFiltersView('list')} data-testid="tasks-filters-view-list" title="List view"
                      className={`n-focus inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors ${filtersViewMode === 'list' ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                      <List size={15} /> List
                    </button>
                    <button onClick={() => setFiltersView('calendar')} data-testid="tasks-filters-view-calendar" title="Calendar view"
                      className={`n-focus inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors ${filtersViewMode === 'calendar' ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                      <CalendarDays size={15} /> Calendar
                    </button>
                  </div>
                )}
                {view === 'completed' && (
                  <div className="inline-flex items-center rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-0.5" data-testid="tasks-completed-view-toggle">
                    <button onClick={() => setCompletedView('grid')} data-testid="tasks-completed-view-grid" title="Grid view"
                      className={`n-focus inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors ${completedViewMode === 'grid' ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                      <LayoutGrid size={15} /> Grid
                    </button>
                    <button onClick={() => setCompletedView('list')} data-testid="tasks-completed-view-list" title="List view"
                      className={`n-focus inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[13px] font-medium transition-colors ${completedViewMode === 'list' ? 'bg-[var(--n-accent-bg)] text-[var(--n-ink)] shadow-[inset_0_0_0_1px_var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                      <List size={15} /> List
                    </button>
                  </div>
                )}
                {view === 'completed' && ownedCompletedIds.length > 0 && (
                  <button onClick={toggleSelectAllCompleted} data-testid="tasks-completed-select-all"
                    className="n-focus inline-flex items-center gap-1.5 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2.5 text-sm text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
                    <CheckCheck size={15} /> {allOwnedSelected ? 'Clear all' : 'Select all'}
                  </button>
                )}
                {isProjectView && (
                  <button onClick={() => setSmartAddOpen(true)} data-testid="tasks-project-add-button" className="n-focus inline-flex items-center gap-1.5 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2.5 text-sm text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]"><Plus size={15} /> Add</button>
                )}
                {/* Mobile: search + filter as two compact icons (desktop shows
                    the full field/bar inline). Command palette (⌘K) remains for
                    power users. */}
                <div className="ml-auto inline-flex items-center gap-1.5 sm:hidden" data-testid="tasks-mobile-controls">
                  <button type="button" onClick={() => setMobileSearchOpen((v) => !v)} data-testid="tasks-mobile-search-toggle" title="Search" aria-pressed={mobileSearchOpen}
                    className={`n-focus grid h-9 w-9 place-items-center rounded-xl border transition-colors ${mobileSearchOpen ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'border-[var(--n-border)] bg-[var(--n-surface)] text-[var(--n-ink-soft)]'}`}>
                    <Search size={17} />
                  </button>
                  {(inlineFilterView || view === 'completed') && (
                    <button type="button" onClick={() => setMobileFiltersOpen((v) => !v)} data-testid="tasks-mobile-filter-toggle" title="Filters" aria-pressed={mobileFiltersOpen}
                      className={`relative n-focus grid h-9 w-9 place-items-center rounded-xl border transition-colors ${(mobileFiltersOpen || inlineFiltersActive) ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'border-[var(--n-border)] bg-[var(--n-surface)] text-[var(--n-ink-soft)]'}`}>
                      <SlidersHorizontal size={17} />
                      {inlineFiltersActive && <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full" style={{ background: 'var(--n-accent)' }} />}
                    </button>
                  )}
                </div>
              </div>
              {(view === 'upcoming' || view === 'groups') && selectedTags.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="tasks-active-filters">
                  {selectedTags.map((t) => (
                    <span key={t} data-testid="tasks-active-filter-chip" className="inline-flex items-center gap-1 rounded-full border border-[var(--n-accent-soft)] bg-[var(--n-accent-bg)] px-2.5 py-1 text-[12px] font-medium text-[var(--n-accent)]">
                      <Hash size={10} />{t}<button onClick={() => toggleTag(t)} className="n-focus opacity-70 hover:opacity-100"><X size={11} /></button>
                    </span>
                  ))}
                  <button onClick={() => setSelectedTags([])} data-testid="tasks-clear-filters-button" className="n-focus text-[12px] font-medium text-[var(--n-accent)] hover:underline">Clear</button>
                </div>
              )}
            </div>

            {view === 'completed' && (
              <div className={`${mobileFiltersOpen ? 'flex' : 'hidden'} sm:flex flex-wrap items-center gap-2 border-b border-[var(--n-line)] px-6 py-3`} data-testid="tasks-completed-filters">
                <span className="text-[12px] font-medium text-[var(--n-muted)]">Completed</span>
                <input type="date" value={compFrom} max={compTo || undefined} onChange={(e) => setCompFrom(e.target.value)} data-testid="tasks-completed-from"
                  className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-2.5 py-1.5 text-[13px] text-[var(--n-ink)]" />
                <span className="text-[12px] text-[var(--n-muted)]">to</span>
                <input type="date" value={compTo} min={compFrom || undefined} onChange={(e) => setCompTo(e.target.value)} data-testid="tasks-completed-to"
                  className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-2.5 py-1.5 text-[13px] text-[var(--n-ink)]" />
                <MiniSelect testid="tasks-completed-project" value={compProject} onChange={setCompProject} width="w-52"
                  leading={<Circle size={9} className="fill-current" style={{ color: compProject === 'all' ? 'var(--n-muted-2)' : (projectById[compProject]?.color || 'var(--n-accent)') }} />}
                  options={[{ value: 'all', label: 'All projects' }, ...projects.map((p) => ({ value: p.id, label: p.name }))]} />
                {/* Tag filter — in line with the other Completed filter controls. */}
                {tagFilterCombo(true)}
                {(compFrom || compTo || compProject !== 'all' || selectedTags.length > 0) && (
                  <button onClick={() => { setCompFrom(''); setCompTo(''); setCompProject('all'); setSelectedTags([]); }} data-testid="tasks-completed-clear-filters"
                    className="n-focus inline-flex items-center gap-1 text-[12px] font-medium text-[var(--n-accent)] hover:underline"><X size={12} /> Clear filters</button>
                )}
              </div>
            )}

            {view === 'completed' && selectedTags.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 border-b border-[var(--n-line)] px-6 py-2" data-testid="tasks-completed-filter-chips">
                <span className="mr-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-[var(--n-muted-2)]">Active</span>
                {selectedTags.map((t) => (
                  <span key={t} data-testid={`tasks-today-chip-tag-${t}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[var(--n-border)] bg-[var(--n-canvas-2)] py-1 pl-2.5 pr-1 text-[12px] font-medium text-[var(--n-ink-soft)]">
                    <Hash size={11} className="text-[var(--n-muted-2)]" />{t}
                    <button type="button" onClick={() => toggleTag(t)} aria-label={`Remove ${t} filter`}
                      className="n-focus grid h-4 w-4 place-items-center rounded-full text-[var(--n-muted)] hover:bg-[var(--n-border)] hover:text-[var(--n-ink)] transition-colors"><X size={11} /></button>
                  </span>
                ))}
                <button type="button" onClick={() => setSelectedTags([])} data-testid="tasks-completed-chips-clear-all"
                  className="n-focus ml-0.5 text-[12px] font-medium text-[var(--n-accent)] hover:underline">Clear all</button>
              </div>
            )}

            {inlineFilterView && (
              <div className={`${mobileFiltersOpen ? 'flex' : 'hidden'} sm:flex flex-wrap items-center gap-2 border-b border-[var(--n-line)] px-6 py-3`} data-testid="tasks-today-filters">
                <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[var(--n-muted)]"><SlidersHorizontal size={13} /> Filter</span>
                {showFxProject && (
                  <MiniSelect testid="tasks-today-project" value={tProject} onChange={setTProject} width="w-52"
                    leading={<Circle size={9} className="fill-current" style={{ color: tProject === 'all' ? 'var(--n-muted-2)' : (projectById[tProject]?.color || 'var(--n-accent)') }} />}
                    options={[{ value: 'all', label: 'All projects' }, ...projects.map((p) => ({ value: p.id, label: p.name }))]} />
                )}
                {showFxDue && (
                  <>
                    <MiniSelect testid="tasks-today-due-mode" value={tDueMode} onChange={setTDueMode} width="w-44"
                      leading={<CalendarDays size={12} className="text-[var(--n-muted-2)]" />}
                      options={view === 'all' ? ALL_DUE_OPTIONS : TODAY_DUE_OPTIONS} />
                    {tDueMode === 'on' && (
                      <input type="date" value={tDueOn} onChange={(e) => setTDueOn(e.target.value)} data-testid="tasks-today-due-on"
                        className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-2.5 py-1.5 text-[13px] text-[var(--n-ink)]" />
                    )}
                    {tDueMode === 'range' && (
                      <>
                        <input type="date" value={tDueFrom} max={tDueTo || undefined} onChange={(e) => setTDueFrom(e.target.value)} data-testid="tasks-today-due-from"
                          className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-2.5 py-1.5 text-[13px] text-[var(--n-ink)]" />
                        <span className="text-[12px] text-[var(--n-muted)]">to</span>
                        <input type="date" value={tDueTo} min={tDueFrom || undefined} onChange={(e) => setTDueTo(e.target.value)} data-testid="tasks-today-due-to"
                          className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-2.5 py-1.5 text-[13px] text-[var(--n-ink)]" />
                      </>
                    )}
                  </>
                )}
                <MiniSelect testid="tasks-today-shared" value={tShared} onChange={setTShared} width="w-48"
                  leading={<Users2 size={12} className="text-[var(--n-muted-2)]" />}
                  options={SHARED_MODE_OPTIONS} />
                {/* Tag filter — in line with the other filter controls. */}
                {tagFilterCombo(true)}
                {/* All Tasks: pick a saved filter to apply it in place (clears everything else). */}
                {view === 'all' && savedFilters.length > 0 && (
                  <MiniSelect testid="tasks-all-saved-filter"
                    value={inlineUnchangedFromSaved ? inlineFilterId : ''}
                    onChange={(v) => { if (v) applyInlineSavedFilter(savedFilters.find((f) => f.id === v)); }}
                    width="w-56"
                    leading={<Bookmark size={12} className="text-[var(--n-muted-2)]" />}
                    options={[{ value: '', label: 'Saved filters…' }, ...savedFilters.map((f) => ({ value: f.id, label: f.name }))]} />
                )}
                {/* All Tasks: independent toggles — "No due" shows the right-hand
                    panel (works alongside a month selection); "Completed" adds a
                    trailing group of finished tasks. */}
                {view === 'all' && (
                  <>
                    <button type="button" onClick={() => setTNoDue((v) => !v)} data-testid="tasks-all-nodue-toggle" aria-pressed={tNoDue}
                      title={tNoDue ? 'Hide the No due date panel' : 'Show the No due date panel'}
                      className={`n-focus inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] font-medium transition-colors ${tNoDue ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'border-[var(--n-border)] bg-[var(--n-surface)] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                      {tNoDue ? <Check size={13} /> : <CalendarClock size={13} />} No due
                    </button>
                    <button type="button" onClick={() => { if (!tCompleted) setAllGridExpanded((s) => { const n = new Set(s); n.add('completed'); return n; }); setTCompleted((v) => !v); }} data-testid="tasks-all-completed-toggle" aria-pressed={tCompleted}
                      title={tCompleted ? 'Hide completed tasks' : 'Show completed tasks in a group at the end'}
                      className={`n-focus inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] font-medium transition-colors ${tCompleted ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'border-[var(--n-border)] bg-[var(--n-surface)] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                      {tCompleted ? <Check size={13} /> : <CheckCheck size={13} />} Completed
                    </button>
                  </>
                )}
                {inlineFiltersActive && (
                  <button onClick={clearInlineFilters} data-testid="tasks-today-clear-filters"
                    className="n-focus inline-flex items-center gap-1 text-[12px] font-medium text-[var(--n-accent)] hover:underline"><X size={12} /> Clear filters</button>
                )}
                <div className="ml-auto flex items-center gap-2">
                  {tSaving ? (
                    <div className="flex items-center gap-1.5" data-testid="tasks-today-save-box">
                      <input autoFocus value={tSaveName} onChange={(e) => setTSaveName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveInlineFilter(); else if (e.key === 'Escape') { setTSaving(false); setTSaveName(''); } }}
                        placeholder="Filter name" data-testid="tasks-today-save-name"
                        className="n-focus w-40 rounded-lg border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-2.5 py-1.5 text-[13px] text-[var(--n-ink)]" />
                      <button onClick={saveInlineFilter} data-testid="tasks-today-save-confirm"
                        className="n-focus rounded-lg bg-[var(--n-accent)] px-2.5 py-1.5 text-[12px] font-semibold text-white hover:opacity-90">Save</button>
                      <button onClick={() => { setTSaving(false); setTSaveName(''); }} data-testid="tasks-today-save-cancel"
                        className="n-focus rounded-lg px-2 py-1.5 text-[12px] font-medium text-[var(--n-muted)] hover:text-[var(--n-ink)]"><X size={13} /></button>
                    </div>
                  ) : (
                    <div className="relative">
                      <button
                        disabled={view === 'all' && inlineUnchangedFromSaved}
                        title={(view === 'all' && inlineUnchangedFromSaved) ? 'This filter is already saved' : 'Save this filter'}
                        onClick={() => { if (savedFilters.length > 0) { setTSaveMenuOpen((o) => !o); } else { setTSaving(true); setTSaveName(''); } }} data-testid="tasks-today-save-filter"
                        className="n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-1.5 text-[12px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--n-surface)]">
                        <Bookmark size={13} /> Save filter{savedFilters.length > 0 && <ChevronDown size={12} className="-mr-0.5 text-[var(--n-muted-2)]" />}
                      </button>
                      {tSaveMenuOpen && (
                        <>
                          <div className="fixed inset-0 z-[190]" onClick={() => setTSaveMenuOpen(false)} data-testid="tasks-today-save-menu-backdrop" />
                          <div className="absolute right-0 top-full z-[200] mt-1 w-60 overflow-hidden rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[0_12px_32px_rgba(0,0,0,0.14)]" data-testid="tasks-today-save-menu">
                            <button onClick={() => { setTSaveMenuOpen(false); setTSaving(true); setTSaveName(''); }} data-testid="tasks-today-save-as-new"
                              className="n-focus flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] font-medium text-[var(--n-ink)] hover:bg-[var(--n-canvas-2)]"><Plus size={14} className="text-[var(--n-accent)]" /> Save as new filter</button>
                            <div className="mt-1 border-t border-[var(--n-line)] px-2.5 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-wide text-[var(--n-muted-2)]">Update existing</div>
                            <div className="max-h-48 overflow-auto">
                              {savedFilters.map((sf) => (
                                <button key={sf.id} onClick={() => updateInlineFilter(sf.id, sf.name)} data-testid={`tasks-today-update-filter-${sf.id}`}
                                  className="n-focus flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-[13px] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
                                  <Check size={13} className="shrink-0 text-[var(--n-muted-2)]" /> <span className="min-w-0 flex-1 truncate">{sf.name}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {inlineFilterView && inlineChips.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 border-b border-[var(--n-line)] px-6 py-2" data-testid="tasks-today-filter-chips">
                <span className="mr-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-[var(--n-muted-2)]">Active</span>
                {inlineChips.map((c) => (
                  <span key={c.key} data-testid={`tasks-today-chip-${c.key}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[var(--n-border)] bg-[var(--n-canvas-2)] py-1 pl-2.5 pr-1 text-[12px] font-medium text-[var(--n-ink-soft)]">
                    {c.icon && <span className="text-[var(--n-muted-2)]">{c.icon}</span>}
                    {c.dot && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c.dot }} />}
                    {c.label}
                    <button type="button" onClick={c.onRemove} aria-label={`Remove ${c.label} filter`} data-testid={`tasks-today-chip-remove-${c.key}`}
                      className="n-focus grid h-4 w-4 place-items-center rounded-full text-[var(--n-muted)] hover:bg-[var(--n-border)] hover:text-[var(--n-ink)] transition-colors"><X size={11} /></button>
                  </span>
                ))}
                <button type="button" onClick={clearInlineFilters} data-testid="tasks-today-chips-clear-all"
                  className="n-focus ml-0.5 text-[12px] font-medium text-[var(--n-accent)] hover:underline">Clear all</button>
              </div>
            )}



            {view === 'filters' ? (<>{FilterPanel}{filterHasCriteria && filterChips}{filtersViewMode === 'calendar' ? CalendarContent : AgendaContent}</>) : view === 'all' ? (allViewMode === 'calendar' ? CalendarContent : AllTasksContent) : view === 'upcoming' ? UpcomingContent : isTodayGrid ? AllTasksContent : boardActive ? BoardContent : (isProjectView && viewMode === 'calendar') ? CalendarContent : AgendaContent}

            {selectedTaskIds.size > 0 && (
              <div className="pointer-events-none absolute inset-x-0 bottom-6 z-[150] flex justify-center px-6" data-testid="tasks-bulk-bar">
                <div className="pointer-events-auto flex items-center gap-3 rounded-2xl border border-[var(--n-border)] bg-[var(--n-surface)]/95 px-3 py-2.5 shadow-[var(--n-shadow)] backdrop-blur-md">
                  <span className="inline-flex items-center gap-2 pl-1 text-[13px] font-medium text-[var(--n-ink)]">
                    <span className="grid h-6 min-w-6 place-items-center rounded-full bg-[var(--n-accent)] px-1.5 text-[12px] font-bold text-white tabular-nums" data-testid="tasks-bulk-count">{selectedTaskIds.size}</span>
                    selected
                  </span>
                  <div className="h-5 w-px bg-[var(--n-line)]" />
                  {view === 'completed' ? (
                    <>
                      {selectedTaskIds.size < ownedCompletedIds.length && (
                        <button onClick={toggleSelectAllCompleted} data-testid="tasks-bulk-select-all"
                          className="n-focus inline-flex items-center gap-1.5 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2 text-[13px] font-semibold text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
                          <CheckCheck size={15} /> Select all ({ownedCompletedIds.length})
                        </button>
                      )}
                      <button onClick={bulkReopenSelected} data-testid="tasks-bulk-reopen"
                        className="n-focus inline-flex items-center gap-1.5 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2 text-[13px] font-semibold text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
                        <RotateCcw size={15} /> Mark not done
                      </button>
                      <button onClick={bulkDeleteSelected} data-testid="tasks-bulk-delete"
                        className="n-focus inline-flex items-center gap-1.5 rounded-xl bg-[var(--n-danger)] px-3 py-2 text-[13px] font-semibold text-white hover:opacity-90">
                        <Trash2 size={15} /> Delete
                      </button>
                    </>
                  ) : (
                    <button onClick={bulkCompleteSelected} data-testid="tasks-bulk-complete"
                      className="n-focus inline-flex items-center gap-1.5 rounded-xl bg-[var(--n-accent)] px-3 py-2 text-[13px] font-semibold text-white hover:opacity-90">
                      <Check size={15} strokeWidth={3} /> Mark done
                    </button>
                  )}
                  <button onClick={clearSelection} data-testid="tasks-bulk-clear"
                    className="n-focus inline-flex items-center gap-1.5 rounded-xl px-2.5 py-2 text-[13px] font-medium text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]">
                    <X size={15} /> Clear
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Task detail — opens only in full-focus (full-screen) mode. */}
          {Detail}
        </main>
      </div>

      {/* Share dialog */}
      <TaskShareDialog open={shareDialog.open} resource={shareDialog.resource} notify={notify}
        onChanged={() => { scheduleReload(); }} onClose={() => setShareDialog({ open: false, resource: null })} />

      {/* Tasks backup & restore (super admin) */}
      <TasksAdminDialog open={adminOpen} onClose={() => setAdminOpen(false)} />

      {/* Task templates + groups managers */}
      <TaskTemplatesDialog open={templatesOpen} onClose={() => setTemplatesOpen(false)}
        onChanged={loadTemplates} notify={notify} />
      <TaskGroupsDialog open={groupsOpen} onClose={() => setGroupsOpen(false)}
        onChanged={() => { loadTemplates(); loadGroups(); scheduleReload(); }} notify={notify} />
      <SaveAsTemplateDialog open={!!saveTemplateTask} task={saveTemplateTask}
        onClose={() => setSaveTemplateTask(null)} onChanged={loadTemplates} notify={notify} />

      {/* Mention alerts (stacked, top-right) */}
      {mentionAlerts.length > 0 && (
        <div className="fixed right-5 top-[68px] z-[320] flex w-[320px] max-w-[calc(100vw-2.5rem)] flex-col gap-2" data-testid="tasks-mention-alert-stack">
          {mentionAlerts.map((m) => (
            <div key={m.key} className="rounded-2xl border-2 border-[var(--n-accent)] bg-[var(--n-surface)] p-4 shadow-[var(--n-shadow)]" data-testid="task-mention-toast" role="alert">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--n-accent-bg)] text-[var(--n-accent)]"><AtSign size={18} /></span>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-accent)]">{m.author_name} mentioned you</div>
                  <div className="mt-0.5 truncate text-sm font-semibold text-[var(--n-ink)]" title={m.task_title}>{m.task_title}</div>
                  {m.snippet && <div className="mt-0.5 line-clamp-2 text-[12px] text-[var(--n-muted)]">“{m.snippet}”</div>}
                  <div className="mt-2.5 flex items-center gap-2">
                    <button onClick={() => { openMention({ task_id: m.task_id, comment_id: m.comment_id }); setMentionAlerts((prev) => prev.filter((x) => x.key !== m.key)); }} data-testid="task-mention-open-button"
                      className="n-focus inline-flex items-center gap-1.5 rounded-lg bg-[var(--n-accent)] px-3 py-1.5 text-[12px] font-semibold text-white hover:opacity-90">Open task</button>
                    <button onClick={() => setMentionAlerts((prev) => prev.filter((x) => x.key !== m.key))} data-testid="task-mention-dismiss"
                      className="n-focus rounded-lg px-3 py-1.5 text-[12px] font-medium text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)]">Dismiss</button>
                  </div>
                </div>
                <button onClick={() => setMentionAlerts((prev) => prev.filter((x) => x.key !== m.key))} aria-label="Close" className="text-[var(--n-muted-2)] hover:text-[var(--n-ink)]"><X size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Live ping nudges — full-width slide-in banner on mobile, centred card on desktop */}
      {pingAlerts.length > 0 && (
        <div className="fixed z-[321] flex flex-col gap-2 inset-x-3 top-3 sm:inset-x-auto sm:left-1/2 sm:top-[68px] sm:w-[340px] sm:max-w-[calc(100vw-2.5rem)] sm:-translate-x-1/2" data-testid="tasks-ping-alert-stack">
          {pingAlerts.map((p) => (
            <div key={p.key} className="n-nudge-in rounded-2xl border-2 border-[var(--n-accent)] bg-[var(--n-surface)] p-4 shadow-[var(--n-shadow)]" data-testid="task-ping-toast" role="alert">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--n-accent-bg)] text-[var(--n-accent)]"><BellRing size={18} /></span>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-accent)]">Your attention is needed</div>
                  <div className="mt-0.5 truncate text-sm font-semibold text-[var(--n-ink)]" title={p.task_title}>{p.task_title}</div>
                  <div className="mt-0.5 text-[12px] text-[var(--n-muted)]">{p.from_name || 'The owner'} pinged you to look at this task.</div>
                  <div className="mt-2.5 flex items-center gap-2">
                    <button onClick={() => { openTask({ id: p.task_id }); setPingAlerts((prev) => prev.filter((x) => x.key !== p.key)); }} data-testid="task-ping-open-button"
                      className="n-focus inline-flex items-center gap-1.5 rounded-lg bg-[var(--n-accent)] px-3 py-1.5 text-[12px] font-semibold text-white hover:opacity-90">Open task</button>
                    <button onClick={() => setPingAlerts((prev) => prev.filter((x) => x.key !== p.key))} data-testid="task-ping-dismiss"
                      className="n-focus rounded-lg px-3 py-1.5 text-[12px] font-medium text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)]">Dismiss</button>
                  </div>
                </div>
                <button onClick={() => setPingAlerts((prev) => prev.filter((x) => x.key !== p.key))} aria-label="Close" className="text-[var(--n-muted-2)] hover:text-[var(--n-ink)]"><X size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Due reminder alerts (stacked, bottom-right) */}
      {dueQueue.length > 0 && (
        <div className="fixed bottom-5 right-5 z-[320] flex w-[320px] max-w-[calc(100vw-2.5rem)] flex-col gap-2" data-testid="tasks-reminder-alert-stack">
          {dueQueue.slice(0, 4).map((n) => (
            <div key={remKey(n)} className="rounded-2xl border-2 border-[var(--n-accent)] bg-[var(--n-surface)] p-4 shadow-[var(--n-shadow)]" data-testid="task-reminder-toast" role="alert">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--n-accent-bg)] text-[var(--n-accent)]"><BellRing size={18} /></span>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-accent)]">Task reminder</div>
                  <div className="mt-0.5 truncate text-sm font-semibold text-[var(--n-ink)]" title={n.title || 'Untitled'}>{n.title || 'Untitled task'}</div>
                  {n.owned === false && n.shared_by && (
                    <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-[var(--n-accent)]"><Users2 size={11} /> Shared by {n.shared_by}</div>
                  )}
                  {n.reminder_at && <div className="text-[11px] text-[var(--n-muted)]">{formatWhen(n.reminder_at)}{n.reminder_rule && n.reminder_rule !== 'none' ? ` · ${RECUR_LABEL[n.reminder_rule]}` : ''}</div>}
                  <div className="mt-2.5 flex items-center gap-2">
                    <button onClick={() => reminders.openFromReminder(n)} data-testid="task-reminder-open-task-button"
                      className="n-focus inline-flex items-center gap-1.5 rounded-lg bg-[var(--n-accent)] px-3 py-1.5 text-[12px] font-semibold text-white hover:opacity-90">Open task</button>
                    <button onClick={() => reminders.acknowledge(n)} data-testid="task-reminder-dismiss"
                      className="n-focus rounded-lg px-3 py-1.5 text-[12px] font-medium text-[var(--n-muted)] hover:bg-[var(--n-canvas-2)]">Dismiss</button>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="task-reminder-snooze">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]">Snooze</span>
                    <button onClick={() => reminders.snooze(n, 10, notify)} data-testid="task-reminder-snooze-10m"
                      className="n-focus rounded-md border border-[var(--n-border)] px-2 py-0.5 text-[11px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">10 min</button>
                    <button onClick={() => reminders.snooze(n, 60, notify)} data-testid="task-reminder-snooze-1h"
                      className="n-focus rounded-md border border-[var(--n-border)] px-2 py-0.5 text-[11px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">1 hour</button>
                    <button onClick={() => reminders.snoozeLaterToday(n, notify)} data-testid="task-reminder-snooze-later-today"
                      className="n-focus rounded-md border border-[var(--n-border)] px-2 py-0.5 text-[11px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">Later today</button>
                    <button onClick={() => reminders.snoozeTomorrowMorning(n, notify)} data-testid="task-reminder-snooze-tomorrow-morning"
                      className="n-focus rounded-md border border-[var(--n-border)] px-2 py-0.5 text-[11px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">Tomorrow morning</button>
                    <button onClick={() => { const k = remKey(n); setCustomSnoozeKey((cur) => (cur === k ? null : k)); setCustomSnoozeVal(toLocalInputValue(Date.now() + 3600000)); }}
                      data-testid="task-reminder-snooze-custom-toggle"
                      className={`n-focus inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors
                        ${customSnoozeKey === remKey(n) ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'border-[var(--n-border)] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                      <CalendarClock size={12} /> Custom
                    </button>
                  </div>
                  {customSnoozeKey === remKey(n) && (
                    <div className="mt-2 flex items-center gap-1.5" data-testid="task-reminder-snooze-custom">
                      <input type="datetime-local" value={customSnoozeVal} min={toLocalInputValue(Date.now())}
                        onChange={(e) => setCustomSnoozeVal(e.target.value)} data-testid="task-reminder-snooze-custom-input"
                        className="n-focus min-w-0 flex-1 rounded-md border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1 text-[12px] text-[var(--n-ink)]" />
                      <button onClick={() => { if (reminders.snoozeCustom(n, customSnoozeVal, notify)) setCustomSnoozeKey(null); }} data-testid="task-reminder-snooze-custom-set"
                        className="n-focus rounded-md bg-[var(--n-accent)] px-2.5 py-1 text-[12px] font-semibold text-white hover:opacity-90">Set</button>
                    </div>
                  )}
                </div>
                <button onClick={() => reminders.acknowledge(n)} aria-label="Close" className="text-[var(--n-muted-2)] hover:text-[var(--n-ink)]"><X size={15} /></button>
              </div>
            </div>
          ))}
          {dueQueue.length > 4 && (
            <div className="rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2 text-center text-[11px] text-[var(--n-muted)]">+{dueQueue.length - 4} more due</div>
          )}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-5 left-1/2 z-[330] -translate-x-1/2 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-4 py-2.5 text-sm text-[var(--n-ink)] shadow-[var(--n-shadow)]" data-testid="tasks-toast">{toast}</div>
      )}

      {/* Smart "Add a task" — plain-English create with voice + live preview */}
      <SmartAddTask open={smartAddOpen} onClose={() => setSmartAddOpen(false)}
        onCreate={createTaskFromNl} onCreateBulk={createTasksFromNlBulk}
        tags={availableTagNames} projects={projects} users={directory}
        groups={taskGroups.filter((g) => g.is_owner)}
        templates={taskTemplates} onApplyTemplate={applyTemplate}
        onManageTemplates={() => { setSmartAddOpen(false); setTemplatesOpen(true); }}
        defaultProjectId={defaultProjectId} onChangeDefaultProject={changeDefaultProject}
        contextProjectName={isProjectView ? (projectById[view]?.name || '') : ''} />

      {/* Card sub-task quick popover — tap a card's sub-task badge to tick/untick
          without opening the full task. Positioned by the badge's rect. */}
      {subtaskPopover && (
        <SmartPopover anchor={subtaskPopover.anchor} width={288}
          onClose={() => setSubtaskPopover(null)}
          testId="task-card-subtask-popover" backdropTestId="task-card-subtask-popover-backdrop"
          zPanel="z-[191]" zBackdrop="z-[190]">
          <div className="p-2.5">
            <div className="mb-1.5 flex items-center justify-between px-0.5">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted)]"><ListChecks size={12} /> Sub-tasks</span>
              <button type="button" onClick={() => setSubtaskPopover(null)} data-testid="task-card-subtask-popover-close"
                className="n-focus text-[var(--n-muted-2)] hover:text-[var(--n-ink)]"><X size={13} /></button>
            </div>
            <TaskSubtasks compact allowAdd taskId={subtaskPopover.id} canEdit={subtaskPopover.canEdit} onTaskSync={syncTaskFromSubtasks} />
          </div>
        </SmartPopover>
      )}

      {/* Mobile long-press quick actions — mark done, change due date, quick comment. */}
      {quickPanel && (
        <TaskQuickActions
          task={quickPanel.task}
          position={{ x: quickPanel.x, y: quickPanel.y }}
          onClose={() => setQuickPanel(null)}
          onToggleComplete={toggleComplete}
          onSetDue={quickSetDue}
          onSetPriority={quickSetPriority}
          onPostComment={quickComment}
          onFetchPingStatus={quickPingStatus}
          onPing={quickPing} />
      )}

      {/* Command palette — ⌘K / Ctrl+K quick filter & jump.
          Rendered inside `.notes-scope` (not a portal) so all --n-* design
          tokens resolve and it matches the module theme. */}
      {cmdOpen && (
        <div className="fixed inset-0 z-[360] flex items-start justify-center p-4 pt-[12vh]" data-testid="tasks-cmd-overlay"
          onClick={() => setCmdOpen(false)}>
          <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" />
          <div role="dialog" aria-label="Command palette" onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-[var(--n-border)] bg-[var(--n-surface)] shadow-[var(--n-shadow)]"
            data-testid="tasks-cmd-dialog">
            <Command
              label="Command palette"
              className="bg-transparent text-[var(--n-ink)] [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-[var(--n-muted-2)]"
            >
              <CommandInput
                autoFocus
                placeholder="Filter or jump to a view, project, saved filter or tag…"
                className="text-[14px] text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]"
                data-testid="tasks-cmd-input"
              />
              <CommandList className="max-h-[min(60vh,440px)] n-scroll">
                <CommandEmpty className="py-8 text-center text-[13px] text-[var(--n-muted)]" data-testid="tasks-cmd-empty">
                  Nothing matches. Try a project, filter or tag name.
                </CommandEmpty>

                {/* Quick view navigation */}
                <CommandGroup heading="Go to">
                  {[
                    { id: 'today', label: 'Today', icon: Sun },
                    { id: 'upcoming', label: 'Upcoming', icon: CalendarClock },
                    { id: 'all', label: 'All Tasks', icon: ListChecks },
                    { id: 'completed', label: 'Completed', icon: CheckCheck },
                  ].map((v) => {
                    const Icon = v.icon;
                    return (
                      <CommandItem
                        key={v.id}
                        value={`view ${v.label}`}
                        onSelect={() => paletteGoView(v.id)}
                        className="cursor-pointer gap-2.5 text-[var(--n-ink-soft)] data-[selected=true]:!bg-[var(--n-accent-bg)] data-[selected=true]:!text-[var(--n-accent)]"
                        data-testid={`tasks-cmd-view-${v.id}`}
                      >
                        <Icon size={16} className="text-[var(--n-muted)]" />
                        <span>{v.label}</span>
                        <span className="ml-auto text-[11px] text-[var(--n-muted-2)]">view</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>

                {/* Projects → All Tasks filtered by that project */}
                {projects.length > 0 && (
                  <CommandGroup heading="Filter by project">
                    {projects.map((p) => (
                      <CommandItem
                        key={p.id}
                        value={`project ${p.name} ${p.id}`}
                        onSelect={() => paletteFilterProject(p.id)}
                        className="cursor-pointer gap-2.5 text-[var(--n-ink-soft)] data-[selected=true]:!bg-[var(--n-accent-bg)] data-[selected=true]:!text-[var(--n-accent)]"
                        data-testid={`tasks-cmd-project-${p.id}`}
                      >
                        <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: p.color || 'var(--n-accent)' }} />
                        <span className="truncate">{p.name}</span>
                        {p.shared_by && <span className="text-[11px] text-[var(--n-muted-2)]">· shared</span>}
                        <span className="ml-auto text-[11px] text-[var(--n-muted-2)]">All Tasks</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {/* Saved filters → apply the full saved filter */}
                {savedFilters.length > 0 && (
                  <CommandGroup heading="Saved filters">
                    {savedFilters.map((f) => (
                      <CommandItem
                        key={f.id}
                        value={`filter ${f.name} ${f.id}`}
                        onSelect={() => paletteApplySavedFilter(f)}
                        className="cursor-pointer gap-2.5 text-[var(--n-ink-soft)] data-[selected=true]:!bg-[var(--n-accent-bg)] data-[selected=true]:!text-[var(--n-accent)]"
                        data-testid={`tasks-cmd-filter-${f.id}`}
                      >
                        <Bookmark size={16} className="text-[var(--n-muted)]" />
                        <span className="truncate">{f.name}</span>
                        <span className="ml-auto text-[11px] text-[var(--n-muted-2)]">apply</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}

                {/* Tags → All Tasks filtered by that tag */}
                {tags.length > 0 && (
                  <CommandGroup heading="Filter by tag">
                    {tags.map((t) => {
                      const label = typeof t === 'string' ? t : t.tag;
                      return (
                        <CommandItem
                          key={label}
                          value={`tag ${label}`}
                          onSelect={() => paletteFilterTag(label)}
                          className="cursor-pointer gap-2.5 text-[var(--n-ink-soft)] data-[selected=true]:!bg-[var(--n-accent-bg)] data-[selected=true]:!text-[var(--n-accent)]"
                          data-testid={`tasks-cmd-tag-${label}`}
                        >
                          <Hash size={16} className="text-[var(--n-muted)]" />
                          <span className="truncate">{label}</span>
                          <span className="ml-auto text-[11px] text-[var(--n-muted-2)]">All Tasks</span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}
              </CommandList>
              {/* Footer hints */}
              <div className="flex items-center gap-3 border-t border-[var(--n-line)] px-3 py-2 text-[11px] text-[var(--n-muted-2)]">
                <span className="inline-flex items-center gap-1"><CornerDownLeft size={12} /> to select</span>
                <span className="inline-flex items-center gap-1"><kbd className="rounded border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-1 font-mono text-[10px] text-[var(--n-ink-soft)]">esc</kbd> to close</span>
                <span className="ml-auto inline-flex items-center gap-1"><CmdIcon size={12} /> <kbd className="rounded border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-1 font-mono text-[10px] text-[var(--n-ink-soft)]">K</kbd> anytime</span>
              </div>
            </Command>
          </div>
        </div>
      )}

      {/* Keyboard shortcuts help overlay */}
      {helpOpen && (
        <div className="fixed inset-0 z-[340] grid place-items-center p-4" data-testid="tasks-shortcuts-overlay"
          onClick={() => setHelpOpen(false)}>
          <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" />
          <div role="dialog" aria-label="Keyboard shortcuts" onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-2xl border border-[var(--n-border)] bg-[var(--n-surface)] p-5 shadow-[var(--n-shadow)]" data-testid="tasks-shortcuts-dialog">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="grid h-8 w-8 place-items-center rounded-lg bg-[var(--n-accent-bg)] text-[var(--n-accent)]"><Keyboard size={17} /></span>
                <h2 className="font-display text-lg font-semibold text-[var(--n-ink)]">Keyboard shortcuts</h2>
              </div>
              <button onClick={() => setHelpOpen(false)} aria-label="Close" data-testid="tasks-shortcuts-close"
                className="n-focus grid h-8 w-8 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]"><X size={16} /></button>
            </div>
            <div className="space-y-1.5">
              {[
                ['⌘K', 'Open the command palette'],
                ['t', 'Create a new task'],
                ['g then c', 'Open chat'],
                ['/', 'Focus the search box'],
                ['d', 'Go to Today'],
                ['u', 'Go to Upcoming'],
                ['f', 'Open Filters & Labels'],
                ['[', 'Show / hide the sidebar'],
                ['?', 'Toggle this shortcuts panel'],
                ['Esc', 'Close panel or task detail'],
              ].map(([key, label]) => (
                <div key={key} className="flex items-center justify-between rounded-lg px-2 py-1.5 hover:bg-[var(--n-canvas-2)]">
                  <span className="text-[13.5px] text-[var(--n-ink-soft)]">{label}</span>
                  <kbd className="inline-flex min-w-[28px] items-center justify-center rounded-md border border-[var(--n-border)] bg-[var(--n-canvas-2)] px-2 py-0.5 font-mono text-[12px] font-semibold text-[var(--n-ink)] shadow-[0_1px_0_var(--n-border)]">{key}</kbd>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[11.5px] text-[var(--n-muted-2)]">Shortcuts are disabled while you are typing in a field.</p>
          </div>
        </div>
      )}

      {/* Mobile bottom navigation — thumb-reachable New / Search / module switch */}
      <nav data-testid="tasks-mobile-bottombar"
        className="fixed inset-x-0 bottom-0 z-[190] flex items-stretch border-t border-[var(--n-line)] bg-[var(--n-surface)]/95 backdrop-blur-md md:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <button type="button" data-testid="tasks-bottombar-new" onClick={() => setSmartAddOpen(true)}
          className="n-focus flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[var(--n-accent)]">
          <span className="grid h-8 w-8 place-items-center rounded-full bg-[var(--n-accent)] text-white"><Plus size={18} /></span>
          <span className="text-[10.5px] font-semibold">New</span>
        </button>
        <button type="button" data-testid="tasks-bottombar-search"
          onClick={() => { setMobileNav(false); requestAnimationFrame(() => { const el = searchRef.current; if (el) { el.scrollIntoView({ block: 'center' }); el.focus(); } }); }}
          className="n-focus flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[var(--n-muted)] hover:text-[var(--n-ink)]">
          <Search size={19} />
          <span className="text-[10.5px] font-medium">Search</span>
        </button>
        <button type="button" data-testid="tasks-bottombar-notes" onClick={() => navigate('/notes')}
          className="n-focus flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[var(--n-muted)] hover:text-[var(--n-ink)]">
          <BookOpen size={19} />
          <span className="text-[10.5px] font-medium">Notes</span>
        </button>
        {chatEnabled && (
          <button type="button" data-testid="tasks-bottombar-chat" onClick={() => setChatOpen(true)}
            className="relative n-focus flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[var(--n-muted)] hover:text-[var(--n-ink)]">
            <MessageSquare size={19} />
            {chatUnread > 0 && <span className="absolute right-1/2 top-1 grid h-4 min-w-4 translate-x-3 place-items-center rounded-full px-1 text-[9px] font-bold text-white" style={{ background: 'var(--n-danger)' }}>{chatUnread > 99 ? '99+' : chatUnread}</span>}
            <span className="text-[10.5px] font-medium">Chat</span>
          </button>
        )}
      </nav>

      {/* ---- Web Push opt-in prompt (one-time, respects browser permission) ---- */}
      <PushPrompt onToast={notify} />
      <PlannerReminderAlerts />
      <PlannerNavShortcuts />
      {chatEnabled && <ChatDock open={chatOpen} onClose={() => setChatOpen(false)} onUnread={setChatUnread} onReserveChange={setChatReserve} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared dictation-enabled text field (textarea + mic + voice-command hint).
// Reuses the Web Speech API + dictationCommands pipeline. `setValue` is the
// React state setter so interim/final chunks insert at the caret.
//
// When `mentionUsers` / `mentionGroups` are supplied, typing a name right after
// a people cue ("ask …", "shared with …", "assign to …", "with …", a comma or
// "and") pops a directory autocomplete (teammates AND groups) so the name is
// spelled exactly as the task parser expects.
// ---------------------------------------------------------------------------
// Matches a partial name token typed after a people cue at the caret.
const MENTION_CUE_RE = /(?:\b(?:remind|ask|tell|get|have|notify|nudge|assigned?\s+to|shared?\s+with|share\s+to|with|for)\b|[,&+/]|\band\b)\s+([A-Za-z][\w'’.-]*)$/i;

const DictationField = ({ value, setValue, placeholder, rows = 2, testid, onSubmit, autoFocus = false, mentionUsers = null, mentionGroups = null }) => {
  const taRef = useRef(null);
  const [mention, setMention] = useState(null); // { items, start, end } | null
  const [mentionIdx, setMentionIdx] = useState(0);

  const buildMentions = useCallback((val, caret) => {
    const dirU = (mentionUsers || []).filter((u) => u && (u.name || u.email));
    const dirG = (mentionGroups || []).filter((g) => g && g.name);
    if (dirU.length === 0 && dirG.length === 0) return null;
    const before = String(val).slice(0, caret);
    const lineStart = before.lastIndexOf('\n') + 1;
    const line = before.slice(lineStart);
    // Two ways to summon the directory:
    //  1) an explicit "@" trigger — "@" (optionally followed by a partial name),
    //     at the line start or after a space (so it never fires inside emails);
    //  2) a natural-language cue ("share with / assign to / remind …").
    let q; let start; let end; let prefixAt = false;
    const at = line.match(/(?:^|\s)@([A-Za-z][\w'’.-]*)?$/);
    if (at) {
      q = (at[1] || '').toLowerCase();
      start = caret - (at[1] ? at[1].length : 0) - 1; // points at the "@"
      end = caret;
      prefixAt = true;
    } else {
      const m = line.match(MENTION_CUE_RE);
      if (!m) return null;
      q = m[1].toLowerCase();
      if (q.length < 1) return null;
      start = caret - m[1].length;
      end = caret;
    }
    const nm = (u) => String(u.name || '').toLowerCase();
    const first = (u) => nm(u).split(/\s+/)[0];
    const local = (u) => String(u.email || '').toLowerCase().split('@')[0];
    const users = dirU
      .filter((u) => !q || nm(u).startsWith(q) || first(u).startsWith(q) || local(u).startsWith(q))
      .map((u) => ({ kind: 'user', id: u.id, name: u.name, email: u.email }));
    const gnm = (g) => String(g.name || '').toLowerCase();
    const groups = dirG
      .filter((g) => !q || gnm(g).startsWith(q) || gnm(g).split(/\s+/).some((w) => w.startsWith(q)))
      .map((g) => ({ kind: 'group', id: g.id, name: g.name, color: g.color, member_count: g.member_count }));
    const items = [...users, ...groups].slice(0, 6);
    if (items.length === 0) return null;
    // Skip if the token already exactly equals the only match's name (nothing to complete).
    if (!prefixAt && items.length === 1 && String(items[0].name || '').toLowerCase() === q) return null;
    return { items, start, end, prefixAt };
  }, [mentionUsers, mentionGroups]);

  const syncMention = useCallback((el) => {
    if (!el) return;
    const mn = buildMentions(el.value, el.selectionStart);
    setMention(mn);
    if (mn) setMentionIdx(0);
  }, [buildMentions]);

  const pickMention = useCallback((u) => {
    if (!mention) return;
    const nameOut = (u.name && u.name.trim()) ? u.name.trim() : (u.email || '');
    const insertText = (mention.prefixAt ? '@' : '') + nameOut + ' ';
    const { start, end } = mention;
    setValue((prev) => {
      const next = prev.slice(0, start) + insertText + prev.slice(end);
      const caret = start + insertText.length;
      requestAnimationFrame(() => {
        const ta = taRef.current;
        if (ta) { ta.focus(); try { ta.setSelectionRange(caret, caret); } catch { /* noop */ } }
      });
      return next;
    });
    setMention(null);
  }, [mention, setValue]);

  const insert = useCallback((raw) => {
    const ops = tokenizeDictation(raw);
    if (!ops.length) return;
    setValue((prev) => {
      const ta = taRef.current;
      const start = ta && ta.selectionStart != null ? ta.selectionStart : String(prev).length;
      const end = ta && ta.selectionEnd != null ? ta.selectionEnd : start;
      const { text, caret } = applyOpsToText(prev, start, end, ops);
      requestAnimationFrame(() => {
        if (taRef.current) { taRef.current.focus(); try { taRef.current.setSelectionRange(caret, caret); } catch { /* noop */ } }
      });
      return text;
    });
  }, [setValue]);
  const { listening, interim, toggle } = useDictation(insert);

  const onKeyDown = (e) => {
    if (mention && mention.items.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx((i) => (i + 1) % mention.items.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx((i) => (i - 1 + mention.items.length) % mention.items.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickMention(mention.items[mentionIdx] || mention.items[0]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setMention(null); return; }
    }
    if (onSubmit && e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onSubmit(); }
  };

  const initials = (u) => (u.name || u.email || '?').slice(0, 1).toUpperCase();

  return (
    <div>
      {listening && (
        <div className="mb-2 flex items-center gap-1.5 rounded-lg bg-[var(--n-danger,#e5484d)]/10 px-2.5 py-1 text-[11.5px] font-medium text-[var(--n-danger,#e5484d)]" data-testid={`${testid}-dictate-status`}>
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-current" />
          </span>
          <span className="truncate">{interim ? interim : 'Listening… speak your task, say “comma”, “full stop”…'}</span>
        </div>
      )}
      <div className="flex items-start gap-2">
        <div className="relative flex-1">
          <textarea ref={taRef} value={value} rows={rows} autoFocus={autoFocus}
            onChange={(e) => { setValue(e.target.value); const el = e.target; requestAnimationFrame(() => syncMention(el)); }}
            onClick={(e) => syncMention(e.target)}
            onKeyUp={(e) => { if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) syncMention(e.target); }}
            onBlur={() => { setTimeout(() => setMention(null), 120); }}
            onKeyDown={onKeyDown}
            placeholder={placeholder} data-testid={testid}
            className="n-focus min-h-[44px] w-full resize-y rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2.5 text-[13.5px] leading-relaxed text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]" />
          {mention && mention.items.length > 0 && (
            <div className="absolute left-0 right-0 top-full z-[60] mt-1 max-h-56 overflow-y-auto n-scroll rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]" data-testid={`${testid}-mention-menu`}>
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]">Share with a teammate or group</div>
              {mention.items.map((u, i) => (
                <button key={`${u.kind}:${u.id}`} type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setMentionIdx(i)}
                  onClick={() => pickMention(u)}
                  data-testid={`${testid}-mention-option`}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${i === mentionIdx ? 'bg-[var(--n-canvas-2)]' : 'hover:bg-[var(--n-canvas-2)]'}`}>
                  {u.kind === 'group' ? (
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-white" style={{ background: u.color || '#6C8FD1' }}><Users2 size={13} /></span>
                  ) : (
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--n-accent-bg)] text-[10px] font-bold text-[var(--n-accent)]">{initials(u)}</span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-[var(--n-ink)]">{u.name}</span>
                    <span className="block truncate text-[11px] text-[var(--n-muted)]">{u.kind === 'group' ? `group · ${u.member_count ?? 0} member${(u.member_count ?? 0) === 1 ? '' : 's'}` : u.email}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        {SPEECH_SUPPORTED && (
          <div className="flex shrink-0 items-center gap-0.5">
            <button type="button" onClick={toggle} data-testid={`${testid}-dictate-button`} aria-pressed={listening}
              title={listening ? 'Stop dictation' : 'Dictate (voice to text)'}
              className={`n-focus inline-flex h-[44px] w-[44px] items-center justify-center rounded-xl border transition-colors ${listening
                ? 'border-[var(--n-danger,#e5484d)] bg-[var(--n-danger,#e5484d)]/10 text-[var(--n-danger,#e5484d)]'
                : 'border-[var(--n-border)] bg-[var(--n-surface)] text-[var(--n-muted-2)] hover:text-[var(--n-ink)] hover:bg-[var(--n-canvas-2)]'}`}>
              {listening ? <MicOff size={16} /> : <Mic size={16} />}
            </button>
            <DictationHint side="bottom" align="end" />
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Plain-English filter box inside the Filter Builder. Decodes typed / spoken
// text into due scopes, tags and projects via the native parser (no AI).
// ---------------------------------------------------------------------------
const NlFilterBox = ({ onApply, availableTags, projects }) => {
  const [text, setText] = useState('');
  const preview = useMemo(() => parseFilterText(text, { tags: availableTags, projects }), [text, availableTags, projects]);
  const apply = useCallback(() => {
    if (!preview.hasAny) return;
    onApply(parseFilterText(text, { tags: availableTags, projects }));
    setText('');
  }, [preview.hasAny, onApply, text, availableTags, projects]);

  return (
    <div className="mb-4 rounded-xl border border-[var(--n-line)] bg-[var(--n-canvas-2)] p-3" data-testid="tasks-nl-filter">
      <div className="mb-2 flex items-center gap-2">
        <MessageSquare size={14} className="text-[var(--n-accent)]" />
        <span className="text-[12px] font-semibold text-[var(--n-ink)]">Describe your filter in plain English</span>
      </div>
      <DictationField value={text} setValue={setText} rows={2} onSubmit={apply}
        testid="tasks-nl-filter-input" placeholder={`e.g. “${FILTER_EXAMPLES[0]}”`} />
      {preview.matched.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="tasks-nl-filter-preview">
          <span className="text-[11px] font-medium text-[var(--n-muted-2)]">Detected</span>
          {preview.matched.map((m, i) => (
            <span key={`${m}-${i}`} className="inline-flex items-center gap-1 rounded-full border border-[var(--n-accent)] bg-[var(--n-accent-bg)] px-2 py-0.5 text-[11.5px] font-medium text-[var(--n-accent)]">
              <Check size={10} strokeWidth={3} />{m}
            </span>
          ))}
        </div>
      )}
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <button type="button" onClick={apply} disabled={!preview.hasAny} data-testid="tasks-nl-filter-apply"
          className="n-focus inline-flex items-center gap-1.5 rounded-lg bg-[var(--n-accent)] px-3 py-1.5 text-[12.5px] font-semibold text-white hover:opacity-90 disabled:opacity-50">
          <Filter size={13} /> Apply filter
        </button>
        {text && (
          <button type="button" onClick={() => setText('')} data-testid="tasks-nl-filter-clear"
            className="n-focus rounded-lg px-2 py-1.5 text-[12.5px] font-medium text-[var(--n-muted)] hover:text-[var(--n-accent)]">Clear</button>
        )}
        <span className="ml-auto text-[11px] text-[var(--n-muted-2)]">Understands due words, your tags &amp; projects</span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Natural-language "Add a task" modal. Type or dictate a sentence; a live
// preview shows the decoded task before it is created (with sub-tasks).
// ---------------------------------------------------------------------------
const fmtLinkDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d); const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((dt - today) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return dt.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
};

const SmartAddTask = ({ open, onClose, onCreate, onCreateBulk, tags, projects, users, groups = [], contextProjectName, templates = [], onApplyTemplate, onManageTemplates, defaultProjectId = null, onChangeDefaultProject }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('single'); // 'single' | 'multi'
  const [applyingId, setApplyingId] = useState(null); // template being applied
  const [remEdit, setRemEdit] = useState(null); // null = follow parse; '' = off; else datetime-local
  const preview = useMemo(() => parseTaskText(text, { tags, projects, users, groups }), [text, tags, projects, users, groups]);
  const lines = useMemo(() => text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean), [text]);
  const multiPreviews = useMemo(
    () => (mode === 'multi' ? lines.map((l) => parseTaskText(l, { tags, projects, users, groups })) : []),
    [mode, lines, tags, projects, users, groups],
  );
  useEffect(() => { if (open) { setText(''); setBusy(false); setMode('single'); } }, [open]);
  // Re-sync the reminder editor whenever the parsed reminder changes (i.e. the
  // user keeps typing) so manual tweaks apply to the current parse only.
  useEffect(() => { setRemEdit(null); }, [preview.reminderAt]);

  // "link with …" — attach the new task to upcoming events/meetings on create.
  const [linkTargets, setLinkTargets] = useState([]);
  const [linkResults, setLinkResults] = useState([]);
  const [linkLoading, setLinkLoading] = useState(false);
  const linkDebRef = useRef(null);
  const linkQuery = useMemo(() => {
    const m = /link with\s*([^\n]*)$/i.exec(text);
    return m ? { active: true, q: (m[1] || '').trim(), index: m.index } : { active: false, q: '', index: -1 };
  }, [text]);
  useEffect(() => { if (open) { setLinkTargets([]); setLinkResults([]); } }, [open]);
  useEffect(() => {
    if (!open || !linkQuery.active) { setLinkResults([]); return undefined; }
    if (linkDebRef.current) clearTimeout(linkDebRef.current);
    setLinkLoading(true);
    linkDebRef.current = setTimeout(async () => {
      try {
        const { data } = await notesApi.get('/planner/schedule-targets', { params: { q: linkQuery.q, tz_offset: -new Date().getTimezoneOffset() } });
        setLinkResults(data.targets || []);
      } catch { setLinkResults([]); } finally { setLinkLoading(false); }
    }, 220);
    return () => linkDebRef.current && clearTimeout(linkDebRef.current);
  }, [open, linkQuery.active, linkQuery.q]);
  const pickLinkTarget = (t) => {
    setLinkTargets((cur) => (cur.some((x) => x.target_id === t.target_id) ? cur : [...cur, t]));
    setText((cur) => { const m = /link with\s*[^\n]*$/i.exec(cur); return m ? cur.slice(0, m.index).replace(/\s+$/, '') : cur; });
    setLinkResults([]);
  };
  const removeLinkTarget = (id) => setLinkTargets((cur) => cur.filter((x) => x.target_id !== id));

  if (!open) return null;

  const canSubmit = mode === 'single' ? !!text.trim() : lines.length > 0;
  const submit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      if (mode === 'single') {
        const parsed = parseTaskText(text, { tags, projects, users, groups });
        if (remEdit !== null) {
          parsed.reminderAt = remEdit ? new Date(remEdit).toISOString() : null;
          if (!parsed.reminderAt) parsed.reminderRule = 'none';
        }
        if (linkTargets.length) parsed.scheduleLinks = linkTargets.map((t) => ({ target_type: t.target_type, target_id: t.target_id }));
        await onCreate(parsed);
      } else await onCreateBulk(lines);
    } finally { setBusy(false); }
  };

  const projLabel = preview.projectName || (contextProjectName ? `${contextProjectName} (current)` : 'Default project');
  const miniProj = (p) => p.projectName || (contextProjectName || 'Default');
  // Owned projects power the "New tasks go to" default-project selector.
  const ownedProjects = projects.filter((p) => !p.shared_by);
  const defaultOptions = [{ value: '', label: 'Auto (favourite project)' }, ...ownedProjects.map((p) => ({ value: p.id, label: p.name }))];
  const defaultColor = (ownedProjects.find((p) => p.id === defaultProjectId)?.color) || 'var(--n-muted-2)';
  // Example prompts — tailored to the project the modal was opened from.
  const exampleList = contextProjectName
    ? [`Call the supplier tomorrow high priority for ${contextProjectName}`,
       `Review the latest draft for ${contextProjectName} next friday`,
       `Send weekly update every monday for ${contextProjectName}`]
    : TASK_EXAMPLES;
  // Effective reminder = manual tweak (if any) else the parsed reminder.
  const effReminderIso = remEdit === null ? (preview.reminderAt || null) : (remEdit ? new Date(remEdit).toISOString() : null);

  return (
    <div className="fixed inset-0 z-[340] grid place-items-start justify-center overflow-y-auto p-4 pt-[8vh]" data-testid="tasks-smart-add-overlay" onClick={onClose}>
      <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" />
      <div role="dialog" aria-label="Add a task in plain English" onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-xl rounded-2xl border border-[var(--n-border)] bg-[var(--n-surface)] p-5 shadow-[var(--n-shadow)]" data-testid="tasks-smart-add-dialog">
        <div className="mb-1 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-[var(--n-accent-bg)] text-[var(--n-accent)]"><Type size={17} /></span>
            <h2 className="font-display text-lg font-semibold text-[var(--n-ink)]">Add a task</h2>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="tasks-smart-add-close"
            className="n-focus grid h-8 w-8 place-items-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)]"><X size={16} /></button>
        </div>
        <p className="mb-3 text-[12.5px] text-[var(--n-muted)]">Write it the way you would say it — the project, due date, priority, tags and sub-tasks are picked up automatically. Type <span className="font-semibold text-[var(--n-accent)]">@</span> to share with a teammate.</p>

        {/* Default project selector — where untargeted tasks land (synced to account). */}
        {onChangeDefaultProject && ownedProjects.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-[var(--n-line)] bg-[var(--n-canvas-2)] px-3 py-2" data-testid="tasks-smart-add-default-project">
            <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[var(--n-ink-soft)]"><Inbox size={13} className="text-[var(--n-accent)]" /> New tasks go to</span>
            <MiniSelect testid="tasks-smart-add-default-select" value={defaultProjectId || ''} width="w-52"
              onChange={(v) => onChangeDefaultProject(v || null)}
              leading={<span className="h-2 w-2 rounded-full" style={{ background: defaultColor }} />}
              options={defaultOptions} />
            {contextProjectName && (
              <span className="text-[11px] text-[var(--n-muted-2)]">· this one lands in <strong className="font-medium text-[var(--n-ink-soft)]">{contextProjectName}</strong> (current)</span>
            )}
          </div>
        )}

        {/* Start from a template, or write a fresh line below. */}
        {(templates.length > 0 || onManageTemplates) && (
          <div className="mb-3 rounded-xl border border-[var(--n-line)] bg-[var(--n-canvas-2)] p-2.5" data-testid="tasks-smart-add-templates">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]">
                <Bookmark size={12} /> Start from a template
              </span>
              {onManageTemplates && (
                <button type="button" onClick={onManageTemplates} data-testid="tasks-smart-add-manage-templates"
                  className="n-focus text-[11px] font-medium text-[var(--n-accent)] hover:underline">Manage</button>
              )}
            </div>
            {templates.length > 0 ? (
              <div className="space-y-2" data-testid="tasks-smart-add-template-chips">
                {(() => {
                  const cats = {};
                  templates.forEach((t) => { const c = (t.category || '').trim(); (cats[c] = cats[c] || []).push(t); });
                  const keys = Object.keys(cats).sort((a, b) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)));
                  const multi = keys.length > 1 || (keys.length === 1 && keys[0] !== '');
                  return keys.map((c) => (
                    <div key={c || '__none'}>
                      {multi && <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]">{c || 'Uncategorised'}</div>}
                      <div className="flex flex-wrap gap-1.5">
                        {cats[c].map((t) => (
                          <button key={t.id} type="button" disabled={!!applyingId}
                            onClick={async () => { setApplyingId(t.id); try { await onApplyTemplate?.(t.id); } finally { setApplyingId(null); } }}
                            data-testid="tasks-smart-add-template-chip"
                            title={t.is_owner === false ? `Shared by ${t.owner_name || 'a teammate'}` : t.name}
                            className="n-focus inline-flex items-center gap-1.5 rounded-full border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-1 text-[12px] text-[var(--n-ink-soft)] hover:border-[var(--n-accent)] hover:text-[var(--n-accent)] disabled:opacity-50">
                            {applyingId === t.id ? <Loader2 size={12} className="animate-spin" /> : (t.is_owner === false ? <Users2 size={11} /> : <Bookmark size={11} />)}
                            {t.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            ) : (
              <p className="text-[11.5px] text-[var(--n-muted-2)]">No templates yet — use “Save as template” on any task, or create one from Manage.</p>
            )}
            {templates.length > 0 && (
              <div className="mt-2 flex items-center gap-2 text-[10.5px] font-medium uppercase tracking-[0.12em] text-[var(--n-muted-2)]">
                <span className="h-px flex-1 bg-[var(--n-line)]" /> or write a fresh line <span className="h-px flex-1 bg-[var(--n-line)]" />
              </div>
            )}
          </div>
        )}

        {/* Mode toggle */}
        <div className="mb-3 inline-flex rounded-lg border border-[var(--n-border)] p-0.5" data-testid="tasks-smart-add-mode">
          {[['single', 'Single task'], ['multi', 'One per line']].map(([m, label]) => (
            <button key={m} type="button" data-testid={`tasks-smart-add-mode-${m}`} onClick={() => setMode(m)}
              className={`n-focus rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors ${mode === m ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
              {label}
            </button>
          ))}
        </div>

        <DictationField value={text} setValue={setText} rows={mode === 'multi' ? 5 : 3} autoFocus onSubmit={submit}
          testid="tasks-smart-add-input" mentionUsers={users} mentionGroups={groups}
          placeholder={mode === 'multi'
            ? 'One task per line, e.g.\nPay rent on 1st monthly\nCall supplier tomorrow high priority\nTeam sync every mon and thu for Team'
            : (contextProjectName ? `e.g. “Call the vendor tomorrow high priority for ${contextProjectName}”` : `e.g. “${TASK_EXAMPLES[1]}”`)} />

        {/* "link with …" — attach the task to upcoming events/meetings */}
        {(linkQuery.active || linkTargets.length > 0) && (
          <div className="mt-2" data-testid="tasks-smart-add-linkwith">
            {linkTargets.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5" data-testid="tasks-smart-add-link-chips">
                {linkTargets.map((t) => (
                  <span key={t.target_id} data-testid="tasks-smart-add-link-chip"
                    className="inline-flex items-center gap-1 rounded-full border border-[var(--n-accent)] bg-[var(--n-accent-bg)] px-2 py-0.5 text-[11.5px] font-medium text-[var(--n-accent)]">
                    {t.kind === 'meeting' ? <Users2 size={11} /> : <CalendarDays size={11} />}
                    <span className="max-w-[160px] truncate">{t.title}</span>
                    <button type="button" onClick={() => removeLinkTarget(t.target_id)} aria-label="Remove link" className="hover:text-[var(--n-danger)]"><X size={11} /></button>
                  </span>
                ))}
              </div>
            )}
            {linkQuery.active && (
              <div className="overflow-hidden rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] shadow-[var(--n-shadow)]" data-testid="tasks-smart-add-link-results">
                <div className="border-b border-[var(--n-line)] px-3 py-1.5 text-[11px] font-medium text-[var(--n-muted-2)]">Link with an event or meeting · today or later</div>
                {linkLoading ? (
                  <div className="flex items-center gap-2 px-3 py-2.5 text-[12px] text-[var(--n-muted)]"><Loader2 size={13} className="animate-spin" /> Searching…</div>
                ) : linkResults.length === 0 ? (
                  <div className="px-3 py-2.5 text-[12px] text-[var(--n-muted)]">No upcoming events or meetings{linkQuery.q ? ` for “${linkQuery.q}”` : ''}.</div>
                ) : (
                  <ul className="max-h-48 overflow-y-auto n-scroll py-1">
                    {linkResults.map((t) => {
                      const on = linkTargets.some((x) => x.target_id === t.target_id);
                      return (
                        <li key={`${t.target_type}:${t.target_id}`}>
                          <button type="button" data-testid="tasks-smart-add-link-option" onClick={() => pickLinkTarget(t)}
                            className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-[var(--n-canvas-2)]">
                            {t.kind === 'meeting' ? <Users2 size={14} className="shrink-0 text-[var(--n-accent)]" /> : <CalendarDays size={14} className="shrink-0 text-[var(--n-accent)]" />}
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-[13px] font-medium text-[var(--n-ink)]">{t.title}</span>
                              <span className="flex items-center gap-1.5 text-[11px] text-[var(--n-muted)]">
                                <span className="capitalize">{t.kind}</span>
                                <span className="text-[var(--n-muted-2)]">·</span>
                                {fmtLinkDate(t.date)}{t.start_time ? ` · ${t.start_time}` : ''}{t.recurring ? ' · repeats' : ''}
                              </span>
                            </span>
                            {on && <Check size={14} className="shrink-0 text-[var(--n-accent)]" />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {/* "Multiple lines" nudge in single mode */}
        {mode === 'single' && lines.length > 1 && (
          <button type="button" onClick={() => setMode('multi')} data-testid="tasks-smart-add-multi-nudge"
            className="n-focus mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[var(--n-accent-bg)] px-2.5 py-1 text-[11.5px] font-medium text-[var(--n-accent)]">
            <ListChecks size={12} /> {lines.length} lines detected — create {lines.length} separate tasks?
          </button>
        )}

        {/* Example prompts (only when empty, single mode) */}
        {mode === 'single' && !text.trim() && (
          <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid="tasks-smart-add-examples">
            {exampleList.map((ex) => (
              <button key={ex} type="button" onClick={() => setText(ex)}
                className="n-focus rounded-full border border-dashed border-[var(--n-border)] px-2.5 py-1 text-[11.5px] text-[var(--n-muted)] hover:border-[var(--n-accent)] hover:text-[var(--n-accent)]">
                {ex}
              </button>
            ))}
          </div>
        )}

        {/* Single-task live preview */}
        {mode === 'single' && text.trim() && (
          <div className="mt-3 rounded-xl border border-[var(--n-line)] bg-[var(--n-canvas-2)] p-3" data-testid="tasks-smart-add-preview">
            <div className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">Preview</div>
            <div className="mb-2 flex items-start gap-2">
              <Circle size={15} className="mt-0.5 shrink-0 text-[var(--n-muted-2)]" />
              <span className="text-[14px] font-medium text-[var(--n-ink)]" data-testid="tasks-smart-add-preview-title">{preview.title}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center gap-1 rounded-full border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-0.5 text-[11.5px] text-[var(--n-ink-soft)]" data-testid="tasks-smart-add-preview-project">
                <span className="h-2 w-2 rounded-full" style={{ background: 'var(--n-accent)' }} />{projLabel}
              </span>
              {preview.dueLabel && (
                <span className="inline-flex items-center gap-1 rounded-full border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-0.5 text-[11.5px] text-[var(--n-ink-soft)]" data-testid="tasks-smart-add-preview-due">
                  <CalendarDays size={11} />{preview.dueLabel}
                </span>
              )}
              {effReminderIso && (
                <span className="inline-flex items-center gap-1 rounded-full border border-[var(--n-accent-2)]/40 bg-[var(--n-accent-2)]/10 px-2 py-0.5 text-[11.5px] font-medium text-[var(--n-accent-2)]" data-testid="tasks-smart-add-preview-reminder">
                  <BellRing size={11} />Remind {remEdit === null ? preview.reminderLabel : formatWhen(effReminderIso)}{(remEdit === null && preview.reminderRule !== 'none') ? ` · ${preview.reminderRule}` : ''}
                </span>
              )}
              {((preview.shareNames && preview.shareNames.length > 0) || (preview.shareGroupNames && preview.shareGroupNames.length > 0)) && (
                <span className="inline-flex items-center gap-1 rounded-full border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-0.5 text-[11.5px] text-[var(--n-ink-soft)]" data-testid="tasks-smart-add-preview-share">
                  <Users2 size={11} />Shared with {[...(preview.shareNames || []), ...(preview.shareGroupNames || []).map((g) => `${g} (group)`)].join(', ')}
                </span>
              )}
              {preview.priority !== 'normal' && (
                <span className="inline-flex items-center gap-1 rounded-full border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-0.5 text-[11.5px] text-[var(--n-ink-soft)]" data-testid="tasks-smart-add-preview-priority">
                  <Flag size={11} className={preview.priority === 'high' ? 'text-[var(--n-danger)]' : 'text-[var(--n-accent-2)]'} />{preview.priorityLabel}
                </span>
              )}
              {preview.recurrence !== 'none' && (
                <span className="inline-flex items-center gap-1 rounded-full border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-0.5 text-[11.5px] text-[var(--n-ink-soft)]" data-testid="tasks-smart-add-preview-recurrence">
                  <Repeat size={11} />{preview.recurrenceLabel}
                </span>
              )}
              {preview.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 rounded-full border border-[var(--n-accent)] bg-[var(--n-accent-bg)] px-2 py-0.5 text-[11.5px] font-medium text-[var(--n-accent)]" data-testid="tasks-smart-add-preview-tag">
                  <Hash size={10} />{t}
                </span>
              ))}
            </div>
            {preview.reminderAt && (
              <div className="mt-2.5 flex flex-wrap items-center gap-2" data-testid="tasks-smart-add-reminder-edit">
                <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]"><BellRing size={11} /> Reminder</span>
                <input type="datetime-local" data-testid="tasks-smart-add-reminder-input"
                  value={remEdit !== null ? remEdit : toLocalInputValue(new Date(preview.reminderAt).getTime())}
                  onChange={(e) => setRemEdit(e.target.value)}
                  className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1 text-[12px] text-[var(--n-ink)]" />
                {remEdit !== null && (
                  <button type="button" data-testid="tasks-smart-add-reminder-reset" onClick={() => setRemEdit(null)}
                    className="n-focus rounded-md border border-[var(--n-border)] px-2 py-0.5 text-[11px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">Reset</button>
                )}
                <button type="button" data-testid="tasks-smart-add-reminder-clear" onClick={() => setRemEdit('')}
                  className="n-focus rounded-md border border-[var(--n-border)] px-2 py-0.5 text-[11px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">Clear</button>
              </div>
            )}
            {preview.subtasks.length > 0 && (
              <div className="mt-2.5" data-testid="tasks-smart-add-preview-subtasks">
                <div className="mb-1 flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]"><ListTree size={11} /> {preview.subtasks.length} sub-task{preview.subtasks.length > 1 ? 's' : ''}</div>
                <ul className="space-y-0.5">
                  {preview.subtasks.map((s, i) => (
                    <li key={`${s}-${i}`} className="flex items-center gap-1.5 text-[12.5px] text-[var(--n-ink-soft)]">
                      <Circle size={9} className="shrink-0 text-[var(--n-muted-2)]" />{s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Multi-task preview list */}
        {mode === 'multi' && lines.length > 0 && (
          <div className="mt-3 rounded-xl border border-[var(--n-line)] bg-[var(--n-canvas-2)] p-3" data-testid="tasks-smart-add-multi-preview">
            <div className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">{lines.length} task{lines.length > 1 ? 's' : ''} to create</div>
            <ul className="max-h-64 space-y-1.5 overflow-y-auto n-scroll pr-1">
              {multiPreviews.map((p, i) => (
                <li key={i} className="rounded-lg border border-[var(--n-line)] bg-[var(--n-surface)] px-2.5 py-1.5" data-testid="tasks-smart-add-multi-item">
                  <div className="flex items-center gap-1.5">
                    <span className="grid h-4 w-4 shrink-0 place-items-center rounded-full bg-[var(--n-canvas-2)] text-[9px] font-bold text-[var(--n-muted)]">{i + 1}</span>
                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-[var(--n-ink)]">{p.title}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1 pl-6 text-[10.5px] text-[var(--n-muted)]">
                    <span className="inline-flex items-center gap-0.5"><span className="h-1.5 w-1.5 rounded-full" style={{ background: 'var(--n-accent)' }} />{miniProj(p)}</span>
                    {p.dueLabel && <span className="inline-flex items-center gap-0.5"><CalendarDays size={10} />{p.dueLabel}</span>}
                    {p.reminderAt && <span className="inline-flex items-center gap-0.5 text-[var(--n-accent-2)]"><BellRing size={10} />{p.reminderLabel}</span>}
                    {p.priority !== 'normal' && <span className="inline-flex items-center gap-0.5"><Flag size={10} className={p.priority === 'high' ? 'text-[var(--n-danger)]' : 'text-[var(--n-accent-2)]'} />{p.priorityLabel}</span>}
                    {p.recurrence !== 'none' && <span className="inline-flex items-center gap-0.5 text-[var(--n-accent)]"><Repeat size={10} />{p.recurrenceLabel}</span>}
                    {p.tags.map((t) => <span key={t} className="inline-flex items-center gap-0.5 text-[var(--n-accent)]"><Hash size={9} />{t}</span>)}
                    {p.subtasks.length > 0 && <span className="inline-flex items-center gap-0.5"><ListTree size={10} />{p.subtasks.length}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 flex items-center gap-2 border-t border-[var(--n-line)] pt-3">
          <span className="text-[11px] text-[var(--n-muted-2)]">Press ⌘/Ctrl + Enter to create</span>
          <button type="button" onClick={submit} disabled={busy || !canSubmit} data-testid="tasks-smart-add-submit"
            className="n-focus ml-auto inline-flex items-center gap-1.5 rounded-lg bg-[var(--n-accent)] px-4 py-2 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
            {mode === 'multi' ? `Create ${lines.length || ''} task${lines.length === 1 ? '' : 's'}` : 'Create task'}
          </button>
        </div>
      </div>
    </div>
  );
};


// Inline tag adder used in the detail panel
const TagAdder = ({ existing, onAdd, options = [] }) => {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState('');
  const [idx, setIdx] = useState(0);
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) { setOpen(false); setVal(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const existingLower = (existing || []).map((x) => x.toLowerCase());
  const q = val.trim().replace(/^#/, '');
  const suggestions = (options || [])
    .filter((t) => t && !existingLower.includes(t.toLowerCase()) && (!q || t.toLowerCase().includes(q.toLowerCase())))
    .slice(0, 6);
  const exactExists = (options || []).some((t) => t.toLowerCase() === q.toLowerCase());
  const showCreate = q && !exactExists && !existingLower.includes(q.toLowerCase());
  const rows = [...suggestions.map((t) => ({ type: 'tag', value: t })), ...(showCreate ? [{ type: 'create', value: q }] : [])];
  const add = (t) => {
    const clean = (t || '').trim().replace(/^#/, '');
    if (clean && !existingLower.includes(clean.toLowerCase())) onAdd(clean);
    setVal(''); setIdx(0); setOpen(false);
  };
  const commit = () => { const r = rows[idx]; if (r) add(r.value); else if (q) add(q); };
  if (!open) return (
    <button data-testid="task-detail-add-tag-button" onClick={() => { setOpen(true); setIdx(0); }}
      className="n-focus inline-flex items-center gap-1 rounded-full border border-dashed border-[var(--n-border)] px-2.5 py-0.5 text-[12px] text-[var(--n-muted)] hover:text-[var(--n-accent)] hover:border-[var(--n-accent)]"><Plus size={11} /> add tag</button>
  );
  return (
    <div className="relative" ref={boxRef}>
      <input autoFocus value={val}
        onChange={(e) => { setVal(e.target.value); setIdx(0); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          else if (e.key === 'Escape') { setOpen(false); setVal(''); }
          else if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => (rows.length ? (i + 1) % rows.length : 0)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => (rows.length ? (i - 1 + rows.length) % rows.length : 0)); }
        }}
        placeholder="tag…" data-testid="task-detail-tag-input"
        className="n-focus w-28 rounded-full border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-0.5 text-[12px] text-[var(--n-ink)]" />
      {rows.length > 0 && (
        <div className="absolute left-0 top-full z-40 mt-1 w-48 overflow-hidden rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]" data-testid="task-detail-tag-menu">
          {rows.map((r, i) => (
            <button key={`${r.type}-${r.value}`} type="button"
              data-testid={r.type === 'create' ? 'task-detail-tag-create' : 'task-detail-tag-option'}
              onMouseDown={(e) => { e.preventDefault(); add(r.value); }}
              onMouseEnter={() => setIdx(i)}
              className={`flex w-full items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-left text-[12.5px] transition-colors ${i === idx ? 'bg-[var(--n-canvas-2)] text-[var(--n-ink)]' : 'text-[var(--n-ink-soft)]'}`}>
              {r.type === 'create'
                ? (<><Plus size={12} className="text-[var(--n-accent)]" /> Create “{r.value}”</>)
                : (<><Hash size={11} className="text-[var(--n-muted-2)]" /> <span className="truncate">{r.value}</span></>)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
